import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Button, Form, Row, Col, Badge, Table, Modal } from 'react-bootstrap';
import '../../Common/ManageTournamentsFixturesTable.css';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { getFixtureById, editFixture, Fixture, fetchLiveScores, updateLiveScore, deleteLiveScoreEvent, addLiveScore, startTimer as startTimerAPI, pauseTimer as pauseTimerAPI, resumeTimer as resumeTimerAPI, updateTimer, endTimer, updatePlayerOfTheMatch, declareWalkover, abandonMatch, startMatchMultiSports, pauseMatchMultiSports, resumeMatchMultiSports, endMatchMultiSports, getSets, addSet, updateSet, markSetComplete, deleteSet, SetData } from '../../api/tournament/fixturesService';
import { getTournamentById, ManageTournament, getTournamentSeasons, TournamentSeason } from '../../api/tournament/manageTournamentService';
import { DeleteModal } from '../../Common/DeleteModal';
import { deleteFixture } from '../../api/tournament/fixturesService';
import { listPlayers } from '../../api/tournament/playerService';
import { listTeams } from '../../api/tournament/teamService';
import { getTeamStandings } from '../../api/tournament/standingsService';
import Select from 'react-select';
import { getImageUrl } from '../../utils/imageUrl';
import { getSportValue as getSportValueUtil } from '../../utils/getSportValue';
import {
  PICKLEBALL_EVENT_CATEGORIES,
  isPickleballDoubles,
} from '../../api/tournament/pickleballFixture';
import { TOURNAMENT_KNOCKOUT_STAGES } from '../../api/tournament/types';
import { normalizePlayerOfMatch } from './playerOfMatch';
import MatchHighlightsSection from './Highlights/MatchHighlightsSection';
import MatchGallerySection from './Gallery/MatchGallerySection';
import MatchArtworkSection from './MatchArtworkSection';

// Helper function to get result badge (Won/Lost/Draw) with payment-badge styling matching match management listing
const getResultBadge = (isWinner: boolean | null, isDraw: boolean = false) => {
  if (isDraw) {
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#FFF4E6',
          color: '#F39C12',
          borderColor: '#F39C12',
          textTransform: 'capitalize'
        }}
      >
        Draw
      </span>
    );
  }
  return (
    <span 
      className="badge payment-badge" 
      style={{
        border: '1px solid',
        fontWeight: '500',
        padding: '0.375rem 0.75rem',
        borderRadius: '6px',
        backgroundColor: isWinner ? '#E9FCF2' : '#FFEFF0',
        color: isWinner ? '#1B9E5C' : '#FC5C65',
        borderColor: isWinner ? '#1B9E5C' : '#FC5C65',
        textTransform: 'capitalize'
      }}
    >
      {isWinner ? 'Won' : 'Lost'}
    </span>
  );
};

// Helper function to get completed badge with payment-badge styling
const getCompletedBadge = () => {
  return (
    <span 
      className="badge payment-badge" 
      style={{
        border: '1px solid',
        fontWeight: '500',
        padding: '0.375rem 0.75rem',
        borderRadius: '6px',
        backgroundColor: '#E9FCF2',
        color: '#1B9E5C',
        borderColor: '#1B9E5C',
        textTransform: 'capitalize'
      }}
    >
      Completed
    </span>
  );
};

export const isLiveScoringSupportedSport = (sport: string | null | undefined): boolean => {
  const normalizedSport = (sport || '').replace(/[\s_-]/g, '').toLowerCase();

  // Comment out individual cases below to disable live scoring for that sport.
  switch (normalizedSport) {
    case 'football':
      return true;
    // case 'tennis':
    //   return true;
    case 'tabletennis':
      return true;
    case 'badminton':
      return true;
    case 'padel':
      return true;
    case 'pickleball':
      return true;
    // case 'basketball':
    //   return true;
    // case 'cricket':
    //   return true;
    // case 'chess':
    //   return true;
    // case 'swimming':
    //   return true;
    // case 'skating':
    //   return true;
    default:
      return false;
  }
};

const MatchDetailPage: React.FC = () => {
  const { id: tournamentId, matchId } = useParams<{ id: string; matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Direct links carry the season in the query string; routed navigation uses state.
  const tournamentSeasonId = (location.state as any)?.tournamentSeasonId
    || new URLSearchParams(location.search).get('seasonId')
    || undefined;
  const fromTournamentDetailPage = (location.state as any)?.fromTournamentDetailPage || false; // Check if we came from tournament detail page (default season)

  // Feature flag to hide Match Management and Score Management sections (under development)
  // Set to true to show these sections, false to hide them
  const SHOW_MATCH_AND_SCORE_MANAGEMENT = true;

  const [loading, setLoading] = useState(true);
  const [fixture, setFixture] = useState<Fixture | null>(null);
  const [tournament, setTournament] = useState<ManageTournament | null>(null);
  const [season, setSeason] = useState<TournamentSeason | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Form state for fixture editing
  const [formData, setFormData] = useState<any>({
    matchNum: '',
    sports: { value: 'Football', label: 'Football' },
    stage: null,
    ageCategory: null,
    eventCategory: null,
    group: null,
    round: '', // For Badminton and Table Tennis
    sets: '', // For Badminton and Table Tennis - numeric, odd numbers only
    teamA: null,
    teamB: null,
    playerA: null, // For Badminton and Table Tennis
    playerB: null, // For Badminton and Table Tennis
    playerA2: null, // Second player on Padel side 1
    playerB2: null, // Second player on Padel side 2
    date: '',
    time: '',
    venue: '',
    venueLong: '',
    title: '',
    subtitle: '',
    // Knockout fields for Football
    knockoutStage: null,
    knockoutIndex: '',
    knockoutMatchNum: '',
    streamingVideoUrl: '',
  });
  const [formTouched, setFormTouched] = useState<any>({});
  const [originalFormData, setOriginalFormData] = useState<any>(null);
  const [isFormModified, setIsFormModified] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Teams and groups loading state
  const [filteredTeams, setFilteredTeams] = useState<{ value: string; label: string }[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<{ value: string; label: string }[]>([]);
  const [availableGroups, setAvailableGroups] = useState<{ value: string; label: string }[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  
  // Age categories for dropdown
  const [ageCategoryOptions, setAgeCategoryOptions] = useState<{ value: string; label: string; standingsType?: string; format?: string }[]>([]);
  
  // Match management state
  const [matchStatus, setMatchStatus] = useState<string>('upcoming');
  const [teamAScore, setTeamAScore] = useState<number>(0);
  const [teamBScore, setTeamBScore] = useState<number>(0);
  const [isUpdatingScore, setIsUpdatingScore] = useState(false);
  const [isStartingMatch, setIsStartingMatch] = useState(false);
  const [isPausingMatch, setIsPausingMatch] = useState(false);
  const [isEndingMatch, setIsEndingMatch] = useState(false);
  
  // Match timer state
  const [matchTimer, setMatchTimer] = useState<number>(0); // Timer in seconds
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null); // Timestamp when timer started
  const [accumulatedTime, setAccumulatedTime] = useState<number>(0); // Total time accumulated (for pause/resume)
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const accumulatedTimeRef = useRef<number>(0); // Ref to track accumulated time for closure
  const timerStartTimeRef = useRef<number | null>(null); // Ref to track timer start time for closure
  const timerInitializedRef = useRef<boolean>(false); // Track if timer has been initialized from backend
  
  // Score update state
  const [scoreUpdates, setScoreUpdates] = useState<any[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>(''); // 'teamA' or 'teamB' - start empty to force selection
  const [selectedPlayer, setSelectedPlayer] = useState<any>(null);
  const [eventType, setEventType] = useState<string>(''); // 'goal', 'penalty', 'selfGoal'
  const [eventTime, setEventTime] = useState<string>('');
  const [teamAPlayers, setTeamAPlayers] = useState<any[]>([]);
  const [teamBPlayers, setTeamBPlayers] = useState<any[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  
  // Live scores state (from API)
  const [liveScoreEvents, setLiveScoreEvents] = useState<any[]>([]);
  const [loadingLiveScores, setLoadingLiveScores] = useState(false);
  const [liveScoreData, setLiveScoreData] = useState<any>(null); // Store full live score data for Player of the Match
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [eventToDelete, setEventToDelete] = useState<any>(null);
  const [showAddScoreForm, setShowAddScoreForm] = useState(false);
  const [isAddingScore, setIsAddingScore] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false); // Track if form has been modified during edit
  
  // Confirmation modal states
  const [showStartMatchModal, setShowStartMatchModal] = useState(false);
  const [showPauseMatchModal, setShowPauseMatchModal] = useState(false);
  const [showResumeMatchModal, setShowResumeMatchModal] = useState(false);
  const [showEndMatchModal, setShowEndMatchModal] = useState(false);
  const [showPlayerOfMatchModal, setShowPlayerOfMatchModal] = useState(false);
  const [isResettingTimer, setIsResettingTimer] = useState(false);
  const [showUpdateTimerModal, setShowUpdateTimerModal] = useState(false);
  const [updateTimerValue, setUpdateTimerValue] = useState<string>('');
  const [isUpdatingTimer, setIsUpdatingTimer] = useState(false);
  
  // Player of the match selection state
  const [playerOfTheMatch, setPlayerOfTheMatch] = useState<{ player: string | null; team: string | null }>({ player: null, team: null });
  const [secondPlayerOfTheMatch, setSecondPlayerOfTheMatch] = useState<{ player: string | null; team: string | null }>({ player: null, team: null });
  const [isSavingPlayerOfMatch, setIsSavingPlayerOfMatch] = useState(false);
  
  // Walkover and Abandon Match state (shared modal)
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionModalType, setActionModalType] = useState<'walkover' | 'abandon'>('walkover');
  const [walkoverTeam, setWalkoverTeam] = useState<string>(''); // 'teamA' or 'teamB'
  const [actionReason, setActionReason] = useState<string>(''); // Shared reason for both walkover and abandon
  
  // Sets management state (for Badminton and Table Tennis)
  const [sets, setSets] = useState<SetData[]>([]);
  const [loadingSets, setLoadingSets] = useState(false);
  const [editingSet, setEditingSet] = useState<SetData | null>(null);
  const [isSavingSet, setIsSavingSet] = useState(false);
  const [setToDelete, setSetToDelete] = useState<number | null>(null);
  const [setToMarkComplete, setSetToMarkComplete] = useState<number | null>(null);
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [lastSavedSetData, setLastSavedSetData] = useState<Map<number, { side1Points: number; side2Points: number }>>(new Map());
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const setsExtractedRef = useRef<boolean>(false); // Track if sets have been extracted from fixture

  // Custom time picker state
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedAmPm, setSelectedAmPm] = useState('');
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const timePickerButtonRef = useRef<HTMLButtonElement>(null);

  // Time picker options
  const hours = Array.from({ length: 12 }, (_, i) => ({ 
    value: String(i + 1).padStart(2, '0'), 
    label: String(i + 1) 
  }));
  const minutes = Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, '0'),
    label: i.toString().padStart(2, '0')
  }));
  const amPmOptions = [
    { value: 'AM', label: 'AM' },
    { value: 'PM', label: 'PM' },
  ];

  // Helper function to get sport value consistently using shared utility
  const getSportValue = useCallback((): string => {
    return getSportValueUtil(tournament, location);
  }, [tournament, location]);
  
  // Helper function to normalize sport name for comparison
  // Handles variations like "Table Tennis", "TableTennis", "tabletennis", "TT", "tt"
  const normalizeSportName = useCallback((sport: string): string => {
    if (!sport) return '';
    // Remove spaces and convert to lowercase for consistent comparison
    return sport.replace(/\s+/g, '').toLowerCase();
  }, []);
  
  // Helper function to check if sport is Badminton or Table Tennis
  const isBadmintonOrTT = useCallback((): boolean => {
    try {
      const sport = getSportValue();
      const normalized = normalizeSportName(sport);
      return normalized === 'badminton' || normalized === 'tabletennis' || normalized === 'tt';
    } catch {
      // If getSportValue fails, try formData
      const sport = formData.sports?.value || formData.sports?.label || '';
      const normalized = normalizeSportName(sport);
      return normalized === 'badminton' || normalized === 'tabletennis' || normalized === 'tt';
    }
  }, [getSportValue, formData.sports, normalizeSportName]);

  // Check if fixture editing is allowed (only when status is 'upcoming' or 'pending')
  // Disable editing when match is live (ongoing/paused) or completed
  // Also allow editing if matchStatus is empty/undefined (defaults to upcoming)
  const canEditFixture = !matchStatus || matchStatus === 'upcoming' || matchStatus === 'pending';
  const isMatchLiveOrCompleted = matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended';
  // Editing should remain available while the match is live/paused; only block it once truly finished
  const isMatchCompletedOrEnded = matchStatus === 'completed' || matchStatus === 'ended';
  const isLiveScoringSupported = isLiveScoringSupportedSport(
    fixture?.sports || formData.sports?.value || formData.sports?.label || getSportValue(),
  );
  
  // Check if match is finalized (completed, cancelled, or abandoned)
  // When match is finalized, we should hide action buttons (walkover, abandon, edit, delete)
  // Check both matchStatus and liveScoreData flags to ensure we catch all finalized states
  const isMatchFinalized = matchStatus === 'completed' || 
                          matchStatus === 'ended' || 
                          matchStatus === 'cancelled' ||
                          (liveScoreData?.isCancelled === true) ||
                          (liveScoreData?.isWalkover === true);
  
  // Check if user can perform match actions (walkover/abandon) - available at any time
  // But NOT when match is finalized (completed, cancelled, or abandoned)
  // These actions should be available as long as:
  // 1. Match exists (fixture is loaded)
  // 2. We're not in edit mode
  // 3. Match is not finalized
  // The API will handle actual permission checks
  const canPerformMatchActions = !!fixture && !showEditModal && !isMatchFinalized;
  
  // Helper function to extract error message from API response
  const extractErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
    if (!error) return defaultMessage;
    
    // Check if error has response data
    if (error.response?.data) {
      const responseData = error.response.data;
      
      // Try different possible error message fields (in order of priority)
      return responseData.message || 
             responseData.error || 
             responseData.details || 
             responseData.description ||
             responseData.errorMessage ||
             responseData.msg ||
             responseData.title ||
             (typeof responseData === 'string' ? responseData : null) ||
             defaultMessage;
    }
    
    // Check if error has a message property
    if (error.message) {
      return error.message;
    }
    
    // Check if error is a string
    if (typeof error === 'string') {
      return error;
    }
    
    // Fallback to default message
    return defaultMessage;
  };

  // Copy to clipboard handler
  const handleCopy = async (text: string, fieldName: string) => {
    if (!text || text === '-') return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      // Error copying - silently fail
    }
  };

  // Helper function to determine if match is team vs team or player vs player
  const isTeamMatch = (teamInfo: any): boolean => {
    // If teamInfo has a name property, it's a team match
    return !!(teamInfo?.name || teamInfo?.shortName);
  };

  // Helper function to get display name for team or player
  const getTeamOrPlayerName = (teamInfo: any, fallbackTeam: any): string => {
    // Try new structure first (team1Info/team2Info)
    if (teamInfo) {
      // Check if it's a team match (has name or shortName)
      if (teamInfo.name || teamInfo.shortName) {
        return teamInfo.name || teamInfo.shortName || '-';
      }
      // Otherwise, it's a player match - get first player's name
      if (teamInfo.players && Array.isArray(teamInfo.players) && teamInfo.players.length > 0) {
        const firstPlayer = teamInfo.players[0];
        return firstPlayer?.name || '-';
      }
      // If teamInfo exists but has no name/shortName and no players, it might be an empty object
      // In this case, fall through to fallbackTeam
    }
    
    // Fallback to old structure (teamA/teamB)
    if (fallbackTeam) {
      if (fallbackTeam.name || fallbackTeam.shortName) {
        return fallbackTeam.name || fallbackTeam.shortName || '-';
      }
    }
    
    return '-';
  };

  const getParticipantNames = (slot: number): string => {
    const participants = (fixture as any)?.participants;
    const participant = Array.isArray(participants)
      ? participants.find((item: any) => item?.slot === slot)
      : undefined;
    const names = participant?.players
      ?.map((player: any) => typeof player === 'string' ? player : player?.name)
      .filter(Boolean);

    return names?.join(' & ') || '';
  };

  // Helper to get team1 name
  // Priority: 1. Players (if available), 2. Team object (if available), 3. Fallback to old structure
  const getTeam1Name = (): string => {
    if (!fixture) {
      return '-';
    }

    const isFootball = fixture.sports?.replace(/[\s_-]/g, '').toLowerCase() === 'football';
    const participantNames = isFootball ? '' : getParticipantNames(1);
    if (participantNames) {
      return participantNames;
    }

    const participants = (fixture as any)?.participants;
    const participantTeam = Array.isArray(participants)
      ? participants.find((participant: any) => participant?.slot === 1)?.team
      : undefined;
    const participantTeamId = typeof participantTeam === 'string'
      ? participantTeam
      : participantTeam?._id;
    const participantTeamName = filteredTeams.find((team) => team.value === participantTeamId)?.label;
    if (participantTeamName) {
      return participantTeamName;
    }

    // Check new structure (side1) first for Badminton/Tennis
    const side1 = (fixture as any)?.side1;
    const eventCategory = (fixture as any)?.eventCategory;
    
    if (side1) {
      // Check eventCategory to determine priority
      // Singles: prioritize players, Doubles: prioritize teams
      const isSingles = eventCategory === 'singles' || eventCategory === 'single';
      const isDoubles = eventCategory === 'doubles' || eventCategory === 'double';
      
      if (isSingles) {
        // For singles: prioritize players
        if (side1.players && Array.isArray(side1.players) && side1.players.length > 0) {
          const playerNames = side1.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
        // Fallback to team if players not available
        if (side1.team && typeof side1.team === 'object' && side1.team._id) {
          return side1.team.name || side1.team.shortName || '-';
        }
      } else if (isDoubles) {
        // For doubles: prioritize teams
        if (side1.team && typeof side1.team === 'object' && side1.team._id) {
          return side1.team.name || side1.team.shortName || '-';
        }
        // Fallback to players if team not available
        if (side1.players && Array.isArray(side1.players) && side1.players.length > 0) {
          const playerNames = side1.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
      } else {
        // No eventCategory or unknown: use original priority (players first, then team)
        if (side1.players && Array.isArray(side1.players) && side1.players.length > 0) {
          const playerNames = side1.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
        if (side1.team && typeof side1.team === 'object' && side1.team._id) {
          return side1.team.name || side1.team.shortName || '-';
        }
      }
      
      // If side1.team is a string ID, fall through to check team1Info/teamA
    }
    
    // Priority 3: Fallback to old structure
    const team1Info = (fixture as any)?.team1Info;
    const teamA = fixture.teamA;
    const name = getTeamOrPlayerName(team1Info, teamA);
    return name;
  };

  // Helper to get team2 name
  // Priority based on eventCategory: Singles=players, Doubles=teams
  const getTeam2Name = (): string => {
    if (!fixture) {
      return '-';
    }

    const isFootball = fixture.sports?.replace(/[\s_-]/g, '').toLowerCase() === 'football';
    const participantNames = isFootball ? '' : getParticipantNames(2);
    if (participantNames) {
      return participantNames;
    }

    const participants = (fixture as any)?.participants;
    const participantTeam = Array.isArray(participants)
      ? participants.find((participant: any) => participant?.slot === 2)?.team
      : undefined;
    const participantTeamId = typeof participantTeam === 'string'
      ? participantTeam
      : participantTeam?._id;
    const participantTeamName = filteredTeams.find((team) => team.value === participantTeamId)?.label;
    if (participantTeamName) {
      return participantTeamName;
    }

    // Check new structure (side2) first for Badminton/Tennis
    const side2 = (fixture as any)?.side2;
    const eventCategory = (fixture as any)?.eventCategory;
    
    if (side2) {
      // Check eventCategory to determine priority
      // Singles: prioritize players, Doubles: prioritize teams
      const isSingles = eventCategory === 'singles' || eventCategory === 'single';
      const isDoubles = eventCategory === 'doubles' || eventCategory === 'double';
      
      if (isSingles) {
        // For singles: prioritize players
        if (side2.players && Array.isArray(side2.players) && side2.players.length > 0) {
          const playerNames = side2.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
        // Fallback to team if players not available
        if (side2.team && typeof side2.team === 'object' && side2.team._id) {
          return side2.team.name || side2.team.shortName || '-';
        }
      } else if (isDoubles) {
        // For doubles: prioritize teams
        if (side2.team && typeof side2.team === 'object' && side2.team._id) {
          return side2.team.name || side2.team.shortName || '-';
        }
        // Fallback to players if team not available
        if (side2.players && Array.isArray(side2.players) && side2.players.length > 0) {
          const playerNames = side2.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
      } else {
        // No eventCategory or unknown: use original priority (players first, then team)
        if (side2.players && Array.isArray(side2.players) && side2.players.length > 0) {
          const playerNames = side2.players
            .map((p: any) => p?.name)
            .filter((name: string) => name)
            .join(' & ');
          return playerNames || '-';
        }
        if (side2.team && typeof side2.team === 'object' && side2.team._id) {
          return side2.team.name || side2.team.shortName || '-';
        }
      }
      
      // If side2.team is a string ID, fall through to check team2Info/teamB
    }
    
    // Priority 3: Fallback to old structure
    const team2Info = (fixture as any)?.team2Info;
    const teamB = fixture.teamB;
    const name = getTeamOrPlayerName(team2Info, teamB);
    return name;
  };

  // Check if form has been modified (excluding stream fields)
  // Accept optional formData parameter to use latest data instead of stale closure
  const checkFormModified = useCallback((currentFormData?: any) => {
    if (!originalFormData) {
      setIsFormModified(false);
      return;
    }
    
    const dataToCheck = currentFormData || formData;
    const sport = dataToCheck.sports?.value || dataToCheck.sports?.label || '';
    // Normalize sport name by removing spaces and converting to lowercase
    // Handles "Table Tennis", "TableTennis", "tabletennis", "TT", "tt"
    const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
    const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
    const isPadel = normalizedSport === 'padel';
    const isPickleball = normalizedSport === 'pickleball';
    const requiresTwoPlayersPerSide = isPadel || (isPickleball && isPickleballDoubles(dataToCheck.eventCategory?.value));
    
    // Check age category format to determine if we should check teamA/teamB or playerA/playerB
    const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === dataToCheck.ageCategory?.value);
    const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
    const ageCategoryLabel = ageCategoryObj?.label?.toLowerCase() || '';
    let isPlayerFormat = ageCategoryFormat === 'player';
    if (isPadel) {
      isPlayerFormat = true;
    }
    const isSingleAgeCategory = ageCategoryLabel.includes('single') || ageCategoryLabel.includes('singles');
    
    // Use same fallback logic as validation
    if (!ageCategoryFormat && dataToCheck.sports) {
      const sport = dataToCheck.sports?.value || dataToCheck.sports?.label || '';
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      if (isBadmintonOrTT) {
        isPlayerFormat = true;
      }
    } else if (ageCategoryFormat && isSingleAgeCategory && dataToCheck.sports) {
      const sport = dataToCheck.sports?.value || dataToCheck.sports?.label || '';
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      if (isBadmintonOrTT && !isPlayerFormat) {
        isPlayerFormat = true;
      }
    }
    
    const hasChanges = (
      dataToCheck.matchNum !== originalFormData.matchNum ||
      dataToCheck.time !== originalFormData.time ||
      dataToCheck.sports?.value !== originalFormData.sports?.value ||
      dataToCheck.stage?.value !== originalFormData.stage?.value ||
      dataToCheck.ageCategory?.value !== originalFormData.ageCategory?.value ||
      dataToCheck.eventCategory?.value !== originalFormData.eventCategory?.value ||
      JSON.stringify(dataToCheck.group) !== JSON.stringify(originalFormData.group) ||
      (isPlayerFormat ? (
        dataToCheck.playerA?.value !== originalFormData.playerA?.value ||
        dataToCheck.playerB?.value !== originalFormData.playerB?.value ||
        (requiresTwoPlayersPerSide && (
          dataToCheck.playerA2?.value !== originalFormData.playerA2?.value ||
          dataToCheck.playerB2?.value !== originalFormData.playerB2?.value
        ))
      ) : (
        dataToCheck.teamA?.value !== originalFormData.teamA?.value ||
        dataToCheck.teamB?.value !== originalFormData.teamB?.value
      )) ||
      dataToCheck.date !== originalFormData.date ||
      dataToCheck.venue !== originalFormData.venue ||
      dataToCheck.venueLong !== originalFormData.venueLong ||
      dataToCheck.title !== originalFormData.title ||
      dataToCheck.subtitle !== originalFormData.subtitle ||
      dataToCheck.streamingVideoUrl !== originalFormData.streamingVideoUrl ||
      (isBadmintonOrTT && (dataToCheck.round !== originalFormData.round || dataToCheck.sets !== originalFormData.sets))
    );
    
    setIsFormModified(hasChanges);
  }, [formData, originalFormData]);

  useEffect(() => {
    if (originalFormData) {
      checkFormModified();
    }
  }, [formData, originalFormData, checkFormModified]);

  // Load fixture and tournament data
  useEffect(() => {
    const load = async () => {
      if (!tournamentId || !matchId) return;
      
      // Reset timer initialization flag when loading new match
      timerInitializedRef.current = false;
      
      // Clear any existing timer interval FIRST to prevent stale calculations
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      // Reset timer start time ref when loading a new match
      // NOTE: Don't reset accumulatedTimeRef here - it will be set from backend value
      timerStartTimeRef.current = null;
      
      setLoading(true);
      setError(null);
      try {
        // Get sport from location state, URL search params, or fixture data
        // Sport is REQUIRED for tournament management module
        const selectedTournamentFromState = (location.state as any)?.selectedTournament;
        const sportFromState = selectedTournamentFromState?.sports;
        const urlParams = new URLSearchParams(location.search);
        const sportFromUrl = urlParams.get('sport');
        
        // Try to get sport from available sources
        let sport = sportFromState || sportFromUrl;
        
        // Direct links do not have router state. Resolve the tournament first:
        // tournament lookup is sport-agnostic, while fixture lookup is not.
        if (!sport) {
          const tournamentData = await getTournamentById(tournamentId);
          sport = tournamentData?.sports;
        }
        
        if (!sport) {
          throw new Error('Sport parameter is required. Navigate from the tournament list or include ?sport=<sport> in the URL.');
        }
        
        // Log API call for debugging
        console.log('Loading fixture details - getFixtureById called:', {
          matchId: matchId,
          sport: sport,
          tournamentId: tournamentId
        });
        
        const [fixtureRes, tournamentRes] = await Promise.all([
          getFixtureById(matchId, sport), // FIX: Pass sport parameter
          getTournamentById(tournamentId, sport)
        ]);
        
        // Log API response for debugging
        console.log('Fixture details loaded - getFixtureById response:', {
          matchId: matchId,
          sport: sport,
          hasFixtureData: !!fixtureRes,
          fixtureData: fixtureRes?.data || fixtureRes
        });
        
        // Reset sets extraction ref for new match
        setsExtractedRef.current = false;
        
        const fixtureData = fixtureRes?.data || fixtureRes;
        setFixture(fixtureData as Fixture);
        setTournament(tournamentRes || null);
        
        // Extract sets from fixture data if available (for Badminton/TT)
        // This ensures sets are available immediately, even before loadSets() completes
        // This is especially important when match is completed to show set cards
        if (isBadmintonOrTT() && fixtureData) {
          // Check if sets are in fixture data (could be in sets array or nested)
          const fixtureSets = Array.isArray((fixtureData as any)?.sets) 
            ? (fixtureData as any).sets 
            : (Array.isArray((fixtureData as any)?.liveScoreData?.sets) 
                ? (fixtureData as any).liveScoreData.sets 
                : []);
          
          if (fixtureSets.length > 0) {
            setSets(fixtureSets);
            setsExtractedRef.current = true;
            setLastSavedSetData(prev => {
              const newMap = new Map(prev);
              fixtureSets.forEach((set: SetData) => {
                if (set.setNumber) {
                  newMap.set(set.setNumber, {
                    side1Points: set.side1Points || 0,
                    side2Points: set.side2Points || 0
                  });
                }
              });
              return newMap;
            });
          }
        }
        
        // Load season data if tournamentSeasonId exists and not from tournament detail page
        if (tournamentSeasonId && !fromTournamentDetailPage && tournamentRes) {
          try {
            const { getTournamentSeasons } = await import('../../api/tournament/manageTournamentService');
            const seasons = await getTournamentSeasons(tournamentId, sport);
            const seasonData = seasons.find((s: TournamentSeason) => s._id === tournamentSeasonId);
            if (seasonData) {
              setSeason(seasonData);
            }
          } catch (seasonError: any) {
            // Continue without season data - breadcrumb will show fallback
          }
        }
        
        // Extract match status - normalize to standard values
        // Priority: Use 'status' field first (primary field from API), then fallback to other fields
        const rawStatus = (fixtureData as any)?.status || 
                         (fixtureData as any)?.streamStatus || 
                         (fixtureData as any)?.liveStreamInfo?.status || 
                         (fixtureData as any)?.matchStatus ||
                         'upcoming';
        
        // Get timer value early to use for status determination
        const backendTimer = (fixtureData as any)?.timer;
        
        // Normalize status values to match our expected values
        let normalizedStatus = (rawStatus || '').toLowerCase().trim();
        
        // Map various status values to standard ones
        if (normalizedStatus === 'yet-to-start' || normalizedStatus === 'yet_to_start' || normalizedStatus === 'pending' || normalizedStatus === 'not-started' || normalizedStatus === 'not started') {
          normalizedStatus = 'upcoming';
        } else if (normalizedStatus === 'ongoing' || normalizedStatus === 'live' || normalizedStatus === 'in-progress' || normalizedStatus === 'in_progress' || normalizedStatus === 'started' || normalizedStatus === 'start' || normalizedStatus === 'running') {
          normalizedStatus = 'ongoing';
        } else if (normalizedStatus === 'paused' || normalizedStatus === 'on-hold' || normalizedStatus === 'on_hold' || normalizedStatus === 'hold') {
          normalizedStatus = 'paused';
        } else if (normalizedStatus === 'completed' || normalizedStatus === 'ended' || normalizedStatus === 'finished' || normalizedStatus === 'complete') {
          normalizedStatus = 'completed';
        } else if (!normalizedStatus || normalizedStatus === '') {
          // If no status found, check timer value as fallback
          if (backendTimer !== undefined && backendTimer !== null && backendTimer > 0) {
            // Timer is running, so match is likely ongoing
            normalizedStatus = 'ongoing';
          } else {
            normalizedStatus = 'upcoming';
          }
        } else {
          // If status doesn't match any known values, default to 'upcoming' to allow editing
          // This handles edge cases where API returns unexpected status values
          normalizedStatus = 'upcoming';
        }
        
        // Additional check: If timer exists and is > 0, but status is still 'upcoming', 
        // it means the match has started (backend has timer but status might not be updated)
        if (normalizedStatus === 'upcoming' && backendTimer !== undefined && backendTimer !== null && backendTimer > 0) {
          normalizedStatus = 'ongoing';
        }
        
        // Important: Don't override 'paused' status even if timer exists
        // If status is already 'paused', keep it as paused (timer can exist but match is paused)
        // This ensures paused state persists after page refresh
        if (normalizedStatus === 'paused') {
          // Keep it as paused - don't change to ongoing even if timer > 0
          setMatchStatus('paused');
        } else {
          setMatchStatus(normalizedStatus);
        }
        
        // Initialize timer from backend
        // IMPORTANT: Always use the timer field from the backend response - never use default or hardcoded values
        // Always reinitialize timer from backend when component loads to ensure we have the latest value
        if (backendTimer !== undefined && backendTimer !== null) {
          // Timer value is in seconds from backend - parse it correctly
          // The backend may send a decimal value (e.g., 1003.654), so we need to handle it properly
          const rawTimerValue = typeof backendTimer === 'number' ? backendTimer : parseFloat(String(backendTimer)) || 0;
          // Use Math.floor to get whole seconds for display, but preserve the actual value for accuracy
          const timerValue = Math.floor(rawTimerValue);
          
          if (!timerInitializedRef.current) {
            if (normalizedStatus === 'ongoing') {
            const roundedTimerValue = Math.floor(timerValue);
            
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            const cappedTimerValue = roundedTimerValue >= 5999 ? 5999 : roundedTimerValue;
            
            accumulatedTimeRef.current = cappedTimerValue;
            setAccumulatedTime(cappedTimerValue);
            setMatchTimer(cappedTimerValue);
            
            if (cappedTimerValue >= 5999) {
              setIsTimerRunning(false);
              setTimerStartTime(null);
              timerStartTimeRef.current = null;
              return;
            }
            
            const startTime = Date.now();
            setTimerStartTime(startTime);
            timerStartTimeRef.current = startTime;
            setIsTimerRunning(true);
            
            timerIntervalRef.current = setInterval(() => {
              setMatchTimer(() => {
                const now = Date.now();
                const start = timerStartTimeRef.current || startTime;
                const elapsed = Math.floor((now - start) / 1000);
                const baseTime = accumulatedTimeRef.current;
                const newTime = baseTime + elapsed;
                const finalTime = Math.floor(newTime);
                
                if (finalTime >= 5999) {
                  if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                  }
                  accumulatedTimeRef.current = 5999;
                  setAccumulatedTime(5999);
                  setIsTimerRunning(false);
                  setTimerStartTime(null);
                  timerStartTimeRef.current = null;
                  return 5999;
                }
                
                return finalTime;
              });
            }, 1000);
          } else if (normalizedStatus === 'paused') {
            const roundedTimerValue = Math.floor(timerValue);
            
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            const cappedTimerValue = roundedTimerValue >= 5999 ? 5999 : roundedTimerValue;
            
            accumulatedTimeRef.current = cappedTimerValue;
            setAccumulatedTime(cappedTimerValue);
            setMatchTimer(cappedTimerValue);
            setIsTimerRunning(false);
            setTimerStartTime(null);
            timerStartTimeRef.current = null;
          } else {
            // Match is completed or upcoming - reset timer
            setMatchTimer(0);
            setAccumulatedTime(0);
            accumulatedTimeRef.current = 0;
            setIsTimerRunning(false);
            setTimerStartTime(null);
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
          }
          
          // Mark timer as initialized after setting it up
          timerInitializedRef.current = true;
        }
        } else {
          // No timer value from backend - reset to 0
          if (!timerInitializedRef.current) {
            setMatchTimer(0);
            setAccumulatedTime(0);
            accumulatedTimeRef.current = 0;
            setIsTimerRunning(false);
            setTimerStartTime(null);
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            timerInitializedRef.current = true;
          }
        }
        
        // Clear live score events if match hasn't started
        if (normalizedStatus === 'upcoming' || normalizedStatus === 'pending' || normalizedStatus === 'yet-to-start') {
          setLiveScoreEvents([]);
          setShowAddScoreForm(false);
        }
        
        // Extract scores if available
        // For Badminton/TT, calculate score from completed sets
        // For Football, use score from fixture or live scores
        if (isBadmintonOrTT()) {
          // Score will be calculated from sets in useEffect
        } else {
        setTeamAScore((fixtureData as any)?.teamAScore || (fixtureData as any)?.teamA?.score || 0);
        setTeamBScore((fixtureData as any)?.teamBScore || (fixtureData as any)?.teamB?.score || 0);
        }
        
        // Load age categories for the tournament
        const { getAllAgeCategoriesPaginated } = await import('../../api/tournament/ageCategoryService');
        // Get sport value for API calls
        const sportForAgeCategories = getSportValue();
        // Pass tournamentSeasonId when available (from location.state)
        const ageCategoriesRes = await getAllAgeCategoriesPaginated(tournamentId, 1, 100, '', '', '', tournamentSeasonId || undefined, sportForAgeCategories);
        const ageCategoriesData = ageCategoriesRes?.paginatedResults || [];
        const ageCategoryOptionsList = ageCategoriesData.map((ac: any) => ({
          value: ac._id,
          label: ac.ageCategory,
          standingsType: ac.standingsType,
          format: ac.format // Include format to check if it's 'player' or 'Team'
        }));
        
        // Debug logging to check format field
        console.log('Age categories loaded:', {
          count: ageCategoryOptionsList.length,
          ageCategories: ageCategoryOptionsList.map((ac: { value: string; label: string; format?: string }) => ({
            label: ac.label,
            format: ac.format,
            value: ac.value
          })),
          tournamentSeasonId: tournamentSeasonId,
          rawApiResponse: ageCategoriesData.map((ac: any) => ({
            _id: ac._id,
            ageCategory: ac.ageCategory,
            format: ac.format,
            standingsType: ac.standingsType
          }))
        });
        
        setAgeCategoryOptions(ageCategoryOptionsList);
        
        // Load sets for Badminton and Table Tennis
        // Skip loadSets() if sets are already extracted from fixture data (especially for completed matches)
        // This prevents overwriting sets that were already loaded from fixture data
        if (isBadmintonOrTT() && matchId) {
          // Only call loadSets() if sets haven't been extracted yet
          // For completed matches, sets are already extracted from fixture data above
          // Use setsExtractedRef to check if sets were already extracted (this is set synchronously)
          if (!setsExtractedRef.current) {
            await loadSets();
          }
        }
        
        // Convert time to 12-hour format if needed
        // Priority: time (scheduled time) -> dateTime (scheduled datetime) -> startTime (actual start time)
        let timeValue = fixtureData?.time || (fixtureData as any)?.dateTime || fixtureData?.startTime || '';
        if (timeValue && timeValue.includes('T')) {
          // ISO datetime string
          const date = new Date(timeValue);
          const hours = date.getHours();
          const minutes = date.getMinutes();
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const hours12 = hours % 12 || 12;
          timeValue = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        } else if (timeValue && timeValue.includes(':') && !timeValue.includes('AM') && !timeValue.includes('PM')) {
          // 24-hour format
          const parts = timeValue.split(':');
          const hours = parseInt(parts[0], 10);
          const minutes = parseInt(parts[1] || '0', 10);
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const hours12 = hours % 12 || 12;
          timeValue = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }
        
        // Find age category option - use the local ageCategoryOptionsList since state might not be updated yet
        const ageCategoryId = typeof fixtureData?.ageCategory === 'object' ? fixtureData?.ageCategory?._id : fixtureData?.ageCategory;
        let selectedAgeCategory = ageCategoryOptionsList.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === ageCategoryId);
        
        if (!selectedAgeCategory && ageCategoryId) {
          try {
            const { getAgeCategoryById } = await import('../../api/tournament/ageCategoryService');
            const ageCategoryResponse = await getAgeCategoryById(ageCategoryId, sportForAgeCategories);
            const ageCategory = ageCategoryResponse?.data?.data || ageCategoryResponse?.data || ageCategoryResponse;

            if (ageCategory?._id) {
              selectedAgeCategory = {
                value: ageCategory._id,
                label: ageCategory.ageCategory || ageCategory.name || ageCategory._id,
                standingsType: ageCategory.standingsType,
                format: ageCategory.format,
              };
              ageCategoryOptionsList.push(selectedAgeCategory);
              setAgeCategoryOptions(ageCategoryOptionsList);
              setFixture((currentFixture) => currentFixture
                ? { ...currentFixture, ageCategory }
                : currentFixture);
            }
          } catch (error) {
            console.warn('Unable to load the fixture age category:', error);
          }
        }
        
        // Find stage option - check both fixture.stage and fixture.knockout.stage
        const stageOptions = [
          { value: 'league', label: 'League' },
          { value: 'knockout', label: 'Knockout' },
        ];
        const fixtureStage = fixtureData?.stage || (fixtureData as any)?.knockout?.stage;
        const selectedStage = fixtureStage ? stageOptions.find(s => s.value === fixtureStage?.toLowerCase()) || null : null;
        
        // Get fixture sport for format detection (must be declared before use)
        const fixtureSport = fixtureData?.sports || getSportValue();
        const isFootball = fixtureSport.replace(/[\s_-]/g, '').toLowerCase() === 'football';
        
        // Initialize form data for editing
        // Get team/player info - check new structure (side1/side2) first for Badminton/Tennis
        const participants = (fixtureData as any)?.participants;
        const participantOne = !isFootball && Array.isArray(participants)
          ? participants.find((participant: any) => participant?.slot === 1)
          : undefined;
        const participantTwo = !isFootball && Array.isArray(participants)
          ? participants.find((participant: any) => participant?.slot === 2)
          : undefined;
        const side1 = (fixtureData as any)?.side1 || participantOne;
        const side2 = (fixtureData as any)?.side2 || participantTwo;
        const toPlayerOption = (player: any) => player?._id
          ? { value: player._id, label: player.name || player._id }
          : null;
        const padelPlayerA2 = toPlayerOption(side1?.players?.[1]);
        const padelPlayerB2 = toPlayerOption(side2?.players?.[1]);
        const team1Info = (fixtureData as any)?.team1Info;
        const team2Info = (fixtureData as any)?.team2Info;
        const teamA = fixtureData?.teamA;
        const teamB = fixtureData?.teamB;
        
        // Determine age category format FIRST to know whether to prioritize teams or players
        const ageCategoryObjForFormatCheck = ageCategoryOptionsList.find((ac: { value: string; label: string; format?: string }) => ac.value === ageCategoryId);
        const ageCategoryFormatForFormatCheck = ageCategoryObjForFormatCheck?.format?.toLowerCase();
        const ageCategoryLabelForFormatCheck = ageCategoryObjForFormatCheck?.label?.toLowerCase() || '';
        let isPlayerFormatForExtraction = ageCategoryFormatForFormatCheck === 'player';
        const isPadel = fixtureSport.replace(/\s+/g, '').toLowerCase() === 'padel';
        const isPickleball = fixtureSport.replace(/\s+/g, '').toLowerCase() === 'pickleball';
        const usesMultiPlayerSides = isPadel || (isPickleball && isPickleballDoubles((fixtureData as any)?.eventCategory));
        if (isPadel || isPickleball) {
          isPlayerFormatForExtraction = true;
        }
        const isSingleAgeCategoryForFormatCheck = ageCategoryLabelForFormatCheck.includes('single') || ageCategoryLabelForFormatCheck.includes('singles');
        
        // Fallback: If format is missing, check sport type
        if (!ageCategoryFormatForFormatCheck) {
          const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
          const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
          if (isBadmintonOrTT) {
            isPlayerFormatForExtraction = true;
          }
        } else if (ageCategoryFormatForFormatCheck && isSingleAgeCategoryForFormatCheck) {
          const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
          const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
          if (isBadmintonOrTT && !isPlayerFormatForExtraction) {
            isPlayerFormatForExtraction = true;
          }
        }
        
        // Get team/player IDs and names - Priority based on format: Teams first for team format, Players first for player format
        let team1Id, team2Id, team1Name, team2Name;
        
        if (side1) {
          if (isPlayerFormatForExtraction) {
            // For player format: Priority 1: Players, Priority 2: Team object
            if (side1.players && Array.isArray(side1.players) && side1.players.length > 0) {
              team1Id = side1.players[0]?._id;
              team1Name = side1.players.map((p: any) => p?.name).filter((name: string) => name).join(' & ') || side1.players[0]?.name;
            } else if (side1.team && typeof side1.team === 'object' && side1.team._id) {
              team1Id = side1.team._id;
              team1Name = side1.team.name || side1.team.shortName;
            } else if (side1.team && typeof side1.team === 'string') {
              team1Id = side1.team;
              team1Name = 'Player A';
            }
          } else {
            // For team format: Priority 1: Team object, Priority 2: Players (fallback)
            if (side1.team && typeof side1.team === 'object' && side1.team._id) {
              team1Id = side1.team._id;
              team1Name = side1.team.name || side1.team.shortName;
            } else if (side1.team && typeof side1.team === 'string') {
              team1Id = side1.team;
              // Try to get name from team1Info or teamA
              team1Name = team1Info ? (team1Info.name || team1Info.shortName) : (teamA?.name || teamA?.shortName) || 'Team A';
            } else if (side1.players && Array.isArray(side1.players) && side1.players.length > 0) {
              // Fallback to players if team not found
              team1Id = side1.players[0]?._id;
              team1Name = side1.players.map((p: any) => p?.name).filter((name: string) => name).join(' & ') || side1.players[0]?.name;
            }
          }
        }
        
        if (side2) {
          if (isPlayerFormatForExtraction) {
            // For player format: Priority 1: Players, Priority 2: Team object
            if (side2.players && Array.isArray(side2.players) && side2.players.length > 0) {
              team2Id = side2.players[0]?._id;
              team2Name = side2.players.map((p: any) => p?.name).filter((name: string) => name).join(' & ') || side2.players[0]?.name;
            } else if (side2.team && typeof side2.team === 'object' && side2.team._id) {
              team2Id = side2.team._id;
              team2Name = side2.team.name || side2.team.shortName;
            } else if (side2.team && typeof side2.team === 'string') {
              team2Id = side2.team;
              team2Name = 'Player B';
            }
          } else {
            // For team format: Priority 1: Team object, Priority 2: Players (fallback)
            if (side2.team && typeof side2.team === 'object' && side2.team._id) {
              team2Id = side2.team._id;
              team2Name = side2.team.name || side2.team.shortName;
            } else if (side2.team && typeof side2.team === 'string') {
              team2Id = side2.team;
              // Try to get name from team2Info or teamB
              team2Name = team2Info ? (team2Info.name || team2Info.shortName) : (teamB?.name || teamB?.shortName) || 'Team B';
            } else if (side2.players && Array.isArray(side2.players) && side2.players.length > 0) {
              // Fallback to players if team not found
              team2Id = side2.players[0]?._id;
              team2Name = side2.players.map((p: any) => p?.name).filter((name: string) => name).join(' & ') || side2.players[0]?.name;
            }
          }
        }
        
        // Priority 3: Fallback to old structure if new structure didn't provide data
        // Also handle cases where side1.team/side2.team are string IDs but we need the full team object
        if (!team1Id || !team1Name || team1Name === 'Team A') {
          // Try to get team ID from various sources
          const fallbackTeam1Id = team1Id || team1Info?._id || teamA?._id || (typeof side1?.team === 'string' ? side1.team : null);
          // Try to get team name from various sources
          const fallbackTeam1Name = team1Info ? (team1Info.name || team1Info.shortName || (team1Info.players?.[0]?.name)) : 
                                      (teamA?.name || teamA?.shortName || 
                                       (side1?.team && typeof side1.team === 'object' ? (side1.team.name || side1.team.shortName) : null));
          
          if (fallbackTeam1Id) {
            team1Id = fallbackTeam1Id;
            // Only update name if we have a real name (not placeholder)
            if (fallbackTeam1Name && fallbackTeam1Name !== 'Team A') {
              team1Name = fallbackTeam1Name;
            } else if (!team1Name || team1Name === 'Team A') {
              // Keep placeholder if no real name found, but at least we have the ID
              team1Name = 'Team A';
            }
          }
        }
        
        if (!team2Id || !team2Name || team2Name === 'Team B') {
          // Try to get team ID from various sources
          const fallbackTeam2Id = team2Id || team2Info?._id || teamB?._id || (typeof side2?.team === 'string' ? side2.team : null);
          // Try to get team name from various sources
          const fallbackTeam2Name = team2Info ? (team2Info.name || team2Info.shortName || (team2Info.players?.[0]?.name)) : 
                                      (teamB?.name || teamB?.shortName || 
                                       (side2?.team && typeof side2.team === 'object' ? (side2.team.name || side2.team.shortName) : null));
          
          if (fallbackTeam2Id) {
            team2Id = fallbackTeam2Id;
            // Only update name if we have a real name (not placeholder)
            if (fallbackTeam2Name && fallbackTeam2Name !== 'Team B') {
              team2Name = fallbackTeam2Name;
            } else if (!team2Name || team2Name === 'Team B') {
              // Keep placeholder if no real name found, but at least we have the ID
              team2Name = 'Team B';
            }
          }
        }
        
        // Check age category format instead of sport type
        const ageCategoryObjForInit = ageCategoryOptionsList.find((ac: { value: string; label: string; format?: string }) => ac.value === selectedAgeCategory?.value);
        const ageCategoryFormatForInit = ageCategoryObjForInit?.format?.toLowerCase();
        const ageCategoryLabelForInit = ageCategoryObjForInit?.label?.toLowerCase() || '';
        
        // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
        let isPlayerFormatForInit = ageCategoryFormatForInit === 'player';
        if (isPadel || isPickleball) {
          isPlayerFormatForInit = true;
        }
        const isSingleAgeCategoryForInit = ageCategoryLabelForInit.includes('single') || ageCategoryLabelForInit.includes('singles');
        
        // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
        if (!ageCategoryFormatForInit) {
          const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
          const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
          if (isBadmintonOrTT) {
            if (isSingleAgeCategoryForInit) {
              isPlayerFormatForInit = true;
            } else {
              isPlayerFormatForInit = true; // Badminton/Table Tennis default to player format
            }
          }
        } else if (ageCategoryFormatForInit && isSingleAgeCategoryForInit) {
          const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
          const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
          if (isBadmintonOrTT && !isPlayerFormatForInit) {
            isPlayerFormatForInit = true;
          }
        }
        
        // Initialize group - use fixture.group if it exists
        let groupField = null;
        if (fixtureData?.group) {
          groupField = { value: fixtureData.group, label: `Group ${fixtureData.group}` };
        }
        
        // For Badminton/Table Tennis, prefill round and sets regardless of format (team or player)
        // For other sports, only prefill if player format
        const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTTSport = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
        const shouldPrefillRoundAndSets = isBadmintonOrTTSport || isPlayerFormatForInit;
        
        // Debug logging to help identify issues
        console.log('Team data extraction for team format:', {
          team1Id: team1Id,
          team1Name: team1Name,
          team2Id: team2Id,
          team2Name: team2Name,
          side1: side1,
          side2: side2,
          team1Info: team1Info,
          team2Info: team2Info,
          teamA: teamA,
          teamB: teamB,
          isPlayerFormat: isPlayerFormatForInit,
          isPlayerFormatForExtraction: isPlayerFormatForExtraction,
          ageCategoryFormat: ageCategoryFormatForInit,
          ageCategoryLabel: ageCategoryObjForInit?.label,
          ageCategoryId: ageCategoryId,
          selectedAgeCategory: selectedAgeCategory,
          shouldPrefillRoundAndSets: shouldPrefillRoundAndSets
        });
        
        // Load knockout data if available
        const knockoutData = (fixtureData as any)?.knockout;
        const knockoutStageOptions = [
          { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
        ];
        const knockoutStageField = knockoutData?.stage 
          ? knockoutStageOptions.find(opt => opt.value === knockoutData.stage) || null
          : null;

        const initialFormData = {
          matchNum: fixtureData?.matchNum || '',
          sports: { value: fixtureSport, label: fixtureSport },
          date: fixtureData?.date || '',
          time: timeValue,
          venue: fixtureData?.venue || '',
          venueLong: fixtureData?.venueLong || fixtureData?.venue || '',
          title: (fixtureData as any)?.title || '',
          subtitle: (fixtureData as any)?.subtitle || '',
          teamA: isPlayerFormatForInit ? null : (team1Id ? { value: team1Id, label: team1Name || 'Team A' } : null),
          teamB: isPlayerFormatForInit ? null : (team2Id ? { value: team2Id, label: team2Name || 'Team B' } : null),
          playerA: isPlayerFormatForInit ? ((isPadel || isPickleball) ? toPlayerOption(side1?.players?.[0]) : (team1Id ? { value: team1Id, label: team1Name || 'Player A' } : null)) : null,
          playerB: isPlayerFormatForInit ? ((isPadel || isPickleball) ? toPlayerOption(side2?.players?.[0]) : (team2Id ? { value: team2Id, label: team2Name || 'Player B' } : null)) : null,
          playerA2: usesMultiPlayerSides ? padelPlayerA2 : null,
          playerB2: usesMultiPlayerSides ? padelPlayerB2 : null,
          ageCategory: selectedAgeCategory || null,
          eventCategory: isPickleball
            ? PICKLEBALL_EVENT_CATEGORIES.find((option) => option.value === (fixtureData as any)?.eventCategory) || null
            : null,
          stage: selectedStage,
          group: groupField,
          round: shouldPrefillRoundAndSets ? (fixtureData?.round || '') : '',
          sets: shouldPrefillRoundAndSets ? (fixtureData?.setCount ? String(fixtureData.setCount) : '') : '',
          knockoutStage: knockoutStageField,
          knockoutIndex: knockoutData?.index ? String(knockoutData.index) : '',
          knockoutMatchNum: knockoutData?.matchNum || '',
          streamKey: (fixtureData as any)?.liveStreamInfo?.streamKey || '',
          ingestionUrl: (fixtureData as any)?.liveStreamInfo?.ingestionUrl || (fixtureData as any)?.liveStreamInfo?.streamUrl || '',
          recordedUrl: (fixtureData as any)?.streamRecordingInfo?.streamS3Url || (fixtureData as any)?.streamRecordingInfo?.recordedUrl || '',
          streamingVideoUrl: (fixtureData as any)?.streamingVideoUrl || ''
        };
        setFormData(initialFormData);
        const originalData = JSON.parse(JSON.stringify(initialFormData));
        setOriginalFormData(originalData);
        setIsFormModified(false);
        // Initialize form validation state
        requestAnimationFrame(() => {
          const validation = validateForm(initialFormData);
          setIsFormValid(validation.valid);
        });
        
        // Load teams and groups if age category is selected - use local ageCategoryOptionsList
        // Set flag to prevent duplicate calls from other useEffects
        initialLoadRef.current = true;
        if (selectedAgeCategory && ageCategoryOptionsList.length > 0) {
          // NOTE: We do NOT load groups during initial load - groups are only needed when editing
          // This prevents unnecessary get-all-standings API calls when just viewing the match
          // Groups will be loaded when the user starts editing (via useEffect on formData.ageCategory?.value)
          
          // Load teams or players based on age category format (not sport type)
          // Check age category format if available
          const ageCategoryObjForLoad = ageCategoryOptionsList.find((ac: { value: string; label: string; format?: string }) => ac.value === selectedAgeCategory.value);
          const ageCategoryFormatForLoad = ageCategoryObjForLoad?.format?.toLowerCase();
          const ageCategoryLabelForLoad = ageCategoryObjForLoad?.label?.toLowerCase() || '';
          
          // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
          let isPlayerFormatForLoad = ageCategoryFormatForLoad === 'player';
          
          // Additional check: If age category name contains 'single' or 'singles', it's likely player format
          const isSingleAgeCategoryForLoad = ageCategoryLabelForLoad.includes('single') || ageCategoryLabelForLoad.includes('singles');
          
          // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
          // This handles cases where format field is not present in API response for default seasons
          if (!ageCategoryFormatForLoad) {
            const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
            const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
            // For Badminton/Table Tennis, if format is missing:
            // 1. If age category name contains 'single'/'singles', assume player format
            // 2. Otherwise, assume player format (Badminton/Table Tennis are typically player-based)
            if (isBadmintonOrTT) {
              if (isSingleAgeCategoryForLoad) {
                isPlayerFormatForLoad = true;
                console.log('Initial load - Format missing but age category name contains "single" - defaulting to player format');
              } else {
                isPlayerFormatForLoad = true;
                console.log('Initial load - Format missing but sport is Badminton/Table Tennis - defaulting to player format');
              }
            }
          } else if (ageCategoryFormatForLoad && isSingleAgeCategoryForLoad) {
            // If format exists but age category name suggests single, double-check for Badminton/Table Tennis
            const normalizedFixtureSport = fixtureSport.replace(/\s+/g, '').toLowerCase();
            const isBadmintonOrTT = normalizedFixtureSport === 'badminton' || normalizedFixtureSport === 'tabletennis' || normalizedFixtureSport === 'tt';
            if (isBadmintonOrTT && !isPlayerFormatForLoad) {
              // Override format if it's Badminton/Table Tennis with single age category
              isPlayerFormatForLoad = true;
              console.log('Initial load - Age category name contains "single" for Badminton/Table Tennis - overriding format to player');
            }
          }
          
          console.log('Initial load - Age category format check:', {
            fixtureSport: fixtureSport,
            ageCategoryFormat: ageCategoryFormatForLoad,
            ageCategoryFormatRaw: ageCategoryObjForLoad?.format,
            isPlayerFormat: isPlayerFormatForLoad,
            ageCategoryObj: ageCategoryObjForLoad,
            selectedAgeCategory: selectedAgeCategory,
            allAgeCategoryOptions: ageCategoryOptionsList.map((ac: { value: string; label: string; format?: string }) => ({ value: ac.value, label: ac.label, format: ac.format }))
          });
          
          if (isPlayerFormatForLoad) {
            // Load players for player format age categories
            // Clear teams first to ensure clean state
            setFilteredTeams([]);
            console.log('Initial load - Loading PLAYERS for player format age category');
            loadPlayersByAgeCategory(selectedAgeCategory.value, selectedAgeCategory.label);
          } else {
            // Load teams for team format age categories (default)
            // Clear players first to ensure clean state
            setFilteredPlayers([]);
            console.log('Initial load - Loading TEAMS for team format age category');
          // Load teams - pass the age category name directly to avoid the lookup issue
          // Skip standings call during initial load to prevent unnecessary API calls
          // We don't need to filter teams by group during initial load since teams are already selected
          loadTeamsByAgeCategory(selectedAgeCategory.value, fixtureData?.group, selectedAgeCategory.label, true);
          }
        }
        
        validateForm(initialFormData);
      } catch (e: any) {
        setError(extractErrorMessage(e, 'Failed to load match details'));
      } finally {
        setLoading(false);
        // Reset flag after a short delay to allow useEffects to run for user changes
        setTimeout(() => {
          initialLoadRef.current = false;
        }, 100);
      }
    };
    load();
  }, [tournamentId, matchId]);

  // Track loading state to prevent duplicate API calls
  const groupsLoadingRef = useRef<{ [key: string]: boolean }>({});
  const initialLoadRef = useRef<boolean>(false);
  
  // Load groups based on age category and standings type
  const loadGroupsByAgeCategory = useCallback(async (ageCategoryId: string, standingsType: string): Promise<{ value: string; label: string }[]> => {
    if (!ageCategoryId || standingsType !== 'group') {
      setAvailableGroups([]);
      return [];
    }
    
    // Prevent duplicate calls for the same age category
    if (groupsLoadingRef.current[ageCategoryId]) {
      return [];
    }
    
    groupsLoadingRef.current[ageCategoryId] = true;
    setGroupsLoading(true);
    try {
      // Get sport value with fallbacks to ensure it's always available
      let sport: string;
      try {
        sport = getSportValue();
      } catch (sportError) {
        // Fallback 1: Try to get sport from formData (set from fixture data)
        sport = formData.sports?.value || formData.sports?.label;
        // Fallback 2: Try tournament object
        if (!sport && tournament?.sports) {
          sport = tournament.sports;
        }
        // Fallback 3: Try fixture object
        if (!sport && fixture?.sports) {
          sport = (fixture as any).sports;
        }
        if (!sport) {
          throw sportError;
        }
      }
      const response = await getTeamStandings(ageCategoryId, sport, tournamentSeasonId);
      if (Array.isArray(response) && response.length > 0) {
        // Check if it's group standings (has groupName property)
        if (response[0].hasOwnProperty('groupName')) {
          const groupOptions = response.map((group: any) => ({
            value: group.groupName,
            label: `Group ${group.groupName}`
          }));
          setAvailableGroups(groupOptions);
          return groupOptions;
        } else {
          setAvailableGroups([]);
          return [];
        }
      } else {
        setAvailableGroups([]);
        return [];
      }
    } catch (error) {
      console.error('Error loading groups by age category:', error);
      setAvailableGroups([]);
      return [];
    } finally {
      setGroupsLoading(false);
      groupsLoadingRef.current[ageCategoryId] = false;
    }
  }, [tournament?.sports, getSportValue, tournamentSeasonId]);

  // Track loading state to prevent duplicate API calls for teams
  const teamsLoadingRef = useRef<{ [key: string]: boolean }>({});
  
  // Load teams based on selected age category and group
  // This is only used for editing fixture details, not for score updates
  const loadTeamsByAgeCategory = useCallback(async (ageCategoryId: string, groupName?: string, ageCategoryName?: string, skipStandingsCall?: boolean) => {
    if (!ageCategoryId || !tournamentId) {
      setFilteredTeams([]);
      return;
    }
    
    // Create a unique key for this request
    const requestKey = `${ageCategoryId}_${groupName || 'all'}`;
    
    // Prevent duplicate calls for the same request
    if (teamsLoadingRef.current[requestKey]) {
      return;
    }
    
    // Find the age category object to get the label (name) - use provided name or lookup
    let finalAgeCategoryName = ageCategoryName;
    if (!finalAgeCategoryName) {
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === ageCategoryId);
      if (!ageCategoryObj) {
        console.error('Age category object not found for ID:', ageCategoryId, {
          availableOptions: ageCategoryOptions.map(ac => ({ value: ac.value, label: ac.label })),
          lookingFor: ageCategoryId
        });
        setFilteredTeams([]);
        return;
      }
      finalAgeCategoryName = ageCategoryObj.label;
    }
    
    teamsLoadingRef.current[requestKey] = true;
    setTeamsLoading(true);
    try {
      // Get sport value - prioritize formData.sports (what user is editing) over getSportValue()
      // This ensures we use the correct sport for the fixture being edited
      let sport: string;
      // Priority 1: Use formData.sports (most accurate for the fixture being edited)
      sport = formData.sports?.value || formData.sports?.label;
      
      // Priority 2: Try getSportValue() if formData doesn't have sport
      if (!sport) {
        try {
          sport = getSportValue();
        } catch (sportError) {
          // Continue to next fallback
        }
      }
      
      // Priority 3: Try tournament object
      if (!sport && tournament?.sports) {
        sport = tournament.sports;
      }
      
      // Priority 4: Try fixture object
      if (!sport && fixture?.sports) {
        sport = (fixture as any).sports;
      }
      
      if (!sport) {
        throw new Error('Sport parameter is required to load teams.');
      }
      
      // Safety check: Only load teams for non-Badminton/Table Tennis sports
      // Normalize sport name by removing spaces and converting to lowercase
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      
      console.log('loadTeamsByAgeCategory - Sport check:', {
        sport: sport,
        normalizedSport: normalizedSport,
        isBadmintonOrTT: isBadmintonOrTT,
        formDataSports: formData.sports
      });
      
      if (isBadmintonOrTT) {
        console.warn('loadTeamsByAgeCategory called for Badminton/Table Tennis sport:', sport, '- Skipping team load and clearing teams list');
        setFilteredTeams([]);
        teamsLoadingRef.current[requestKey] = false;
        setTeamsLoading(false);
        return;
      }
      
      const response = await listTeams({
        tournamentId: tournamentId,
        ageCategory: finalAgeCategoryName, // Use the label (name) instead of ID
        pageSize: 1000, // Get all teams for this age category
        tournamentSeason: tournamentSeasonId || undefined, // Pass tournamentSeasonId when available (from location.state)
        sport: sport // Pass sport parameter with fallbacks
      });
      
      const teamsData = response.data?.paginatedResults || response.paginatedResults || [];
      
      let filteredTeamsData = teamsData;
      
      // If group is selected, filter teams by group using standings API (same logic as FixturesTable)
      // Only call this when editing fixture details, not for score updates
      // skipStandingsCall flag prevents unnecessary API calls when not editing
      if (groupName && !skipStandingsCall) {
        try {
          // Only call getTeamStandings if we don't already have the standings data
          const standingsKey = `standings_${ageCategoryId}`;
          if (!groupsLoadingRef.current[standingsKey] && !groupsLoadingRef.current[ageCategoryId]) {
            groupsLoadingRef.current[standingsKey] = true;
            const standingsResponse = await getTeamStandings(ageCategoryId, sport, tournamentSeasonId);
            if (Array.isArray(standingsResponse) && standingsResponse.length > 0) {
              // Check if it's group standings (has groupName property)
              if (standingsResponse[0].hasOwnProperty('groupName')) {
                const groupStanding = standingsResponse.find((group: any) => group.groupName === groupName) as any;
                if (groupStanding && groupStanding.standings) {
                  const groupTeamIds = groupStanding.standings.map((standing: any) => standing.team._id);
                  // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
                  // Check both team.team._id (actual team ID) and team._id (teamSeason ID) for compatibility
                  filteredTeamsData = teamsData.filter((team: any) => {
                    const teamData = team.team || team; // Use nested team object if available
                    return groupTeamIds.includes(teamData._id) || groupTeamIds.includes(team._id);
                  });
                }
              }
            }
            groupsLoadingRef.current[standingsKey] = false;
          }
        } catch (error) {
          console.error('Error filtering teams by group:', error);
          const standingsKey = `standings_${ageCategoryId}`;
          groupsLoadingRef.current[standingsKey] = false;
        }
      }
      
      // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
      // Handle nested team object structure (team.team) or flat structure (team)
      const teamOptions = filteredTeamsData
        .map((team: any) => {
          // Use nested team object if available, fallback to team itself
          const teamData = team.team || team;
          
          // Get team ID - prefer team._id (actual team ID) over _id (teamSeason ID)
          const teamId = teamData._id || team._id || team.id || '';
          
          // Get team name - check multiple possible locations
          const teamName = teamData.name || team.name || teamData.shortName || team.shortName || 
                          teamData.teamName || team.teamName || teamData.short_name || team.short_name ||
                          teamData.abbreviation || team.abbreviation || '';
          
          return {
            value: teamId,
            label: teamName || `Team ${teamId.substring(0, 8)}` // Fallback to partial ID if no name
          };
        })
        .filter((option: { value: string; label: string }) => option.value && option.label); // Filter out invalid entries
      
      setFilteredTeams(teamOptions);
    } catch (error) {
      console.error('Error loading teams by age category:', error);
      setFilteredTeams([]);
    } finally {
      setTeamsLoading(false);
      teamsLoadingRef.current[requestKey] = false;
    }
  }, [tournamentId, ageCategoryOptions, tournament?.sports, getSportValue]);

  // Load players based on selected age category (for Badminton/Table Tennis)
  const loadPlayersByAgeCategory = useCallback(async (ageCategoryId: string, ageCategoryName?: string) => {
    if (!ageCategoryId || !tournamentId) {
      setFilteredPlayers([]);
      return;
    }
    
    // Create a unique key for this request
    const requestKey = `players_${ageCategoryId}`;
    
    // Prevent duplicate calls for the same request
    if (teamsLoadingRef.current[requestKey]) {
      return;
    }
    
    // Find the age category object to get the label (name) - use provided name or lookup
    let finalAgeCategoryName = ageCategoryName;
    if (!finalAgeCategoryName) {
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === ageCategoryId);
      if (!ageCategoryObj) {
        console.error('Age category object not found for ID:', ageCategoryId);
        setFilteredPlayers([]);
        return;
      }
      finalAgeCategoryName = ageCategoryObj.label;
    }
    
    // Get sport value - prioritize formData.sports (what user is editing) over getSportValue()
    // This ensures we use the correct sport for the fixture being edited
    let sport: string;
    // Priority 1: Use formData.sports (most accurate for the fixture being edited)
    sport = formData.sports?.value || formData.sports?.label;
    
    // Priority 2: Try getSportValue() if formData doesn't have sport
    if (!sport) {
      try {
        sport = getSportValue();
      } catch (sportError) {
        // Continue to next fallback
      }
    }
    
    // Priority 3: Try tournament object
    if (!sport && tournament?.sports) {
      sport = tournament.sports;
    }
    
    // Priority 4: Try fixture object
    if (!sport && fixture?.sports) {
      sport = (fixture as any).sports;
    }
    
    if (!sport) {
      throw new Error('Sport parameter is required to load players.');
    }
    
    // Load player participants for Badminton, Table Tennis, and Padel.
    // Normalize sport name by removing spaces and converting to lowercase
    const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
    const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
    const isPadel = normalizedSport === 'padel';
    const isPickleball = normalizedSport === 'pickleball';
    
    console.log('loadPlayersByAgeCategory - Sport check:', {
      sport: sport,
      normalizedSport: normalizedSport,
      isBadmintonOrTT: isBadmintonOrTT,
      isPadel: isPadel,
      isPickleball: isPickleball,
      formDataSports: formData.sports
    });
    
    if (!isBadmintonOrTT && !isPadel && !isPickleball) {
      console.warn('loadPlayersByAgeCategory called for a non-player sport:', sport, '- Skipping player load and clearing players list');
      setFilteredPlayers([]);
      teamsLoadingRef.current[requestKey] = false;
      setPlayersLoading(false);
      return;
    }
    
    teamsLoadingRef.current[requestKey] = true;
    setPlayersLoading(true);
    try {
      // Log for debugging
      console.log('Loading players for sport:', sport, 'ageCategory:', finalAgeCategoryName);
      
      const response = await listPlayers({
        tournament: tournamentId,
        ageCategory: finalAgeCategoryName,
        pageSize: 1000, // Get all players for this age category
        tournamentSeason: tournamentSeasonId || undefined,
        sport: sport // Pass original sport value (API handles normalization)
      });
      
      const playersData = response.data?.paginatedResults || response.paginatedResults || [];
      console.log('Players loaded:', playersData.length, 'players');
      
      // Map players to options format
      const playerOptions = playersData
        .map((player: any) => {
          const playerInfo = player.playerInfo || player;
          const playerName = playerInfo.name || player.name || 'Unknown Player';
          const playerId = playerInfo._id || player._id || player.id || '';
          
          return {
            value: playerId,
            label: playerName
          };
        })
        .filter((option: { value: string; label: string }) => option.value && option.label); // Filter out invalid entries
      
      setFilteredPlayers(playerOptions);
    } catch (error) {
      console.error('Error loading players by age category:', error);
      setFilteredPlayers([]);
    } finally {
      setPlayersLoading(false);
      teamsLoadingRef.current[requestKey] = false;
    }
  }, [tournamentId, ageCategoryOptions, tournament?.sports, formData.sports, fixture?.sports, getSportValue, tournamentSeasonId]);

  // Load teams or players when age category or group changes (only if not initial load)
  // This is only for editing fixture details, not for score updates
  useEffect(() => {
    // Skip during initial load to prevent duplicate calls
    if (initialLoadRef.current) {
      return;
    }
    
    // Only load teams/players when editing fixture details (when formData is being modified by user)
    // This prevents unnecessary API calls when just viewing the match
    if (formData.ageCategory?.value) {
      // Check age category format (player vs team)
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory.value);
      
      // If age category not found in options, log warning and default to teams
      if (!ageCategoryObj) {
        console.warn('Age category not found in options:', {
          ageCategoryValue: formData.ageCategory.value,
          availableOptions: ageCategoryOptions.map(ac => ({ value: ac.value, label: ac.label, format: ac.format }))
        });
        // Default to teams if age category not found
        setFilteredPlayers([]);
      const groupValue = formData.group?.value || (typeof formData.group === 'string' ? formData.group : null);
        loadTeamsByAgeCategory(formData.ageCategory.value, groupValue, undefined, false);
        return;
      }
      
      const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
      const ageCategoryLabel = ageCategoryObj?.label?.toLowerCase() || '';
      
      // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
      let isPlayerFormat = ageCategoryFormat === 'player';
      
      // Additional check: If age category name contains 'single' or 'singles', it's likely player format
      const isSingleAgeCategory = ageCategoryLabel.includes('single') || ageCategoryLabel.includes('singles');
      
      // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
      // This handles cases where format field is not present in API response for default seasons
      if (!ageCategoryFormat && formData.sports) {
        const sport = formData.sports?.value || formData.sports?.label || '';
        const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
        // For Badminton/Table Tennis, if format is missing:
        // 1. If age category name contains 'single'/'singles', assume player format
        // 2. Otherwise, assume player format (Badminton/Table Tennis are typically player-based)
        if (isBadmintonOrTT) {
          if (isSingleAgeCategory) {
            isPlayerFormat = true;
            console.log('Format missing but age category name contains "single" - defaulting to player format');
          } else {
            isPlayerFormat = true;
            console.log('Format missing but sport is Badminton/Table Tennis - defaulting to player format');
          }
        }
      } else if (ageCategoryFormat && isSingleAgeCategory && formData.sports) {
        // If format exists but age category name suggests single, double-check for Badminton/Table Tennis
        const sport = formData.sports?.value || formData.sports?.label || '';
        const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
        if (isBadmintonOrTT && !isPlayerFormat) {
          // Override format if it's Badminton/Table Tennis with single age category
          isPlayerFormat = true;
          console.log('Age category name contains "single" for Badminton/Table Tennis - overriding format to player');
        }
      }
      
      // Debug logging
      console.log('useEffect - Age category format check:', {
        ageCategoryFormat: ageCategoryFormat,
        ageCategoryFormatRaw: ageCategoryObj?.format,
        isPlayerFormat: isPlayerFormat,
        sport: formData.sports?.value || formData.sports?.label,
        ageCategoryObj: ageCategoryObj,
        ageCategory: formData.ageCategory?.value,
        allAgeCategoryOptions: ageCategoryOptions.map((ac: { value: string; label: string; format?: string }) => ({ value: ac.value, label: ac.label, format: ac.format }))
      });
      
      if (isPlayerFormat) {
        // Load players for player format age categories
        // First, clear teams to ensure clean state
        setFilteredTeams([]);
        console.log('Loading PLAYERS for player format age category');
        loadPlayersByAgeCategory(formData.ageCategory.value, ageCategoryObj?.label);
      } else {
        // Load teams for team format age categories (default)
        // First, clear players to ensure clean state
        setFilteredPlayers([]);
        // If stage is knockout, don't filter by group (pass undefined)
        const isKnockout = formData.stage?.value === 'knockout';
        const groupValue = isKnockout ? undefined : (formData.group?.value || (typeof formData.group === 'string' ? formData.group : null));
        console.log('Loading TEAMS for team format age category (format:', ageCategoryFormat || 'undefined/null', ', stage:', formData.stage?.value, ', group:', groupValue, ')');
      // Skip standings call if we're not actively editing (to prevent unnecessary getTeamStandings calls)
      loadTeamsByAgeCategory(formData.ageCategory.value, groupValue, undefined, false);
      }
    } else {
      // Clear both if age category not available
      setFilteredTeams([]);
      setFilteredPlayers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.ageCategory?.value, formData.group?.value, formData.stage?.value, formData.sports]);

  // Load groups when age category changes (only if not initial load)
  useEffect(() => {
    // Skip during initial load to prevent duplicate calls
    if (initialLoadRef.current) {
      return;
    }
    
    if (formData.ageCategory?.value) {
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === formData.ageCategory.value);
      const isKnockout = formData.stage?.value === 'knockout';
      // Load groups only if standings type is 'group' AND stage is not 'knockout'
      if (ageCategoryObj?.standingsType === 'group' && !isKnockout) {
        loadGroupsByAgeCategory(formData.ageCategory.value, ageCategoryObj.standingsType);
      } else {
        setAvailableGroups([]);
      }
    } else {
      setAvailableGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.ageCategory?.value, formData.stage?.value]);

  // Track team IDs to prevent duplicate API calls
  // NOTE: Players are now loaded from fetchLiveScores API, not from listPlayers
  // This useEffect is removed to prevent duplicate API calls
  // Players will be populated when loadLiveScores is called

  // Validation function matching FixturesTable
  const validateForm = useCallback((data?: any) => {
    const form = data || formData;
    const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string; format?: string }) => ac.value === form.ageCategory?.value);
    const isGroupStandings = ageCategoryObj?.standingsType === 'group';
    // Check age category format instead of sport type
    const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
    const ageCategoryLabel = ageCategoryObj?.label?.toLowerCase() || '';
    
    // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
    let isPlayerFormat = ageCategoryFormat === 'player';
    const isPadel = (form.sports?.value || form.sports?.label || '').replace(/\s+/g, '').toLowerCase() === 'padel';
    const isPickleball = (form.sports?.value || form.sports?.label || '').replace(/\s+/g, '').toLowerCase() === 'pickleball';
    const requiresTwoPlayersPerSide = isPadel || (isPickleball && isPickleballDoubles(form.eventCategory?.value));
    if (isPadel || isPickleball) {
      isPlayerFormat = true;
    }
    
    // Additional check: If age category name contains 'single' or 'singles', it's likely player format
    const isSingleAgeCategory = ageCategoryLabel.includes('single') || ageCategoryLabel.includes('singles');
    
    // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
    if (!ageCategoryFormat && form.sports) {
      const sport = form.sports?.value || form.sports?.label || '';
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      if (isBadmintonOrTT) {
        if (isSingleAgeCategory) {
          isPlayerFormat = true;
        } else {
          isPlayerFormat = true; // Badminton/Table Tennis default to player format
        }
      }
    } else if (ageCategoryFormat && isSingleAgeCategory && form.sports) {
      // If format exists but age category name suggests single, double-check for Badminton/Table Tennis
      const sport = form.sports?.value || form.sports?.label || '';
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      if (isBadmintonOrTT && !isPlayerFormat) {
        // Override format if it's Badminton/Table Tennis with single age category
        isPlayerFormat = true;
      }
    }
    
    const isKnockout = form.stage?.value === 'knockout';
    
    // Group is required only for league stage with group standings, not for knockout or player format
    const groupRequired = !isKnockout && isGroupStandings && !isPlayerFormat;
    
    const errors: any = {
      matchNum: !form.matchNum ? 'Match Number is required.' : '',
      time: !form.time ? 'Start Time is required.' : '',
      sports: !form.sports ? 'Sports is required.' : '',
      stage: '',
      ageCategory: !form.ageCategory ? 'Age Category is required.' : '',
      eventCategory: isPickleball && !form.eventCategory ? 'Event Category is required.' : '',
      // Group is only required for league stage with group standings, not for knockout or player format
      group: groupRequired && !form.group ? 'Group is required for group standings.' : '',
      teamA: isPlayerFormat ? '' : (!form.teamA ? 'Team A is required.' : ''),
      teamB: isPlayerFormat ? '' : (!form.teamB ? 'Team B is required.' : ''),
      playerA: '',
      playerB: '',
      playerA2: requiresTwoPlayersPerSide ? (!form.playerA2 ? 'Second player for Team 1 is required.' : '') : '',
      playerB2: requiresTwoPlayersPerSide ? (!form.playerB2 ? 'Second player for Team 2 is required.' : '') : '',
      date: !form.date ? 'Date is required.' : '',
      venue: !form.venue ? 'Venue is required.' : '',
      venueLong: !form.venueLong ? 'Venue Full Address is required.' : '',
    };
    
    // Sets and rounds apply to Badminton/Table Tennis player formats, not Padel.
    if (isPlayerFormat && !isPadel && !(isPickleball && !isPickleballDoubles(form.eventCategory?.value))) {
      errors.sets = !form.sets ? 'Sets is required.' : 
                   (!/^\d+$/.test(form.sets) || parseInt(form.sets) % 2 === 0) ? 'Sets must be an odd number (1, 3, 5, 7, etc.).' : '';
      errors.round = isKnockout && !form.round ? 'Round is required for Knockout stage.' : '';
    }
    
    const valid = Object.values(errors).every(x => !x);
    
    // Debug logging for validation
    console.log('Form validation:', {
      isPlayerFormat: isPlayerFormat,
      ageCategoryFormat: ageCategoryFormat,
      ageCategoryLabel: ageCategoryLabel,
      errors: errors,
      valid: valid,
      hasPlayerA: !!form.playerA,
      hasPlayerB: !!form.playerB,
      hasTeamA: !!form.teamA,
      hasTeamB: !!form.teamB
    });
    
    return { errors, valid };
  }, [ageCategoryOptions, formData]);

  const handleEdit = () => {
    // Prevent editing only once the match is completed/ended; live/paused matches remain editable
    if (isMatchCompletedOrEnded) {
      setFormError('Cannot edit fixture once match has completed');
      return;
    }
    
    // Allow editing - show inline form
    setShowEditModal(true);
    setFormError(null);
    setFormTouched({});
    
    // Load teams or players when entering edit mode if age category is selected
    // Use the same format detection logic as useEffect
    if (formData.ageCategory?.value) {
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory.value);
      const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
      const ageCategoryLabel = ageCategoryObj?.label?.toLowerCase() || '';
      
      // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
      let isPlayerFormat = ageCategoryFormat === 'player';
      const isSingleAgeCategory = ageCategoryLabel.includes('single') || ageCategoryLabel.includes('singles');
      
      // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
      if (!ageCategoryFormat && formData.sports) {
        const sport = formData.sports?.value || formData.sports?.label || '';
        const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
        if (isBadmintonOrTT) {
          isPlayerFormat = true;
        }
      } else if (ageCategoryFormat && isSingleAgeCategory && formData.sports) {
        const sport = formData.sports?.value || formData.sports?.label || '';
        const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
        if (isBadmintonOrTT && !isPlayerFormat) {
          isPlayerFormat = true;
        }
      }
      
      // Load groups only if standings type is 'group' AND stage is not 'knockout'
      const isKnockout = formData.stage?.value === 'knockout';
      if (ageCategoryObj?.standingsType === 'group' && !isKnockout) {
        loadGroupsByAgeCategory(formData.ageCategory.value, ageCategoryObj.standingsType);
      } else {
        setAvailableGroups([]);
      }
      
      if (isPlayerFormat) {
        // Load players for player format age categories
        console.log('handleEdit - Loading PLAYERS for player format age category');
        loadPlayersByAgeCategory(formData.ageCategory.value, ageCategoryObj?.label);
      } else {
        // Load teams for team format age categories
        const groupValue = formData.group?.value || (typeof formData.group === 'string' ? formData.group : null);
        console.log('handleEdit - Loading TEAMS for team format age category');
        loadTeamsByAgeCategory(formData.ageCategory.value, groupValue, undefined, false);
      }
    }
    
    // Initialize form validation state when entering edit mode
    requestAnimationFrame(() => {
      const validation = validateForm();
      setIsFormValid(validation.valid);
    });
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setFormError(null);
    setFormTouched({});
    // Reset form to original data if user cancels
    if (originalFormData) {
      setFormData(originalFormData);
      setIsFormModified(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    setFormTouched({ ...formTouched, [name]: true });
    // Validate with updated data and check form modification
    requestAnimationFrame(() => {
      const validation = validateForm(updatedFormData);
      setIsFormValid(validation.valid);
      // Update form modified state using the updated data
      checkFormModified(updatedFormData);
    });
  };

  // Handle select changes
  const handleSelectChange = (name: string, option: any) => {
    let updatedFormData;
    if (name === 'ageCategory') {
      // Clear team/player selections and group when age category changes
      updatedFormData = { 
        ...formData, 
        [name]: option, 
        group: null,
        teamA: null, 
        teamB: null,
        playerA: null,
        playerB: null,
        playerA2: null,
        playerB2: null,
      };
      setFormTouched({ ...formTouched, [name]: true, group: true });
    } else if (name === 'eventCategory') {
      updatedFormData = {
        ...formData,
        eventCategory: option,
        playerA: null,
        playerB: null,
        playerA2: null,
        playerB2: null,
        sets: '',
      };
      setFormTouched({ ...formTouched, eventCategory: true, playerA: true, playerB: true, playerA2: true, playerB2: true });
    } else if (name === 'stage') {
      // When stage changes to knockout, clear group and reload teams without group filter
      const isKnockout = option?.value === 'knockout';
      updatedFormData = { 
        ...formData, 
        [name]: option,
        group: isKnockout ? null : formData.group, // Clear group if switching to knockout
        teamA: isKnockout ? null : formData.teamA, // Clear teams when switching to knockout
        teamB: isKnockout ? null : formData.teamB
      };
      setFormTouched({ ...formTouched, [name]: true, group: true });
    } else {
      updatedFormData = { ...formData, [name]: option };
      setFormTouched({ ...formTouched, [name]: true });
    }
    setFormData(updatedFormData);
    // Validate with updated data and check form modification
    requestAnimationFrame(() => {
      const validation = validateForm(updatedFormData);
      setIsFormValid(validation.valid);
      // Update form modified state using the updated data
      checkFormModified(updatedFormData);
    });
  };

  // Validate form whenever formData changes (especially when players/teams are loaded)
  useEffect(() => {
    if (showEditModal && formData.ageCategory?.value) {
      const validation = validateForm();
      setIsFormValid(validation.valid);
    }
  }, [formData.playerA, formData.playerB, formData.teamA, formData.teamB, formData.ageCategory, formData.sports, showEditModal, validateForm]);

  // Handle group change
  const handleGroupChange = (option: any) => {
    // Check if it's player format - if so, don't reset players
    const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory?.value);
    const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
    const ageCategoryLabel = ageCategoryObj?.label?.toLowerCase() || '';
    let isPlayerFormat = ageCategoryFormat === 'player';
    const isSingleAgeCategory = ageCategoryLabel.includes('single') || ageCategoryLabel.includes('singles');
    
    if (!ageCategoryFormat && formData.sports) {
      const sport = formData.sports?.value || formData.sports?.label || '';
      const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
      if (isBadmintonOrTT) {
        isPlayerFormat = true;
      }
    }
    
    // Only reset teams/players based on format
    const updatedFormData = isPlayerFormat 
      ? { ...formData, group: option, playerA: null, playerB: null } // Reset players for player format
      : { ...formData, group: option, teamA: null, teamB: null }; // Reset teams for team format
    setFormData(updatedFormData);
    setFormTouched({ ...formTouched, group: true, teamA: true, teamB: true });
    // Validate with updated data and check form modification
    requestAnimationFrame(() => {
      const validation = validateForm(updatedFormData);
      setIsFormValid(validation.valid);
      // Update form modified state using the updated data
      checkFormModified(updatedFormData);
    });
  };

  // Prevent scroll from changing numeric input values
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  // Helper functions for custom time picker
  const convertTo12Hour = (hour: string, minute: string, amPm: string) => {
    const hour12 = hour.padStart(2, '0');
    const minute12 = minute.padStart(2, '0');
    return `${hour12}:${minute12} ${amPm}`;
  };

  const parseTimeToAmPm = (timeString: string) => {
    if (!timeString) return { hour: '', minute: '', amPm: '' };
    
    // Handle both 12-hour format (08:00 PM) and 24-hour format (20:00)
    if (timeString.includes('AM') || timeString.includes('PM')) {
      // 12-hour format: "08:00 PM"
      const match = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        return {
          hour: match[1].padStart(2, '0'),
          minute: match[2],
          amPm: match[3].toUpperCase()
        };
      }
    } else {
      // 24-hour format: "20:00"
      const [hours, mins] = timeString.split(':');
      const hour24 = parseInt(hours);
      const amPm = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      return {
        hour: hour12.toString().padStart(2, '0'),
        minute: mins || '00',
        amPm: amPm
      };
    }
    
    return { hour: '', minute: '', amPm: '' };
  };

  const handleCustomTimeSelection = (type: 'hour' | 'minute' | 'amPm', value: string) => {
    if (type === 'hour') {
      setSelectedHour(value);
    } else if (type === 'minute') {
      setSelectedMinute(value);
    } else if (type === 'amPm') {
      setSelectedAmPm(value);
      
      // Auto-close and set time when AM/PM is selected
      if (selectedHour && selectedMinute) {
        const time12 = convertTo12Hour(selectedHour, selectedMinute, value);
        const updatedFormData = { ...formData, time: time12 };
        setFormData(updatedFormData);
        setFormTouched({ ...formTouched, time: true });
        setShowTimePicker(false);
        // Reset selections
        setSelectedHour('');
        setSelectedMinute('');
        setSelectedAmPm('');
        // Validate
        requestAnimationFrame(() => {
          const validation = validateForm(updatedFormData);
          setIsFormValid(validation.valid);
        });
      }
    }
  };

  const openTimePicker = () => {
    if (timePickerButtonRef.current) {
      const rect = timePickerButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Fixed size for better display of all options
      const dropdownWidth = 600;
      const dropdownHeight = 500;
      
      // Center the dropdown in the viewport
      let top = (viewportHeight - dropdownHeight) / 2;
      let left = (viewportWidth - dropdownWidth) / 2;
      
      // Ensure it doesn't go off screen
      if (left < 20) {
        left = 20;
      }
      if (left + dropdownWidth > viewportWidth - 20) {
        left = viewportWidth - dropdownWidth - 20;
      }
      if (top < 20) {
        top = 20;
      }
      if (top + dropdownHeight > viewportHeight - 20) {
        top = viewportHeight - dropdownHeight - 20;
      }
      
      setPickerPosition({ top, left });
    }
    
    if (formData.time) {
      const parsed = parseTimeToAmPm(formData.time);
      setSelectedHour(parsed.hour);
      setSelectedMinute(parsed.minute);
      setSelectedAmPm(parsed.amPm);
    } else {
      setSelectedHour('');
      setSelectedMinute('');
      setSelectedAmPm('');
    }
    setShowTimePicker(true);
    
    // Prevent body scrolling
    document.body.classList.add('time-picker-open');
  };

  const closeTimePicker = () => {
    setShowTimePicker(false);
    setSelectedHour('');
    setSelectedMinute('');
    setSelectedAmPm('');
    
    // Re-enable body scrolling
    document.body.classList.remove('time-picker-open');
  };

  // Close time picker when clicking outside and handle scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showTimePicker && !target.closest('.custom-time-picker-dropdown') && !target.closest('.time-picker-container')) {
        closeTimePicker();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showTimePicker) {
        closeTimePicker();
      }
    };

    const handleScroll = () => {
      if (showTimePicker && timePickerButtonRef.current) {
        // Recalculate position on scroll
        const rect = timePickerButtonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = Math.min(600, viewportWidth - 40);
        const dropdownHeight = Math.min(500, viewportHeight - 40);
        
        let top = (viewportHeight - dropdownHeight) / 2;
        let left = (viewportWidth - dropdownWidth) / 2;
        
        if (left < 20) left = 20;
        if (left + dropdownWidth > viewportWidth - 20) left = viewportWidth - dropdownWidth - 20;
        if (top < 20) top = 20;
        if (top + dropdownHeight > viewportHeight - 20) top = viewportHeight - dropdownHeight - 20;
        
        setPickerPosition({ top, left });
      }
    };

    if (showTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [showTimePicker]);

  // Get team options (exclude the other team)
  const getTeamAOptions = () => {
    return filteredTeams.filter(option => 
      !formData.teamB || option.value !== formData.teamB.value
    );
  };

  const getTeamBOptions = () => {
    return filteredTeams.filter(option => 
      !formData.teamA || option.value !== formData.teamA.value
    );
  };

  const getPlayerAOptions = () => {
    return getPlayerOptions('playerA');
  };

  const getPlayerBOptions = () => {
    return getPlayerOptions('playerB');
  };

  const getPlayerOptions = (field: 'playerA' | 'playerA2' | 'playerB' | 'playerB2') => {
    const selectedPlayerIds = ['playerA', 'playerA2', 'playerB', 'playerB2']
      .filter((key) => key !== field)
      .map((key) => formData[key]?.value)
      .filter(Boolean);
    return filteredPlayers.filter((option) => !selectedPlayerIds.includes(option.value));
  };

  // Helper to convert 12-hour time to 24-hour format
  const formatTimeTo24Hour = (time12: string): string => {
    if (!time12) return '';
    
    // Handle both 12-hour format (08:00 PM) and already 24-hour format (20:00)
    if (time12.includes('AM') || time12.includes('PM')) {
      // 12-hour format: "08:00 PM" or "8:00 AM"
      const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let hour = parseInt(match[1]);
        const minute = match[2];
        const amPm = match[3].toUpperCase();
        
        if (amPm === 'PM' && hour !== 12) {
          hour += 12;
        } else if (amPm === 'AM' && hour === 12) {
          hour = 0;
        }
        
        return `${hour.toString().padStart(2, '0')}:${minute}`;
      }
    } else {
      // Already in 24-hour format: "20:00"
      // Just validate and return as-is
      const match = time12.match(/^(\d{1,2}):(\d{2})$/);
      if (match) {
        return `${parseInt(match[1]).toString().padStart(2, '0')}:${match[2]}`;
      }
    }
    
    return '';
  };

  const handleSave = async () => {
    // Get sport value to determine if Badminton/TT
    const sport = getSportValue();
    
    // Mark all fields as touched for validation
    const touchedFields: any = {
      matchNum: true,
      time: true,
      sports: true,
      stage: true,
      ageCategory: true,
      group: true,
      teamA: true,
      teamB: true,
      playerA: true,
      playerB: true,
      playerA2: true,
      playerB2: true,
      date: true,
      venue: true,
      venueLong: true,
    };
    
    if (isBadmintonOrTT()) {
      touchedFields.round = true;
      touchedFields.sets = true;
    }
    
    setFormTouched(touchedFields);

    setIsSaving(true);
    setFormError(null);
    
    try {
      // Convert time from 12-hour format to 24-hour format for the payload
      const time24Hour = formatTimeTo24Hour(formData.time);
      
      const payload: any = {
        tournament: tournamentId!,
        ageCategory: formData.ageCategory.value,
        sports: formData.sports.label, // Use capitalized label (e.g., "Football")
        stage: formData.stage?.value ? formData.stage.value.toLowerCase() : null, // Ensure lowercase (e.g., "knockout", "league")
        matchNum: parseInt(formData.matchNum),
        date: formData.date,
        time: time24Hour, // Converted to 24-hour format (e.g., "20:00")
        venue: formData.venue,
        venueLong: formData.venueLong || formData.venue,
        title: formData.title,
        subtitle: formData.subtitle,
        streamingVideoUrl: formData.streamingVideoUrl || null,
        ...(sport.replace(/[\s_-]/g, '').toLowerCase() === 'pickleball'
          ? { eventCategory: formData.eventCategory?.value }
          : {}),
      };
      
      // Check age category format instead of sport type
      const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory.value);
      const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
      const isPadel = sport.replace(/[\s_-]/g, '').toLowerCase() === 'padel';
      const isPickleball = sport.replace(/[\s_-]/g, '').toLowerCase() === 'pickleball';
      const requiresTwoPlayersPerSide = isPadel || (isPickleball && isPickleballDoubles(formData.eventCategory?.value));
      const isPlayerFormat = ageCategoryFormat === 'player' || isPadel || isPickleball;
      const isFootball = sport.replace(/[\s_-]/g, '').toLowerCase() === 'football';
      const usesPlayers = !isFootball && isPlayerFormat;
      const side1Players = [formData.playerA?.value, formData.playerA2?.value].filter(Boolean);
      const side2Players = [formData.playerB?.value, formData.playerB2?.value].filter(Boolean);

      payload.format = usesPlayers ? 'player' : 'team';
      if (usesPlayers) {
        payload.preserveEmptyParticipants = true;
      }
      payload.group = formData.group?.value || '';
      payload.participants = [
        {
          slot: 1,
          ...(usesPlayers
            ? { players: requiresTwoPlayersPerSide ? side1Players : side1Players.slice(0, 1) }
            : { team: formData.teamA.value }),
        },
        {
          slot: 2,
          ...(usesPlayers
            ? { players: requiresTwoPlayersPerSide ? side2Players : side2Players.slice(0, 1) }
            : { team: formData.teamB.value }),
        },
      ];

      if (usesPlayers) {
        // Add setCount and round (typically used for player format sports like Badminton/Table Tennis)
        if (formData.sets) {
          payload.setCount = parseInt(formData.sets, 10);
        }
        if (formData.round) {
          payload.round = formData.round;
        }
      }
      
      // Add knockout fields for Football when stage is knockout
      if (!isBadmintonOrTT() && formData.stage?.value === 'knockout') {
        payload.knockout = {
          stage: formData.knockoutStage?.value || null,
          index: formData.knockoutIndex ? parseInt(formData.knockoutIndex, 10) : null,
          matchNum: formData.knockoutMatchNum || null,
        };
      }
      
      await editFixture(matchId!, payload, sport); // FIX: Pass sport parameter
      
      // Reload fixture data
      const fixtureRes = await getFixtureById(matchId!, sport); // FIX: Pass sport parameter
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Reload form data with updated fixture data
      const ageCategoryOptionsList = ageCategoryOptions;
      const ageCategoryId = typeof fixtureData?.ageCategory === 'object' ? fixtureData?.ageCategory?._id : fixtureData?.ageCategory;
      const selectedAgeCategory = ageCategoryOptionsList.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === ageCategoryId);
      const stageOptions = [
        { value: 'league', label: 'League' },
        { value: 'knockout', label: 'Knockout' },
      ];
      const fixtureStage = fixtureData?.stage || (fixtureData as any)?.knockout?.stage;
      const selectedStage = fixtureStage ? stageOptions.find(s => s.value === fixtureStage?.toLowerCase()) || null : null;
      
      // Convert time to 12-hour format
      // Priority: time (scheduled time) -> dateTime (scheduled datetime) -> startTime (actual start time)
      let timeValue = fixtureData?.time || (fixtureData as any)?.dateTime || fixtureData?.startTime || '';
      if (timeValue && timeValue.includes('T')) {
        const date = new Date(timeValue);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        timeValue = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      } else if (timeValue && timeValue.includes(':') && !timeValue.includes('AM') && !timeValue.includes('PM')) {
        const parts = timeValue.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1] || '0', 10);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        timeValue = `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      // Get team/player info using helper logic - declare variables outside object literal
      const team1InfoReload = (fixtureData as any)?.team1Info;
      const team2InfoReload = (fixtureData as any)?.team2Info;
      const teamAReload = fixtureData?.teamA;
      const teamBReload = fixtureData?.teamB;
      
      // Get team/player info using helper logic - check side1/side2 first, then fallback
      const side1Reload = (fixtureData as any)?.side1;
      const side2Reload = (fixtureData as any)?.side2;
      
      // Get team IDs and names - Priority: 1. side1/side2 with team objects, 2. team1Info/teamA, 3. side1/side2 with string IDs
      let team1IdReload, team2IdReload, team1NameReload, team2NameReload;
      
      // Check side1 for team data
      if (side1Reload) {
        // Priority 1: Check for team object
        if (side1Reload.team && typeof side1Reload.team === 'object' && side1Reload.team._id) {
          team1IdReload = side1Reload.team._id;
          team1NameReload = side1Reload.team.name || side1Reload.team.shortName;
        }
        // Priority 2: Check for string ID
        else if (side1Reload.team && typeof side1Reload.team === 'string') {
          team1IdReload = side1Reload.team;
          // Try to get name from team1Info or teamA
          team1NameReload = team1InfoReload ? (team1InfoReload.name || team1InfoReload.shortName) : (teamAReload?.name || teamAReload?.shortName);
        }
      }
      
      // Check side2 for team data
      if (side2Reload) {
        // Priority 1: Check for team object
        if (side2Reload.team && typeof side2Reload.team === 'object' && side2Reload.team._id) {
          team2IdReload = side2Reload.team._id;
          team2NameReload = side2Reload.team.name || side2Reload.team.shortName;
        }
        // Priority 2: Check for string ID
        else if (side2Reload.team && typeof side2Reload.team === 'string') {
          team2IdReload = side2Reload.team;
          // Try to get name from team2Info or teamB
          team2NameReload = team2InfoReload ? (team2InfoReload.name || team2InfoReload.shortName) : (teamBReload?.name || teamBReload?.shortName);
        }
      }
      
      // Fallback to old structure if new structure didn't provide data
      if (!team1IdReload) {
        team1IdReload = team1InfoReload?._id || teamAReload?._id;
        team1NameReload = team1InfoReload ? (team1InfoReload.name || team1InfoReload.shortName || (team1InfoReload.players?.[0]?.name)) : (teamAReload?.name || teamAReload?.shortName);
      }
      if (!team2IdReload) {
        team2IdReload = team2InfoReload?._id || teamBReload?._id;
        team2NameReload = team2InfoReload ? (team2InfoReload.name || team2InfoReload.shortName || (team2InfoReload.players?.[0]?.name)) : (teamBReload?.name || teamBReload?.shortName);
      }
      
      // If we have ID but no name, try to get name from all sources
      if (team1IdReload && (!team1NameReload || team1NameReload === 'Team A')) {
        const fallbackName = team1InfoReload ? (team1InfoReload.name || team1InfoReload.shortName) : 
                            (teamAReload?.name || teamAReload?.shortName) ||
                            (side1Reload?.team && typeof side1Reload.team === 'object' ? (side1Reload.team.name || side1Reload.team.shortName) : null);
        if (fallbackName && fallbackName !== 'Team A') {
          team1NameReload = fallbackName;
        }
      }
      
      if (team2IdReload && (!team2NameReload || team2NameReload === 'Team B')) {
        const fallbackName = team2InfoReload ? (team2InfoReload.name || team2InfoReload.shortName) : 
                            (teamBReload?.name || teamBReload?.shortName) ||
                            (side2Reload?.team && typeof side2Reload.team === 'object' ? (side2Reload.team.name || side2Reload.team.shortName) : null);
        if (fallbackName && fallbackName !== 'Team B') {
          team2NameReload = fallbackName;
        }
      }
      
      // Debug logging
      console.log('Team data extraction for reload (team format):', {
        team1IdReload: team1IdReload,
        team1NameReload: team1NameReload,
        team2IdReload: team2IdReload,
        team2NameReload: team2NameReload,
        side1Reload: side1Reload,
        side2Reload: side2Reload,
        team1InfoReload: team1InfoReload,
        team2InfoReload: team2InfoReload,
        teamAReload: teamAReload,
        teamBReload: teamBReload
      });
      
      const sportReload = fixtureData?.sports || getSportValue();
      
      // Check age category format instead of sport type
      const ageCategoryIdReload = typeof fixtureData.ageCategory === 'object' ? fixtureData.ageCategory?._id : fixtureData.ageCategory;
      const ageCategoryObjForReload = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === ageCategoryIdReload);
      const ageCategoryFormatForReload = ageCategoryObjForReload?.format?.toLowerCase();
      const ageCategoryLabelForReload = ageCategoryObjForReload?.label?.toLowerCase() || '';
      
      // Check if format is 'player' or if age category name contains 'single'/'singles' (for Badminton/Table Tennis)
      let isPlayerFormatForReload = ageCategoryFormatForReload === 'player';
      const isSingleAgeCategoryForReload = ageCategoryLabelForReload.includes('single') || ageCategoryLabelForReload.includes('singles');
      
      // Fallback: If format is missing, check sport type and age category name for Badminton/Table Tennis
      if (!ageCategoryFormatForReload) {
        const normalizedSportReload = sportReload.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSportReload === 'badminton' || normalizedSportReload === 'tabletennis' || normalizedSportReload === 'tt';
        if (isBadmintonOrTT) {
          isPlayerFormatForReload = true;
        }
      } else if (ageCategoryFormatForReload && isSingleAgeCategoryForReload) {
        const normalizedSportReload = sportReload.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSportReload === 'badminton' || normalizedSportReload === 'tabletennis' || normalizedSportReload === 'tt';
        if (isBadmintonOrTT && !isPlayerFormatForReload) {
          isPlayerFormatForReload = true;
        }
      }
      
      // Initialize group - use fixture.group if it exists
      let groupFieldReload = null;
      if (fixtureData?.group) {
        groupFieldReload = { value: fixtureData.group, label: `Group ${fixtureData.group}` };
      }
      
      // For Badminton/Table Tennis, prefill round and sets regardless of format (team or player)
      // For other sports, only prefill if player format
      const normalizedSportReloadForPrefill = sportReload.replace(/\s+/g, '').toLowerCase();
      const isBadmintonOrTTSportReload = normalizedSportReloadForPrefill === 'badminton' || normalizedSportReloadForPrefill === 'tabletennis' || normalizedSportReloadForPrefill === 'tt';
      const shouldPrefillRoundAndSetsReload = isBadmintonOrTTSportReload || isPlayerFormatForReload;
      
      const updatedFormData = {
        matchNum: fixtureData?.matchNum || '',
        sports: { value: sportReload, label: sportReload },
        date: fixtureData?.date || '',
        time: timeValue,
        venue: fixtureData?.venue || '',
        venueLong: fixtureData?.venueLong || fixtureData?.venue || '',
        title: (fixtureData as any)?.title || '',
        subtitle: (fixtureData as any)?.subtitle || '',
        teamA: isPlayerFormatForReload ? null : (team1IdReload ? { value: team1IdReload, label: team1NameReload || 'Team A' } : null),
        teamB: isPlayerFormatForReload ? null : (team2IdReload ? { value: team2IdReload, label: team2NameReload || 'Team B' } : null),
        playerA: isPlayerFormatForReload ? (team1IdReload ? { value: team1IdReload, label: team1NameReload || 'Player A' } : null) : null,
        playerB: isPlayerFormatForReload ? (team2IdReload ? { value: team2IdReload, label: team2NameReload || 'Player B' } : null) : null,
        ageCategory: selectedAgeCategory || null,
        stage: selectedStage,
        group: groupFieldReload,
        round: shouldPrefillRoundAndSetsReload ? (fixtureData?.round || '') : '',
        sets: shouldPrefillRoundAndSetsReload ? (fixtureData?.setCount ? String(fixtureData.setCount) : '') : '',
        streamKey: (fixtureData as any)?.liveStreamInfo?.streamKey || '',
        ingestionUrl: (fixtureData as any)?.liveStreamInfo?.ingestionUrl || (fixtureData as any)?.liveStreamInfo?.streamUrl || '',
        recordedUrl: (fixtureData as any)?.streamRecordingInfo?.streamS3Url || (fixtureData as any)?.streamRecordingInfo?.recordedUrl || '',
        streamingVideoUrl: (fixtureData as any)?.streamingVideoUrl || ''
      };
      
      setFormData(updatedFormData);
      setOriginalFormData(JSON.parse(JSON.stringify(updatedFormData)));
      setIsFormModified(false);
      setFormTouched({});
      setShowEditModal(false);
      // Scroll to top to show the updated fixture details
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to save fixture'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEditFixture || isMatchLiveOrCompleted) {
      setDeleteError('Cannot delete fixture once match has started or completed');
      return;
    }
    setIsDeleting(true);
    setDeleteError(null);
    try {
      // Get sport value for API calls
      const sport = getSportValue();
      await deleteFixture(matchId!, sport); // FIX: Pass sport parameter
      // If from tournament detail page (default season), navigate back to tournament detail page
      // Otherwise, navigate to season detail page if tournamentSeasonId exists
      if (fromTournamentDetailPage || !tournamentSeasonId) {
        navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
          state: { 
            activeTab: 'match-management',
            selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
            allTournaments: (location.state as any)?.allTournaments
          } 
        });
      } else {
        navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
          state: { 
            activeTab: 'match-management',
            selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
            allTournaments: (location.state as any)?.allTournaments
          } 
        });
      }
    } catch (err: any) {
      setDeleteError(extractErrorMessage(err, 'Failed to delete fixture'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Timer helper functions
  const formatTimer = (seconds: number): string => {
    // Always display as MM:SS format - ensure seconds is an integer
    const totalSeconds = Math.floor(seconds);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${totalMinutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle update timer - set timer to specific value when paused
  const handleUpdateTimer = async () => {
    if (!matchId) return;
    
    // Validate timer value
    if (!updateTimerValue || !updateTimerValue.includes(':')) {
      setFormError('Please enter time in MM:SS format (e.g., 45:30)');
      return;
    }
    
    const timeParts = updateTimerValue.split(':');
    if (timeParts.length !== 2) {
      setFormError('Please enter time in MM:SS format (e.g., 45:30)');
      return;
    }
    
    const minutes = parseInt(timeParts[0], 10);
    const seconds = parseInt(timeParts[1], 10);
    
    if (isNaN(minutes) || isNaN(seconds)) {
      setFormError('Please enter valid numbers for minutes and seconds');
      return;
    }
    
    if (minutes > 99 || seconds > 59) {
      setFormError('Maximum time is 99:59');
      return;
    }
    
    // Convert MM:SS to seconds
    const timeInSeconds = minutes * 60 + seconds;
    
    // Cap at 99:59 (5999 seconds)
    const cappedSeconds = timeInSeconds >= 5999 ? 5999 : timeInSeconds;
    
    setIsUpdatingTimer(true);
    setFormError(null);
    
    try {
      // Get sport value for API calls
      const sport = getSportValue();
      // Update timer via API
      await updateTimer(matchId, cappedSeconds, sport);
      
      // Update local state
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      
      setMatchTimer(cappedSeconds);
      setAccumulatedTime(cappedSeconds);
      accumulatedTimeRef.current = cappedSeconds;
      setIsTimerRunning(false);
      setTimerStartTime(null);
      timerStartTimeRef.current = null;
      
      // Refresh fixture data
      const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Close modal and reset
      setShowUpdateTimerModal(false);
      setUpdateTimerValue('');
      setFormError(null);
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to update timer'));
    } finally {
      setIsUpdatingTimer(false);
    }
  };

  // Handle opening update timer modal
  const handleOpenUpdateTimerModal = () => {
    // Pre-fill with current timer value in MM:SS format
    const currentMinutes = Math.floor(matchTimer / 60);
    const currentSeconds = matchTimer % 60;
    setUpdateTimerValue(`${currentMinutes.toString().padStart(2, '0')}:${currentSeconds.toString().padStart(2, '0')}`);
    setFormError(null);
    setShowUpdateTimerModal(true);
  };

  // Handle closing update timer modal
  const handleCloseUpdateTimerModal = () => {
    setShowUpdateTimerModal(false);
    setUpdateTimerValue('');
    setFormError(null);
  };

  // Handle time input with validation - only allows MM:SS format, max 99:59
  const handleTimeInputChange = (value: string) => {
    // Remove any non-numeric characters except colon
    let cleaned = value.replace(/[^0-9:]/g, '');
    
    // Remove spaces
    cleaned = cleaned.replace(/\s/g, '');
    
    // Only allow one colon
    const colonCount = (cleaned.match(/:/g) || []).length;
    if (colonCount > 1) {
      // Keep only the first colon
      const firstColonIndex = cleaned.indexOf(':');
      cleaned = cleaned.substring(0, firstColonIndex + 1) + cleaned.substring(firstColonIndex + 1).replace(/:/g, '');
    }
    
    // Split by colon to validate parts
    const parts = cleaned.split(':');
    
    if (parts.length === 1) {
      // No colon yet - allow typing numbers (max 2 digits for minutes)
      if (cleaned.length <= 2) {
        setEventTime(cleaned);
      } else if (cleaned.length === 3) {
        // Auto-insert colon after 2 digits
        setEventTime(cleaned.substring(0, 2) + ':' + cleaned.substring(2));
      }
    } else if (parts.length === 2) {
      // Has colon - validate both parts
      let minutes = parts[0];
      let seconds = parts[1];
      
      // Limit minutes to 2 digits and max 99
      if (minutes.length > 2) {
        minutes = minutes.substring(0, 2);
      }
      const minutesNum = parseInt(minutes, 10);
      if (!isNaN(minutesNum) && minutesNum > 99) {
        minutes = '99';
      }
      
      // Limit seconds to 2 digits and max 59
      if (seconds.length > 2) {
        seconds = seconds.substring(0, 2);
      }
      const secondsNum = parseInt(seconds, 10);
      if (!isNaN(secondsNum) && secondsNum > 59) {
        seconds = '59';
      }
      
      // Reconstruct the time string
      setEventTime(minutes + ':' + seconds);
    } else {
      // Invalid format - keep previous value
      return;
    }
  };

  const startTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    // Update ref with current accumulated time before starting
    setAccumulatedTime((prev) => {
      accumulatedTimeRef.current = prev;
      return prev;
    });
    
    const now = Date.now();
    setTimerStartTime(now);
    timerStartTimeRef.current = now;
    setIsTimerRunning(true);
    
    timerIntervalRef.current = setInterval(() => {
      setMatchTimer(() => {
        const elapsed = Math.floor((Date.now() - (timerStartTimeRef.current || now)) / 1000);
        const newTime = accumulatedTimeRef.current + elapsed;
        const finalTime = Math.floor(newTime); // Ensure integer value
        
        // Stop timer at 99:59 (5999 seconds)
        if (finalTime >= 5999) {
          // Clear the interval to stop the timer
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          // Cap accumulated time at 5999
          accumulatedTimeRef.current = 5999;
          setAccumulatedTime(5999);
          setIsTimerRunning(false);
          setTimerStartTime(null);
          timerStartTimeRef.current = null;
          return 5999; // Cap at 99:59
        }
        
        return finalTime;
      });
    }, 1000);
  };

  const pauseTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    if (timerStartTimeRef.current) {
      const elapsed = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
      setAccumulatedTime((prev) => {
        const newAccumulated = Math.floor(prev + elapsed); // Ensure integer value
        accumulatedTimeRef.current = newAccumulated; // Update ref
        // Update the timer display with the new accumulated time
        setMatchTimer(newAccumulated);
        return newAccumulated;
      });
    }
    
    setIsTimerRunning(false);
    setTimerStartTime(null);
    timerStartTimeRef.current = null;
  };

  const handleResetTimer = async () => {
    if (!matchId) return;
    
    // Check if match is paused - user must pause the match before resetting
    if (matchStatus !== 'paused') {
      setFormError('Please pause the match before resetting the timer.');
      return;
    }
    
    setIsResettingTimer(true);
    setFormError(null);
    try {
      // Get sport value for API calls
      const sport = getSportValue();
      // Reset timer to 0 seconds via API
      await updateTimer(matchId, 0, sport);
      
      // Update local state
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setMatchTimer(0);
      setAccumulatedTime(0);
      accumulatedTimeRef.current = 0;
      setIsTimerRunning(false);
      setTimerStartTime(null);
      timerStartTimeRef.current = null;
      
      // Refresh fixture data
      const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to reset timer'));
    } finally {
      setIsResettingTimer(false);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  // Ensure timer stops when match is paused
  useEffect(() => {
    if (matchStatus === 'paused' && isTimerRunning) {
      // If status is paused but timer is still running, stop it
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setIsTimerRunning(false);
      setTimerStartTime(null);
      timerStartTimeRef.current = null;
    }
  }, [matchStatus, isTimerRunning]);

  // Match management handlers - confirmation modals
  const handleStartMatchClick = () => {
    setShowStartMatchModal(true);
  };

  const handlePauseMatchClick = () => {
    setShowPauseMatchModal(true);
  };

  const handleResumeMatchClick = () => {
    setShowResumeMatchModal(true);
  };

  const handleEndMatchClick = () => {
    setShowEndMatchModal(true);
  };

  // Actual match management handlers
  const handleStartMatch = async () => {
    if (!matchId) return;
    
    setShowStartMatchModal(false);
    setIsStartingMatch(true);
    try {
      const sport = getSportValue();
      const isMultiSports = isBadmintonOrTT();
      
      // Refresh fixture data first to get setCount
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Use unified timer API endpoint for all sports (Football, Badminton, Table Tennis)
      await startTimerAPI(matchId, sport);
      
      // For Badminton/TT, load sets after starting match
      if (isMultiSports) {
        // Sets will be created automatically when needed via add-live-score endpoint
        await loadSets();
      } else {
        // Start the local timer for Football
        startTimer();
      }
      
      // Clear any existing live score events before starting the match
      // This ensures we don't show stale/test data from previous API calls
      setLiveScoreEvents([]);
      setTeamAScore(0);
      setTeamBScore(0);
      
      setMatchStatus('ongoing');
      
      // Load live scores after starting (API will return fresh data)
      // Use a small delay to ensure backend has processed the start
      setTimeout(() => {
        if (matchId) {
          loadLiveScores(matchId);
        }
      }, 500);
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to start match'));
    } finally {
      setIsStartingMatch(false);
    }
  };

  const handlePauseMatch = async () => {
    if (!matchId) return;
    
    setShowPauseMatchModal(false);
    setIsPausingMatch(true);
    try {
      const sport = getSportValue();
      const isMultiSports = isBadmintonOrTT();
      
      // Use unified timer API endpoint for all sports
      await pauseTimerAPI(matchId, sport);
      
      // Pause the local timer for Football
      if (!isMultiSports && isLiveScoringSupportedSport(sport)) {
        pauseTimer();
      }
      
      // Get sport value for API calls
      // Refresh fixture data to get updated state
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Extract and set the actual status from backend
      // Priority: Use 'status' field first (primary field from API)
      const rawStatus = (fixtureData as any)?.status || 
                       (fixtureData as any)?.streamStatus || 
                       (fixtureData as any)?.liveStreamInfo?.status || 
                       (fixtureData as any)?.matchStatus ||
                       'paused'; // Default to paused since we just paused
      
      // Normalize status - ensure paused status is preserved
      let normalizedStatus = (rawStatus || '').toLowerCase().trim();
      if (normalizedStatus === 'paused' || normalizedStatus === 'on-hold' || normalizedStatus === 'on_hold' || normalizedStatus === 'hold') {
        normalizedStatus = 'paused';
      } else {
        // If backend doesn't return paused, but we just paused, force it to paused
        // This handles cases where backend might still show 'ongoing' but timer is paused
        normalizedStatus = 'paused';
      }
      setMatchStatus(normalizedStatus);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to pause match');
      setFormError(errorMessage);
      
      // If the error indicates match is not started, sync the local state
      if (errorMessage.toLowerCase().includes('match not started') || 
          errorMessage.toLowerCase().includes('not started')) {
        // Match is not actually started on backend - sync local state
        setMatchStatus('upcoming');
        pauseTimer(); // Stop the local timer
        setMatchTimer(0);
        setAccumulatedTime(0);
        accumulatedTimeRef.current = 0;
        setIsTimerRunning(false);
        setTimerStartTime(null);
        timerStartTimeRef.current = null;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // Get sport value for API calls
        const sport = getSportValue();
        // Refresh fixture data to get actual state
        try {
          const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
          const fixtureData = fixtureRes?.data || fixtureRes;
          setFixture(fixtureData as Fixture);
        } catch (refreshErr) {
          // Ignore refresh errors
        }
      }
    } finally {
      setIsPausingMatch(false);
    }
  };

  const handleResumeMatch = async () => {
    if (!matchId) return;
    
    setShowResumeMatchModal(false);
    setIsStartingMatch(true);
    try {
      const sport = getSportValue();
      const isMultiSports = isBadmintonOrTT();
      
      // Use unified timer API endpoint for all sports
      await resumeTimerAPI(matchId, sport);
      
      // For Football, fetch live scores directly to get the most up-to-date timer value from backend
      if (!isMultiSports) {
      // Fetch live scores directly to get the most up-to-date timer value from backend
        const liveScoresResponse = await fetchLiveScores(matchId, sport);
      let latestTimerValue = accumulatedTimeRef.current || 0;
      
      // Extract timer from live scores response
      if (liveScoresResponse && typeof liveScoresResponse === 'object') {
        const liveScoreData = liveScoresResponse.data || liveScoresResponse;
        if (liveScoreData?.timer !== undefined && liveScoreData?.timer !== null) {
          latestTimerValue = typeof liveScoreData.timer === 'number' 
            ? liveScoreData.timer 
            : parseFloat(String(liveScoreData.timer)) || 0;
        }
      }
      
      // Cap timer at 99:59 (5999 seconds) when resuming
      const roundedTimerValue = Math.floor(latestTimerValue);
      const cappedTimerValue = roundedTimerValue >= 5999 ? 5999 : roundedTimerValue;
      
      // Update accumulated time with the latest backend value
      accumulatedTimeRef.current = cappedTimerValue;
      setAccumulatedTime(cappedTimerValue);
      setMatchTimer(cappedTimerValue);
      
      // If timer is already at max, don't start it
      if (cappedTimerValue >= 5999) {
        setIsTimerRunning(false);
        setTimerStartTime(null);
        timerStartTimeRef.current = null;
      } else {
        // Now start the local timer with the updated value
        startTimer();
      }
      }
      
      // Also load live scores to update the UI state
      await loadLiveScores(matchId, true);
      
      // Refresh fixture data to get updated state
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Extract and set the actual status from backend
      // Priority: Use 'status' field first (primary field from API)
      const rawStatus = (fixtureData as any)?.status || 
                       (fixtureData as any)?.streamStatus || 
                       (fixtureData as any)?.liveStreamInfo?.status || 
                       (fixtureData as any)?.matchStatus ||
                       'ongoing'; // Default to ongoing since we just resumed
      
      // Normalize status - ensure ongoing status is preserved
      let normalizedStatus = (rawStatus || '').toLowerCase().trim();
      if (normalizedStatus === 'ongoing' || normalizedStatus === 'live' || normalizedStatus === 'in-progress' || normalizedStatus === 'in_progress' || normalizedStatus === 'started' || normalizedStatus === 'start' || normalizedStatus === 'running') {
        normalizedStatus = 'ongoing';
      } else {
        // If backend doesn't return ongoing, but we just resumed, force it to ongoing
        normalizedStatus = 'ongoing';
      }
      setMatchStatus(normalizedStatus);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to resume match');
      setFormError(errorMessage);
      
      // If the error indicates match is not started, sync the local state
      if (errorMessage.toLowerCase().includes('match not started') || 
          errorMessage.toLowerCase().includes('not started')) {
        // Match is not actually started on backend - sync local state
        setMatchStatus('upcoming');
        pauseTimer(); // Stop the local timer
        setMatchTimer(0);
        setAccumulatedTime(0);
        accumulatedTimeRef.current = 0;
        setIsTimerRunning(false);
        setTimerStartTime(null);
        timerStartTimeRef.current = null;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // Get sport value for API calls
        const sport = getSportValue();
        // Refresh fixture data to get actual state
        try {
          const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
          const fixtureData = fixtureRes?.data || fixtureRes;
          setFixture(fixtureData as Fixture);
        } catch (refreshErr) {
          // Ignore refresh errors
        }
      }
    } finally {
      setIsStartingMatch(false);
    }
  };

  const handleEndMatch = async () => {
    if (!matchId) return;
    
    setShowEndMatchModal(false);
    setIsEndingMatch(true);
    try {
      const sport = getSportValue();
      const isMultiSports = isBadmintonOrTT();
      
      // Use unified timer API endpoint for all sports
      await endTimer(matchId, sport);
      
      // Stop the local timer for Football
      if (!isMultiSports) {
        pauseTimer();
      }
      
      // Always use getFixtureById to get updated fixture data with sets
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      
      // Extract sets from fixture data for Badminton/TT BEFORE setting fixture state
      // This ensures sets are available immediately when fixture state updates
      if (isBadmintonOrTT() && fixtureData) {
        const fixtureSets = Array.isArray((fixtureData as any)?.sets) 
          ? (fixtureData as any).sets 
          : (Array.isArray((fixtureData as any)?.liveScoreData?.sets) 
              ? (fixtureData as any).liveScoreData.sets 
              : []);
        
        if (fixtureSets.length > 0) {
          setSets(fixtureSets);
          setsExtractedRef.current = true;
          setLastSavedSetData(prev => {
            const newMap = new Map(prev);
            fixtureSets.forEach((set: SetData) => {
              if (set.setNumber) {
                newMap.set(set.setNumber, {
                  side1Points: set.side1Points || 0,
                  side2Points: set.side2Points || 0
                });
              }
            });
            return newMap;
          });
        }
      }
      
      // Set fixture state AFTER extracting sets
      // This ensures useEffect can also extract sets if needed
      setFixture(fixtureData as Fixture);
      
      // Extract and set the actual status from backend
      // Priority: Use 'status' field first (primary field from API)
      const rawStatus = (fixtureData as any)?.status || 
                       (fixtureData as any)?.streamStatus || 
                       (fixtureData as any)?.liveStreamInfo?.status || 
                       (fixtureData as any)?.matchStatus ||
                       'completed';
      
      // Normalize status - ensure 'ended' maps to 'completed'
      let normalizedStatus = (rawStatus || '').toLowerCase().trim();
      if (normalizedStatus === 'completed' || normalizedStatus === 'ended' || normalizedStatus === 'finished' || normalizedStatus === 'complete') {
        normalizedStatus = 'completed';
      } else {
        // If backend doesn't return ended/completed, force it since we just ended the match
        normalizedStatus = 'completed';
      }
      setMatchStatus(normalizedStatus);
      
      // Show Player of the Match modal after ending supported non-set sports.
      // User can skip it and add it later if needed
      if (isLiveScoringSupported && !isBadmintonOrTT()) {
      await handleOpenPlayerOfMatchModal();
      }
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err, 'Failed to end match');
      setFormError(errorMessage);
      
      // If the error indicates match is not started, sync the local state
      if (errorMessage.toLowerCase().includes('match not started') || 
          errorMessage.toLowerCase().includes('not started')) {
        // Match is not actually started on backend - sync local state
        setMatchStatus('upcoming');
        pauseTimer(); // Stop the local timer
        setMatchTimer(0);
        setAccumulatedTime(0);
        accumulatedTimeRef.current = 0;
        setIsTimerRunning(false);
        setTimerStartTime(null);
        timerStartTimeRef.current = null;
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // Get sport value for API calls
        const sport = getSportValue();
        // Refresh fixture data to get actual state
        try {
          const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
          const fixtureData = fixtureRes?.data || fixtureRes;
          setFixture(fixtureData as Fixture);
        } catch (refreshErr) {
          // Ignore refresh errors
        }
      }
    } finally {
      setIsEndingMatch(false);
    }
  };
  
  // Handle closing player of the match modal
  const handleClosePlayerOfMatchModal = () => {
    setShowPlayerOfMatchModal(false);
    setFormError(null);
    // Don't reset the player selections - allow user to come back and edit
  };

  // Handle skipping player of the match selection
  const handleSkipPlayerOfMatch = () => {
    setShowPlayerOfMatchModal(false);
    setFormError(null);
    // Don't reset the player selections - allow user to come back and edit later
  };

  // Handle saving player of the match
  const handleSavePlayerOfMatch = async () => {
    if (!matchId) return;
    
    setIsSavingPlayerOfMatch(true);
    setFormError(null);
    try {
      // Player of the Match is optional - can be null
      await updatePlayerOfTheMatch(matchId, {
        playerOfTheMatch: {
          player: playerOfTheMatch.player || null,
          team: playerOfTheMatch.team || null
        },
        secondPlayerOfTheMatch: {
          player: secondPlayerOfTheMatch.player || null,
          team: secondPlayerOfTheMatch.team || null
        }
      }, getSportValue());
      
      setShowPlayerOfMatchModal(false);
      setFormError(null);
      
      // Reload live scores to get updated data
      await loadLiveScores(matchId);
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to save player of the match'));
    } finally {
      setIsSavingPlayerOfMatch(false);
    }
  };

  // Handle opening player of the match modal (for adding/editing after match ends)
  const handleOpenPlayerOfMatchModal = async () => {
    if (!matchId) return;
    
    // Load players if not already loaded
    if (teamAPlayers.length === 0 && teamBPlayers.length === 0) {
      try {
        await loadLiveScores(matchId, true);
      } catch (loadErr) {
        console.error('Error loading players for Player of the Match:', loadErr);
      }
    }
    
    setShowPlayerOfMatchModal(true);
    setFormError(null);
  };

  // Calculate score from completed sets for Badminton/TT
  const calculateScoreFromSets = useMemo(() => {
    if (!isBadmintonOrTT() || !sets || sets.length === 0) {
      return { teamAScore: 0, teamBScore: 0 };
    }
    
    const completedSets = sets.filter(s => s.isComplete && s.winner);
    const side1Wins = completedSets.filter(s => s.winner === 'side1').length;
    const side2Wins = completedSets.filter(s => s.winner === 'side2').length;
    
    return {
      teamAScore: side1Wins,
      teamBScore: side2Wins
    };
  }, [sets, isBadmintonOrTT]);

  // Helper function to render set-wise score table for Badminton/TT
  const renderSetWiseScoreTable = (allowEdit: boolean = false) => {
    if (!isBadmintonOrTT()) return null;
    
    const setCount = (fixture as any)?.setCount || 3;
    const allSets = sets || [];
    
    // Before match: Show team names only
    if (allSets.length === 0) {
      return (
        <div className="manage-tournaments-table-container" ref={tableContainerRef}>
          <Table className="mb-0 manage-tournaments-table fixtures-table">
            <thead>
              <tr>
                <th>Team/Player</th>
                {Array.from({ length: setCount }, (_, i) => (
                  <th key={i + 1} style={{ textAlign: 'center' }}>Set {i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-user-line" style={{ fontSize: '18px', color: '#495057' }}></i>
                    <span style={{ fontWeight: 500 }}>{getTeam1Name()}</span>
                  </div>
                </td>
                {Array.from({ length: setCount }, (_, i) => (
                  <td key={i + 1} style={{ textAlign: 'center', color: '#6c757d' }}>-</td>
                ))}
              </tr>
              <tr>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-user-line" style={{ fontSize: '18px', color: '#495057' }}></i>
                    <span style={{ fontWeight: 500 }}>{getTeam2Name()}</span>
                  </div>
                </td>
                {Array.from({ length: setCount }, (_, i) => (
                  <td key={i + 1} style={{ textAlign: 'center', color: '#6c757d' }}>-</td>
                ))}
              </tr>
            </tbody>
          </Table>
        </div>
      );
    }
    
    // During/After match: Show set scores
    return (
      <div className="manage-tournaments-table-container" ref={tableContainerRef}>
        <Table className="mb-0 manage-tournaments-table fixtures-table">
          <thead>
            <tr>
              <th>Team/Player</th>
              {Array.from({ length: setCount }, (_, i) => {
                const setNumber = i + 1;
                return (
                  <th key={setNumber} id={`set-${setNumber}`} style={{ textAlign: 'center', padding: '12px 16px', minWidth: '100px', maxWidth: '120px' }}>
                    Set {setNumber}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-user-line" style={{ fontSize: '18px', color: '#495057' }}></i>
                  <span style={{ fontWeight: 500 }}>{getTeam1Name()}</span>
                </div>
              </td>
              {Array.from({ length: setCount }, (_, i) => {
                const setNumber = i + 1;
                const set = allSets.find(s => s.setNumber === setNumber);
                const isCompleted = set && (set.isComplete || (set as any).status === 'completed');
                const isEditing = allowEdit && editingSet && editingSet.setNumber === setNumber;
                const canEdit = allowEdit && isCompleted;
                
                return (
                  <td key={setNumber} style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px 16px', minWidth: '100px', maxWidth: '120px' }}>
                    {isCompleted ? (
                      <div>
                        {allowEdit && isEditing ? (
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                const currentEditing = editingSet || set;
                                const newSet = { ...currentEditing, side1Points: Math.max(0, currentEditing.side1Points - 1) };
                                setEditingSet(newSet);
                              }}
                              style={{ padding: '4px 8px', minWidth: '32px' }}
                            >
                              <i className="ri-subtract-line"></i>
                            </Button>
                            <div style={{ 
                              minWidth: '50px', 
                              fontSize: '18px', 
                              fontWeight: 700,
                              padding: '4px 8px'
                            }}>
                              {editingSet?.side1Points ?? set?.side1Points ?? 0}
                            </div>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                const currentEditing = editingSet || set;
                                const newSet = { ...currentEditing, side1Points: currentEditing.side1Points + 1 };
                                setEditingSet(newSet);
                              }}
                              style={{ padding: '4px 8px', minWidth: '32px' }}
                            >
                              <i className="ri-add-line"></i>
                            </Button>
                          </div>
                        ) : (
                          <div 
                            onClick={allowEdit && canEdit ? () => {
                              if (set) {
                                setEditingSet(set);
                                // Initialize lastSavedSetData if not already set for this set
                                setLastSavedSetData(prev => {
                                  if (!prev.has(set.setNumber)) {
                                    const newMap = new Map(prev);
                                    newMap.set(set.setNumber, {
                                      side1Points: set.side1Points || 0,
                                      side2Points: set.side2Points || 0
                                    });
                                    return newMap;
                                  }
                                  return prev;
                                });
                              }
                            } : undefined}
                            style={{ cursor: (allowEdit && canEdit) ? 'pointer' : 'default' }}
                            title={(allowEdit && canEdit) ? 'Click to edit this set' : ''}
                          >
                            <div style={{ fontSize: '20px', fontWeight: 700 }}>
                              {set?.side1Points ?? 0}
                            </div>
                            {allowEdit && canEdit && (
                              <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px' }}>
                                Click to edit
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d' }}>-</span>
                    )}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-user-line" style={{ fontSize: '18px', color: '#495057' }}></i>
                  <span style={{ fontWeight: 500 }}>{getTeam2Name()}</span>
                </div>
              </td>
              {Array.from({ length: setCount }, (_, i) => {
                const setNumber = i + 1;
                const set = allSets.find(s => s.setNumber === setNumber);
                const isCompleted = set && (set.isComplete || (set as any).status === 'completed');
                const isEditing = allowEdit && editingSet && editingSet.setNumber === setNumber;
                const canEdit = allowEdit && isCompleted;
                
                return (
                  <td key={setNumber} style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px 16px', minWidth: '100px', maxWidth: '120px' }}>
                    {isCompleted ? (
                      <div>
                        {allowEdit && isEditing ? (
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => {
                                const currentEditing = editingSet || set;
                                const newSet = { ...currentEditing, side2Points: Math.max(0, currentEditing.side2Points - 1) };
                                setEditingSet(newSet);
                              }}
                              style={{ padding: '4px 8px', minWidth: '32px' }}
                            >
                              <i className="ri-subtract-line"></i>
                            </Button>
                            <div style={{ 
                              minWidth: '50px', 
                              fontSize: '18px', 
                              fontWeight: 700,
                              padding: '4px 8px'
                            }}>
                              {editingSet?.side2Points ?? set?.side2Points ?? 0}
                            </div>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    const currentEditing = editingSet || set;
                                    const newSet = { ...currentEditing, side2Points: currentEditing.side2Points + 1 };
                                    setEditingSet(newSet);
                                    // Ensure editingSet is set if it wasn't already
                                    if (!editingSet || editingSet.setNumber !== setNumber) {
                                      setEditingSet(newSet);
                                    }
                                  }}
                                  style={{ padding: '4px 8px', minWidth: '32px' }}
                                >
                                  <i className="ri-add-line"></i>
                                </Button>
                          </div>
                        ) : (
                          <div 
                            onClick={allowEdit && canEdit ? () => {
                              if (set) {
                                setEditingSet(set);
                                // Initialize lastSavedSetData if not already set for this set
                                setLastSavedSetData(prev => {
                                  if (!prev.has(set.setNumber)) {
                                    const newMap = new Map(prev);
                                    newMap.set(set.setNumber, {
                                      side1Points: set.side1Points || 0,
                                      side2Points: set.side2Points || 0
                                    });
                                    return newMap;
                                  }
                                  return prev;
                                });
                              }
                            } : undefined}
                            style={{ cursor: (allowEdit && canEdit) ? 'pointer' : 'default' }}
                            title={(allowEdit && canEdit) ? 'Click to edit this set' : ''}
                          >
                            <div style={{ fontSize: '20px', fontWeight: 700 }}>
                              {set?.side2Points ?? 0}
                            </div>
                            {allowEdit && canEdit && (
                              <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '4px' }}>
                                Click to edit
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d' }}>-</span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </Table>
        {allowEdit && editingSet && (
          <div className="d-flex gap-2 mt-3 justify-content-end">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                const setToSave = editingSet;
                handleSaveSet(setToSave);
              }}
                  disabled={isSavingSet || !(editingSet && hasSetChanges(editingSet.setNumber))}
            >
              {isSavingSet ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setEditingSet(null)}
              disabled={isSavingSet}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Update score when sets change for Badminton/TT
  useEffect(() => {
    if (isBadmintonOrTT()) {
      setTeamAScore(calculateScoreFromSets.teamAScore);
      setTeamBScore(calculateScoreFromSets.teamBScore);
    }
  }, [calculateScoreFromSets, isBadmintonOrTT]);

  useEffect(() => {
    if (isBadmintonOrTT() && fixture) {
      const fixtureSets = Array.isArray((fixture as any)?.sets) 
        ? (fixture as any).sets 
        : (Array.isArray((fixture as any)?.liveScoreData?.sets) 
            ? (fixture as any).liveScoreData.sets 
            : []);
      
      if (fixtureSets.length > 0) {
        const needsUpdate = sets.length === 0 || fixtureSets.length > sets.length;
        const currentSetNumbers = new Set<number>(sets.map(s => s.setNumber));
        const fixtureSetNumbersArray = fixtureSets.map((s: any) => s.setNumber).filter((num: any): num is number => typeof num === 'number');
        const fixtureSetNumbers = new Set<number>(fixtureSetNumbersArray);
        const hasDifferentSets = currentSetNumbers.size !== fixtureSetNumbers.size || 
          !fixtureSetNumbersArray.every((num: number) => currentSetNumbers.has(num));
        
        if (needsUpdate || hasDifferentSets) {
          setSets(fixtureSets);
          setsExtractedRef.current = true;
          setLastSavedSetData(prev => {
            const newMap = new Map(prev);
            fixtureSets.forEach((set: SetData) => {
              if (set.setNumber) {
                newMap.set(set.setNumber, {
                  side1Points: set.side1Points || 0,
                  side2Points: set.side2Points || 0
                });
              }
            });
            return newMap;
          });
        }
      }
    }
  }, [fixture?._id, fixture, isBadmintonOrTT]);


  // Sets management functions (for Badminton and Table Tennis)
  const loadSets = async () => {
    if (!matchId || !isBadmintonOrTT()) return;
    
    setLoadingSets(true);
    try {
      const sport = getSportValue();
      const setsData = await getSets(matchId, sport);
      const setsArray = Array.isArray(setsData) ? setsData : (setsData?.sets || []);
      
      if (setsArray.length > 0) {
        setSets(setsArray);
        setsExtractedRef.current = true;
        setLastSavedSetData(prev => {
          const newMap = new Map(prev);
          setsArray.forEach((set: SetData) => {
            if (set.setNumber) {
              newMap.set(set.setNumber, {
                side1Points: set.side1Points || 0,
                side2Points: set.side2Points || 0
              });
            }
          });
          return newMap;
        });
      } else {
        if (sets.length > 0 && setsExtractedRef.current) {
          // Don't update sets - keep existing ones
        } else if (fixture && (fixture as any)?.sets) {
          const fixtureSets = Array.isArray((fixture as any).sets) ? (fixture as any).sets : [];
          if (fixtureSets.length > 0) {
            setSets(fixtureSets);
            setsExtractedRef.current = true;
            setLastSavedSetData(prev => {
              const newMap = new Map(prev);
              fixtureSets.forEach((set: SetData) => {
                if (set.setNumber) {
                  newMap.set(set.setNumber, {
                    side1Points: set.side1Points || 0,
                    side2Points: set.side2Points || 0
                  });
                }
              });
              return newMap;
            });
          }
        }
      }
    } catch (err: any) {
      if (sets.length === 0 && fixture && (fixture as any)?.sets) {
        const fixtureSets = Array.isArray((fixture as any).sets) ? (fixture as any).sets : [];
        if (fixtureSets.length > 0) {
          setSets(fixtureSets);
          setsExtractedRef.current = true;
          setLastSavedSetData(prev => {
            const newMap = new Map(prev);
            fixtureSets.forEach((set: SetData) => {
              if (set.setNumber) {
                newMap.set(set.setNumber, {
                  side1Points: set.side1Points || 0,
                  side2Points: set.side2Points || 0
                });
              }
            });
            return newMap;
          });
        }
      }
    } finally {
      setLoadingSets(false);
    }
  };
  
  const handleResetSet = async (setNumber: number) => {
    if (!matchId || !isBadmintonOrTT() || !setNumber) return;
    
    setIsSavingSet(true);
    setFormError(null);
    try {
      const sport = getSportValue();
      
      // Call updateSet API with reset values (points set to 0)
      await updateSet(matchId, sport, {
        setNumber: setNumber,
        side1Points: 0,
        side2Points: 0
      });
      
      // Reload sets to get updated data
      await loadSets();
      
      // Refresh fixture data
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Clear editing state
      setEditingSet(null);
      
      // Score will be recalculated automatically via useEffect when sets update
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to reset set'));
    } finally {
      setIsSavingSet(false);
    }
  };

  // Helper function to check if set has unsaved changes
  const hasSetChanges = (setNumber: number): boolean => {
    if (!editingSet || editingSet.setNumber !== setNumber) {
      return false; // No editing happening for this set
    }
    
    const lastSaved = lastSavedSetData.get(setNumber);
    if (!lastSaved) {
      return true; // Never saved before, so there are changes
    }
    
    // Compare current editing state with last saved state
    return editingSet.side1Points !== lastSaved.side1Points || 
           editingSet.side2Points !== lastSaved.side2Points;
  };

  const handleSaveSet = async (setData: SetData) => {
    if (!matchId || !isBadmintonOrTT() || !setData.setNumber) return;
    
    // Store current scroll position to prevent jumping to top
    const scrollPosition = window.scrollY;
    
    setIsSavingSet(true);
    try {
      const sport = getSportValue();
      const existingSet = sets.find(s => s.setNumber === setData.setNumber);
      const isCurrentlyComplete = existingSet?.isComplete || false;
      
      // If the set was already complete, keep it as complete after saving
      // The winner will be recalculated by the backend based on the new scores
      const payload: any = {
        setNumber: setData.setNumber,
        side1Points: setData.side1Points,
        side2Points: setData.side2Points
      };
      
      // If saving a completed set, keep it as completed
      if (isCurrentlyComplete) {
        payload.status = 'completed';
      }
      
      await updateSet(matchId, sport, payload);
      await loadSets();
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Store the saved state for this set to track changes
      setLastSavedSetData(prev => {
        const newMap = new Map(prev);
        newMap.set(setData.setNumber, {
          side1Points: setData.side1Points,
          side2Points: setData.side2Points
        });
        return newMap;
      });
      
      setEditingSet(null);
      
      // Restore scroll position immediately and prevent any scrolling
      // Use multiple requestAnimationFrame calls to ensure position is maintained
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      });
      
      // Score will be recalculated automatically via useEffect when sets update
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to save set'));
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } finally {
      setIsSavingSet(false);
    }
  };
  
  // Reusable function to create a new set
  const handleCreateSet = async () => {
    if (!matchId) return;
    
    setIsSavingSet(true);
    setFormError(null);
    try {
      const sport = getSportValue();
      const previousSetsCount = sets.length;
      
      // Verify all previous sets are complete before allowing new set creation
      const allPreviousComplete = sets.length === 0 || sets.every(s => s.isComplete || (s as any).status === 'completed');
      if (!allPreviousComplete) {
        setFormError('Please complete all previous sets before adding a new one.');
        return;
      }
      
      // Call addSet to create new set - API endpoint: /api/v2/tournaments/manage/add-live-score/{matchId}?sport={sport}
      // Payload: {} (empty object) - API will create new set with default values
      const response = await addSet(matchId, sport);
      
      // Check if response indicates success
      if (response?.statusCode === 200 || response?.status === 'success' || response?.data) {
        // Small delay to ensure backend has processed the request
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Reload sets to get the newly created set
        await loadSets();
        
        // Refresh fixture data
        const fixtureRes = await getFixtureById(matchId, sport);
        const fixtureData = fixtureRes?.data || fixtureRes;
        setFixture(fixtureData as Fixture);
        
        // Verify the set was created by checking if sets count increased
        const updatedSetsData = await getSets(matchId, sport);
        const updatedSetsArray = Array.isArray(updatedSetsData) ? updatedSetsData : (updatedSetsData?.sets || []);
        
        if (updatedSetsArray.length <= previousSetsCount) {
          // Set was not created, show error
          setFormError('Failed to create new set. Please ensure all previous sets are completed and try again.');
        } else {
          // Set was created successfully, update local state
          setSets(updatedSetsArray);
          
          // Scroll to the newly created set card and table column for better UX
          const newSetNumber = updatedSetsArray.length;
          setTimeout(() => {
            // First, scroll to the set card (if it exists) - this is the main UI element
            const setCardElement = document.getElementById(`set-card-${newSetNumber}`);
            if (setCardElement) {
              // Scroll the page to bring the set card into view
              setCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Also scroll the table container horizontally to show the new set column
              setTimeout(() => {
                const setTableElement = document.getElementById(`set-${newSetNumber}`);
                if (setTableElement && tableContainerRef.current) {
                  const container = tableContainerRef.current;
                  const containerRect = container.getBoundingClientRect();
                  const elementRect = setTableElement.getBoundingClientRect();
                  const scrollLeft = container.scrollLeft + (elementRect.left - containerRect.left) - (containerRect.width / 2) + (elementRect.width / 2);
                  container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                }
              }, 300);
            } else {
              // Fallback: if set card doesn't exist, just scroll table horizontally
              const setTableElement = document.getElementById(`set-${newSetNumber}`);
              if (setTableElement && tableContainerRef.current) {
                const container = tableContainerRef.current;
                const containerRect = container.getBoundingClientRect();
                const elementRect = setTableElement.getBoundingClientRect();
                const scrollLeft = container.scrollLeft + (elementRect.left - containerRect.left) - (containerRect.width / 2) + (elementRect.width / 2);
                container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
              }
            }
          }, 200);
        }
      } else {
        // API returned unexpected response
        setFormError('Failed to create new set. Please try again.');
      }
    } catch (err: any) {
      const errorMsg = extractErrorMessage(err, 'Failed to create new set');
      setFormError(errorMsg);
      console.error('Error creating new set:', err);
      console.error('Error details:', {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
    } finally {
      setIsSavingSet(false);
    }
  };

  const handleMarkSetComplete = async (setNumber: number) => {
    if (!matchId || !isBadmintonOrTT()) return;
    
    // Store current scroll position to prevent jumping to top
    const scrollPosition = window.scrollY;
    
    setIsSavingSet(true);
    try {
      const sport = getSportValue();
      
      // Priority: Use editingSet data if user has made changes, otherwise use set from sets array
      const setToUse = editingSet && editingSet.setNumber === setNumber 
        ? editingSet 
        : sets.find(s => s.setNumber === setNumber);
      
      if (setToUse) {
        // Use the data from editingSet (if exists) or from sets array
        const side1Points = setToUse.side1Points;
        const side2Points = setToUse.side2Points;
        
        // Determine winner based on points - handle Draw case (0-0)
        // For Draw (0-0), winner remains null
        const isDraw = side1Points === 0 && side2Points === 0;
        const winner = isDraw ? null : (side1Points > side2Points ? 'side1' : 
                      side2Points > side1Points ? 'side2' : null);
        
        // Call updateSet API with the user's entered data (same as handleSaveSet)
        await updateSet(matchId, sport, {
          setNumber: setNumber,
          side1Points: side1Points,
          side2Points: side2Points,
          status: 'completed'
        });
      }
      // Store the saved state for this set to track changes
      const setToUseForSave = editingSet && editingSet.setNumber === setNumber 
        ? editingSet 
        : sets.find(s => s.setNumber === setNumber);
      if (setToUseForSave) {
        setLastSavedSetData(prev => {
          const newMap = new Map(prev);
          newMap.set(setNumber, {
            side1Points: setToUseForSave.side1Points || 0,
            side2Points: setToUseForSave.side2Points || 0
          });
          return newMap;
        });
      }
      
      // Clear editing state when marking set as complete
      setEditingSet(null);
      await loadSets();
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Restore scroll position immediately to prevent jumping to top
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      });
      
      // Close modal after successful completion
      setShowMarkCompleteModal(false);
      setSetToMarkComplete(null);
      // Score will be recalculated automatically via useEffect when sets update
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to mark set as complete'));
      // Restore scroll position even on error
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    } finally {
      setIsSavingSet(false);
    }
  };
  
  const handleConfirmMarkComplete = async () => {
    if (setToMarkComplete) {
      await handleMarkSetComplete(setToMarkComplete);
    }
  };
  
  const handleDeleteSet = async () => {
    if (!matchId || !setToDelete || !isBadmintonOrTT()) return;
    
    setIsSavingSet(true);
    try {
      const sport = getSportValue();
      await deleteSet(matchId, sport, setToDelete);
      await loadSets();
      const fixtureRes = await getFixtureById(matchId, sport);
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      setSetToDelete(null);
      setEditingSet(null); // Clear any editing state when deleting
      // Score will be recalculated automatically via useEffect when sets update
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to delete set'));
    } finally {
      setIsSavingSet(false);
    }
  };

  // Handle action (walkover or abandon) submission
  const handleActionSubmit = async () => {
    if (!matchId || !actionReason.trim()) {
      setFormError(`Please provide a reason for ${actionModalType === 'walkover' ? 'walkover' : 'abandoning the match'}`);
      return;
    }
    
    // For walkover, also need team selection
    if (actionModalType === 'walkover' && !walkoverTeam) {
      setFormError('Please select a team for walkover');
      return;
    }
    
    setIsProcessingAction(true);
    setFormError(null);
    
    try {
      const sport = getSportValue();
      
      if (actionModalType === 'walkover') {
        // Get team ID based on selection - support both new (team1Info/team2Info) and old (teamA/teamB) structures
        const team1Id = (fixture as any)?.team1Info?._id || fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id;
        const team2Id = (fixture as any)?.team2Info?._id || fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id;
        const teamId = walkoverTeam === 'teamA' ? team1Id : team2Id;
        
        if (!teamId) {
          setFormError('Team ID not found');
          setIsProcessingAction(false);
          return;
        }
        
        await declareWalkover(matchId, teamId, actionReason.trim(), sport);
      } else {
        await abandonMatch(matchId, actionReason.trim(), sport);
      }
      
      // Close modal and reset form
      setShowActionModal(false);
      setWalkoverTeam('');
      setActionReason('');
      // Refresh fixture data
      const fixtureRes = await getFixtureById(matchId, sport); // FIX: Pass sport parameter
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      
      // Extract and update match status from refreshed fixture
      const rawStatus = (fixtureData as any)?.status || 
                       (fixtureData as any)?.streamStatus || 
                       (fixtureData as any)?.liveStreamInfo?.status || 
                       (fixtureData as any)?.matchStatus ||
                       (actionModalType === 'abandon' ? 'cancelled' : 'completed');
      
      // Normalize status values to match our expected values
      let normalizedStatus = (rawStatus || '').toLowerCase().trim();
      
      // Map various status values to standard ones
      if (normalizedStatus === 'yet-to-start' || normalizedStatus === 'yet_to_start' || normalizedStatus === 'pending' || normalizedStatus === 'not-started' || normalizedStatus === 'not started') {
        normalizedStatus = 'upcoming';
      } else if (normalizedStatus === 'ongoing' || normalizedStatus === 'live' || normalizedStatus === 'in-progress' || normalizedStatus === 'in_progress' || normalizedStatus === 'started' || normalizedStatus === 'start' || normalizedStatus === 'running') {
        normalizedStatus = 'ongoing';
      } else if (normalizedStatus === 'paused' || normalizedStatus === 'on-hold' || normalizedStatus === 'on_hold' || normalizedStatus === 'hold') {
        normalizedStatus = 'paused';
      } else if (normalizedStatus === 'completed' || normalizedStatus === 'ended' || normalizedStatus === 'finished' || normalizedStatus === 'complete' || normalizedStatus === 'walkover') {
        normalizedStatus = 'completed';
      } else if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
        normalizedStatus = 'cancelled';
      } else {
        // Default based on action type
        normalizedStatus = actionModalType === 'abandon' ? 'cancelled' : 'completed';
      }
      
      // Update match status
      setMatchStatus(normalizedStatus);
      
      // Load live scores to get updated data
      if (matchId) {
        try {
          await loadLiveScores(matchId);
        } catch (scoreError) {
          console.error(`Error loading live scores after ${actionModalType}:`, scoreError);
          // Don't fail the action if score loading fails
        }
      }
      
      // Reload tournament details to reflect changes
      if (tournamentId) {
        // Get sport from location state, URL search params, or existing tournament state
        // Sport is REQUIRED for tournament management module
        const selectedTournamentFromState = (location.state as any)?.selectedTournament;
        const sportFromState = selectedTournamentFromState?.sports;
        const urlParams = new URLSearchParams(location.search);
        const sportFromUrl = urlParams.get('sport');
        const sport = sportFromState || sportFromUrl || tournament?.sports;
        
        if (!sport) {
          console.error('Sport parameter is required but not available. Cannot reload tournament details.');
          return;
        }
        
        const tournamentData = await getTournamentById(tournamentId, sport);
        setTournament(tournamentData);
      }
    } catch (err: any) {
      setFormError(extractErrorMessage(err, `Failed to ${actionModalType === 'walkover' ? 'declare walkover' : 'abandon match'}`));
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Handle action modal close
  const handleCloseActionModal = () => {
    setShowActionModal(false);
    setWalkoverTeam('');
    setActionReason('');
    setFormError(null);
  };

  // Open walkover modal
  const handleOpenWalkoverModal = () => {
    setActionModalType('walkover');
    setShowActionModal(true);
  };

  // Open abandon match modal
  const handleOpenAbandonModal = () => {
    setActionModalType('abandon');
    setShowActionModal(true);
  };

  const handleUpdateScore = async () => {
    setIsUpdatingScore(true);
    try {
      // Get sport value for API calls
      const sport = getSportValue();
      // TODO: Call API to update scores
      // await updateMatchScore(matchId!, { teamAScore, teamBScore });
      // Refresh fixture data
      const fixtureRes = await getFixtureById(matchId!, sport); // FIX: Pass sport parameter
      const fixtureData = fixtureRes?.data || fixtureRes;
      setFixture(fixtureData as Fixture);
      setFormError(null);
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to update scores'));
    } finally {
      setIsUpdatingScore(false);
    }
  };

  // Handle adding a new score update
  const handleAddScoreUpdate = async () => {
    if (!selectedTeam || !eventType || !eventTime || !matchId) {
      setFormError('Please fill all required fields: Team, Event Type, and Time');
      return;
    }
    
    setIsAddingScore(true);
    setFormError(null);
    
    try {
      // Convert time from MM:SS to seconds
      const timeParts = eventTime.split(':');
      const timeInSeconds = parseInt(timeParts[0], 10) * 60 + parseInt(timeParts[1] || '0', 10);
      
      // Get team ID - support both new (team1Info/team2Info) and old (teamA/teamB) structures
      const team1Id = (fixture as any)?.team1Info?._id || fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id;
      const team2Id = (fixture as any)?.team2Info?._id || fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id;
      const teamId = selectedTeam === 'teamA' ? team1Id : team2Id;
      
      if (!teamId) {
        setFormError('Team ID not found');
        setIsAddingScore(false);
        return;
      }
      
      // Prepare payload according to API spec
      // Player is optional - only include if selected
      const payload = {
        events: {
          type: eventType === 'selfGoal' ? 'selfGoal' : eventType,
          time: timeInSeconds,
          player: selectedPlayer ? (selectedPlayer.value || selectedPlayer._id) : null,
          team: teamId
        }
      };
      
      const sport = getSportValue();
      await addLiveScore(matchId, payload, sport);
      
      // Reload live scores to get updated list
      await loadLiveScores(matchId);
      
      // Reset form
      setSelectedPlayer(null);
      setEventType('');
      setEventTime('');
      setFormError(null);
      setShowAddScoreForm(false);
    } catch (error: any) {
      setFormError(extractErrorMessage(error, 'Failed to add score event'));
    } finally {
      setIsAddingScore(false);
    }
  };

  // Handle removing a score update
  const handleRemoveScoreUpdate = (updateId: string) => {
    const update = scoreUpdates.find(u => u.id === updateId);
    if (!update) return;
    
    // Revert score change
    if (update.eventType === 'goal' || update.eventType === 'penalty') {
      if (update.team === 'teamA') {
        setTeamAScore(Math.max(0, teamAScore - 1));
      } else {
        setTeamBScore(Math.max(0, teamBScore - 1));
      }
    } else if (update.eventType === 'selfGoal') {
      if (update.team === 'teamA') {
        setTeamBScore(Math.max(0, teamBScore - 1));
      } else {
        setTeamAScore(Math.max(0, teamAScore - 1));
      }
    }
    
    setScoreUpdates(scoreUpdates.filter(u => u.id !== updateId));
  };

  // Get available players based on selected team - memoized to prevent excessive renders
  const getAvailablePlayers = useMemo((): Array<{ value: string; label: string; isDisabled?: boolean }> => {
    if (!selectedTeam) {
      return [{ value: '', label: 'Select a team first', isDisabled: true }];
    }
    
    if (loadingLiveScores) {
      return [{ value: '', label: 'Loading players...', isDisabled: true }];
    }
    
    let players: any[] = [];
    if (selectedTeam === 'teamA') {
      players = teamAPlayers || [];
    } else if (selectedTeam === 'teamB') {
      players = teamBPlayers || [];
    }
    
    if (players.length === 0) {
      return [{ value: '', label: 'No players available', isDisabled: true }];
    }
    
    const options = players
      .filter((p: any) => p && (p._id || p.id))
      .map((p: any) => ({
        value: p._id || p.id || '',
        label: p.name || p.playerName || 'Unknown Player'
      }));
    
    if (options.length === 0) {
      return [{ value: '', label: 'No players available', isDisabled: true }];
    }
    
    return options;
  }, [selectedTeam, teamAPlayers, teamBPlayers, loadingLiveScores]);

  // Track live scores loading to prevent duplicate calls
  const liveScoresLoadingRef = useRef<{ [key: string]: boolean }>({});
  // Track polling interval for live scores
  const liveScoresPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Load live scores (events) from API and fetch teams/players
  const loadLiveScores = useCallback(async (matchIdParam: string, forceReload: boolean = false) => {
    if (!matchIdParam) return;
    if (!isLiveScoringSupportedSport(getSportValue())) return;
    
    // Prevent duplicate calls for the same match, unless forceReload is true
    if (!forceReload && liveScoresLoadingRef.current[matchIdParam]) {
      return;
    }
    
    liveScoresLoadingRef.current[matchIdParam] = true;
    setLoadingLiveScores(true);
    try {
      const sport = getSportValue();
      const response = await fetchLiveScores(matchIdParam, sport);
      
      // Handle different response structures
      // Structure 1: { statusCode: 200, status: "success", message: "...", data: { events: [], playerList: [], teamAInfo: {}, teamBInfo: {}, ... } }
      // Structure 2: { data: { events: [], ... } }
      // Structure 3: { events: [], ... }
      let liveScoreData = null;
      
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          if (response.data.playerList || response.data.events || response.data.teamAInfo || response.data.teamBInfo) {
            liveScoreData = response.data;
          } else if (response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
            liveScoreData = response.data.data;
          } else {
            liveScoreData = response.data;
          }
        } else if (response.playerList || response.events || response.teamAInfo || response.teamBInfo) {
          liveScoreData = response;
        } else {
          liveScoreData = response;
        }
      }
      
      setLiveScoreData(liveScoreData);
      
      // Extract events array from response
      // Only show events that actually exist (filter out null/undefined/empty events)
      // Note: player is optional, so we don't require it in the filter
      const events = liveScoreData?.events || [];
      const validEvents = Array.isArray(events) 
        ? events.filter((event: any) => event && event._id && event.type && event.team)
        : [];
      setLiveScoreEvents(validEvents);
      
      if (isBadmintonOrTT() && liveScoreData) {
        const liveScoreSets = Array.isArray(liveScoreData?.sets) ? liveScoreData.sets : [];
        
        if (liveScoreSets.length > 0) {
          const needsUpdate = sets.length === 0 || liveScoreSets.length > sets.length;
          const currentSetNumbers = new Set<number>(sets.map(s => s.setNumber));
          const liveScoreSetNumbersArray = liveScoreSets.map((s: any) => s.setNumber).filter((num: any): num is number => typeof num === 'number');
          const liveScoreSetNumbers = new Set<number>(liveScoreSetNumbersArray);
          const hasDifferentSets = currentSetNumbers.size !== liveScoreSetNumbers.size || 
            !liveScoreSetNumbersArray.every((num: number) => currentSetNumbers.has(num));
          
          if (needsUpdate || hasDifferentSets) {
            setSets(liveScoreSets);
            setsExtractedRef.current = true;
            setLastSavedSetData(prev => {
              const newMap = new Map(prev);
              liveScoreSets.forEach((set: SetData) => {
                if (set.setNumber) {
                  newMap.set(set.setNumber, {
                    side1Points: set.side1Points || 0,
                    side2Points: set.side2Points || 0
                  });
                }
              });
              return newMap;
            });
          }
        }
      }
      
      // Update team scores from API response
      if (liveScoreData?.score) {
        setTeamAScore(liveScoreData.score.teamAGoals || 0);
        setTeamBScore(liveScoreData.score.teamBGoals || 0);
      }
      
      // CRITICAL: Update timer from live scores API response
      // The live scores API returns the most up-to-date timer value
      // IMPORTANT: Only sync timer when paused or when there's a very large discrepancy
      // Don't sync every 10 seconds when timer is running smoothly to avoid jumps
      if (liveScoreData?.timer !== undefined && liveScoreData?.timer !== null) {
        const liveTimerValue = typeof liveScoreData.timer === 'number' 
          ? liveScoreData.timer 
          : parseFloat(String(liveScoreData.timer)) || 0;
        const roundedLiveTimer = Math.floor(liveTimerValue);
        
        // Only update timer if match is ongoing or paused
        // Don't update if match is completed or upcoming
        if (matchStatus === 'ongoing' || matchStatus === 'paused') {
          // Use accumulatedTimeRef.current instead of matchTimer state to avoid dependency issues
          const currentTimer = accumulatedTimeRef.current || 0;
          const timerDifference = Math.abs(roundedLiveTimer - currentTimer);
          
          // For paused matches: Always sync to get accurate paused timer value
          // For ongoing matches: Only sync if:
          //   1. Timer hasn't been initialized yet (first load)
          //   2. There's a very large discrepancy (5+ seconds) indicating backend was manually adjusted
          // This prevents the timer from jumping every 10 seconds during normal operation
          const shouldSync = matchStatus === 'paused' || 
                            !timerInitializedRef.current || 
                            (matchStatus === 'ongoing' && timerDifference > 5);
          
          if (shouldSync) {
            const cappedTimer = roundedLiveTimer >= 5999 ? 5999 : roundedLiveTimer;
            
            // Update accumulated time ref (this is the base time)
            accumulatedTimeRef.current = cappedTimer;
            setAccumulatedTime(cappedTimer);
            setMatchTimer(cappedTimer);
            
            // If timer is already at max, stop it
            if (cappedTimer >= 5999 && isTimerRunning) {
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              setIsTimerRunning(false);
              setTimerStartTime(null);
              timerStartTimeRef.current = null;
            }
            
            // If match is ongoing and timer is running, reset the start time to sync properly
            // This ensures the timer continues counting from the synced backend value
            if (matchStatus === 'ongoing' && isTimerRunning) {
              const newStartTime = Date.now();
              setTimerStartTime(newStartTime);
              timerStartTimeRef.current = newStartTime;
              
              // Only restart interval if it doesn't exist or if there was a large discrepancy
              if (!timerIntervalRef.current || timerDifference > 5) {
                // Clear existing interval first
                if (timerIntervalRef.current) {
                  clearInterval(timerIntervalRef.current);
                  timerIntervalRef.current = null;
                }
                
                // Start new interval with synced base time
                timerIntervalRef.current = setInterval(() => {
                  setMatchTimer(() => {
                    const now = Date.now();
                    const start = timerStartTimeRef.current || newStartTime;
                    const elapsed = Math.floor((now - start) / 1000);
                    const baseTime = accumulatedTimeRef.current;
                    const newTime = baseTime + elapsed;
                    const finalTime = Math.floor(newTime);
                    
                    // Stop timer at 99:59 (5999 seconds)
                    if (finalTime >= 5999) {
                      // Clear the interval to stop the timer
                      if (timerIntervalRef.current) {
                        clearInterval(timerIntervalRef.current);
                        timerIntervalRef.current = null;
                      }
                      // Cap accumulated time at 5999
                      accumulatedTimeRef.current = 5999;
                      setAccumulatedTime(5999);
                      setIsTimerRunning(false);
                      setTimerStartTime(null);
                      timerStartTimeRef.current = null;
                      return 5999; // Cap at 99:59
                    }
                    
                    return finalTime;
                  });
                }, 1000);
              }
            } else if (matchStatus === 'paused') {
              // If paused, just update the timer value without starting interval
              if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
              }
              setIsTimerRunning(false);
              setTimerStartTime(null);
              timerStartTimeRef.current = null;
            }
          }
        }
      }
      
      // Extract playerList from response and populate team players
      // This is the primary source for players when adding scores
      if (liveScoreData?.playerList && Array.isArray(liveScoreData.playerList)) {
        // Get team IDs - handle multiple possible structures (new: team1Info/team2Info, old: teamA/teamB, liveScoreData: teamAInfo/teamBInfo)
        const teamAId = liveScoreData?.team1Info?._id || liveScoreData?.teamAInfo?._id || (fixture as any)?.team1Info?._id || fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id;
        const teamBId = liveScoreData?.team2Info?._id || liveScoreData?.teamBInfo?._id || (fixture as any)?.team2Info?._id || fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id;
        
        // Filter players by team
        // The player.team field can be either a string ID or an object with _id
        const teamAPlayersList = teamAId 
          ? liveScoreData.playerList.filter((p: any) => {
              // Handle different structures: p.team can be string, object with _id, or nested
              const playerTeamId = p.team?._id || p.team || p.teamId;
              // Compare both as strings to handle type mismatches
              const teamAIdStr = String(teamAId);
              const playerTeamIdStr = String(playerTeamId);
              const matches = playerTeamIdStr === teamAIdStr;
              
              return matches;
            })
          : [];
        const teamBPlayersList = teamBId 
          ? liveScoreData.playerList.filter((p: any) => {
              // Handle different structures: p.team can be string, object with _id, or nested
              const playerTeamId = p.team?._id || p.team || p.teamId;
              // Compare both as strings to handle type mismatches
              const teamBIdStr = String(teamBId);
              const playerTeamIdStr = String(playerTeamId);
              const matches = playerTeamIdStr === teamBIdStr;
              
              return matches;
            })
          : [];
        
        setTeamAPlayers(Array.isArray(teamAPlayersList) ? teamAPlayersList : []);
        setTeamBPlayers(Array.isArray(teamBPlayersList) ? teamBPlayersList : []);
        
        // Extract Player of the Match data from response
        if (liveScoreData.playerOfTheMatch) {
          const normalizedPlayerOfMatch = normalizePlayerOfMatch(liveScoreData.playerOfTheMatch);
          setPlayerOfTheMatch({
            player: normalizedPlayerOfMatch.player,
            team: normalizedPlayerOfMatch.team
          });
        } else {
          // Clear if not present in response
          setPlayerOfTheMatch({ player: null, team: null });
        }
        if (liveScoreData.secondPlayerOfTheMatch) {
          const normalizedSecondPlayerOfMatch = normalizePlayerOfMatch(liveScoreData.secondPlayerOfTheMatch);
          setSecondPlayerOfTheMatch({
            player: normalizedSecondPlayerOfMatch.player,
            team: normalizedSecondPlayerOfMatch.team
          });
        } else {
          // Clear if not present in response
          setSecondPlayerOfTheMatch({ player: null, team: null });
        }
      } else {
        // If no playerList in response, clear players
        setTeamAPlayers([]);
        setTeamBPlayers([]);
      }
    } catch (error: any) {
      console.error('Error loading live scores:', error);
      setLiveScoreEvents([]);
      // Don't clear players on error - keep existing players if available
      // Only clear if we explicitly got an error that indicates no data
      if (error?.response?.status === 404 || error?.response?.status === 400) {
        // Match not found or invalid - clear players
        setTeamAPlayers([]);
        setTeamBPlayers([]);
      }
    } finally {
      setLoadingLiveScores(false);
      liveScoresLoadingRef.current[matchIdParam] = false;
    }
  }, [fixture, matchStatus, isTimerRunning]);
  
  // Load live scores when match is loaded and match has started or ended
  // Use polling for ongoing/paused matches to keep data fresh, but with reasonable interval
  useEffect(() => {
    // Clear any existing polling interval first
    if (liveScoresPollingIntervalRef.current) {
      clearInterval(liveScoresPollingIntervalRef.current);
      liveScoresPollingIntervalRef.current = null;
    }
    
    if (isLiveScoringSupported && matchId && fixture?._id && (matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended')) {
      // Load immediately once when match status changes
      if (!liveScoresLoadingRef.current[matchId]) {
        // Add a small delay to ensure backend has processed any status changes
        const timeoutId = setTimeout(() => {
          loadLiveScores(matchId, false);
        }, 300);
        
        // Set up polling for ongoing/paused matches (every 10 seconds)
        // This keeps the data fresh without overwhelming the server
        if (matchStatus === 'ongoing' || matchStatus === 'paused') {
          liveScoresPollingIntervalRef.current = setInterval(() => {
            // Only poll if not already loading
            if (!liveScoresLoadingRef.current[matchId]) {
              loadLiveScores(matchId, false);
            }
          }, 10000); // Poll every 10 seconds instead of every second
        }
        
        return () => {
          clearTimeout(timeoutId);
          if (liveScoresPollingIntervalRef.current) {
            clearInterval(liveScoresPollingIntervalRef.current);
            liveScoresPollingIntervalRef.current = null;
          }
        };
      }
    } else {
      // Clear live score events if match hasn't started
      setLiveScoreEvents([]);
      setShowAddScoreForm(false);
      // Don't clear players - they might be needed if match status changes back
    }
  }, [matchId, fixture?._id, matchStatus, loadLiveScores, isLiveScoringSupported]);
  
  // Cleanup polling interval on component unmount
  useEffect(() => {
    return () => {
      if (liveScoresPollingIntervalRef.current) {
        clearInterval(liveScoresPollingIntervalRef.current);
        liveScoresPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle edit event
  const handleEditEvent = async (event: any) => {
    setEditingEvent(event);
    setFormError(null);
    
    // Load players from fetch-live-scores API to ensure we have the latest player list
    // This ensures players are available when editing, even if they weren't loaded before
    let loadedPlayers: { teamAPlayers: any[]; teamBPlayers: any[] } = { teamAPlayers: [], teamBPlayers: [] };
    
    if (matchId) {
      try {
        // Call the API directly to get players immediately
        const sport = getSportValue();
        const response = await fetchLiveScores(matchId, sport);
        
        // Parse response to get playerList
        let liveScoreData = null;
        if (response && typeof response === 'object' && !Array.isArray(response)) {
          if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
            if (response.data.playerList || response.data.events || response.data.teamAInfo || response.data.teamBInfo) {
              liveScoreData = response.data;
            } else if (response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
              liveScoreData = response.data.data;
            } else {
              liveScoreData = response.data;
            }
          } else {
            liveScoreData = response;
          }
        }
        
        // Extract players from response
        if (liveScoreData?.playerList && Array.isArray(liveScoreData.playerList)) {
          const teamAId = liveScoreData?.teamAInfo?._id || fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id;
          const teamBId = liveScoreData?.teamBInfo?._id || fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id;
          
          loadedPlayers.teamAPlayers = teamAId 
            ? liveScoreData.playerList.filter((p: any) => {
                const playerTeamId = p.team?._id || p.team || p.teamId;
                return String(playerTeamId) === String(teamAId);
              })
            : [];
          loadedPlayers.teamBPlayers = teamBId 
            ? liveScoreData.playerList.filter((p: any) => {
                const playerTeamId = p.team?._id || p.team || p.teamId;
                return String(playerTeamId) === String(teamBId);
              })
            : [];
          
          // Update state with loaded players
          setTeamAPlayers(loadedPlayers.teamAPlayers);
          setTeamBPlayers(loadedPlayers.teamBPlayers);
        }
      } catch (error) {
        console.error('Error loading players for edit:', error);
        // Fall back to existing players if API call fails
        loadedPlayers.teamAPlayers = teamAPlayers;
        loadedPlayers.teamBPlayers = teamBPlayers;
      }
    } else {
      // Use existing players if no matchId
      loadedPlayers.teamAPlayers = teamAPlayers;
      loadedPlayers.teamBPlayers = teamBPlayers;
    }
    
    // Set form values for editing
    const teamId = event.team;
    const isTeamA = fixture?.teamA?._id === teamId || (fixture as any)?.teamAInfo?._id === teamId;
    setSelectedTeam(isTeamA ? 'teamA' : 'teamB');
    
    // Find and set player from loaded players
    const players = isTeamA ? loadedPlayers.teamAPlayers : loadedPlayers.teamBPlayers;
    const player = players.find((p: any) => {
      const playerId = p._id || p.id;
      const eventPlayerId = event.player;
      return String(playerId) === String(eventPlayerId);
    });
    
    if (player) {
      setSelectedPlayer({ value: player._id || player.id, label: player.name || player.playerName });
    } else {
      // If player not found in loaded list, try to find it from existing state
      const existingPlayers = isTeamA ? teamAPlayers : teamBPlayers;
      const existingPlayer = existingPlayers.find((p: any) => {
        const playerId = p._id || p.id;
        const eventPlayerId = event.player;
        return String(playerId) === String(eventPlayerId);
      });
      
      if (existingPlayer) {
        setSelectedPlayer({ value: existingPlayer._id || existingPlayer.id, label: existingPlayer.name || existingPlayer.playerName });
      } else {
        // Fallback: set player ID from event (player might not be in list but we can still save)
        setSelectedPlayer({ value: event.player, label: 'Player' });
      }
    }
    
    // Set event type (convert API type to form type)
    let formEventType = event.type;
    if (event.type === 'penalty') formEventType = 'penalty';
    else if (event.type === 'selfGoal' || event.type === 'self-goal') formEventType = 'selfGoal';
    else formEventType = 'goal';
    setEventType(formEventType);
    
    // Set time (convert seconds to MM:SS format)
    const timeInSeconds = event.time || 0;
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    setEventTime(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    
    // Reset form change tracking when starting edit
    setHasFormChanged(false);
  };

  // Handle save edited event
  const handleSaveEditedEvent = async () => {
    if (!editingEvent || !eventType || !eventTime || !selectedTeam) {
      setFormError('Please fill all required fields: Team, Event Type, and Time');
      return;
    }
    
    try {
      setIsAddingScore(true);
      setFormError(null);
      
      // Convert time from MM:SS to seconds
      const timeParts = eventTime.split(':');
      if (timeParts.length !== 2) {
        setFormError('Please enter time in MM:SS format (e.g., 45:30)');
        setIsAddingScore(false);
        return;
      }
      const timeInSeconds = parseInt(timeParts[0], 10) * 60 + parseInt(timeParts[1] || '0', 10);
      
      if (isNaN(timeInSeconds) || timeInSeconds < 0) {
        setFormError('Please enter a valid time');
        setIsAddingScore(false);
        return;
      }
      
      // Get team ID - handle multiple possible structures
      const teamId = selectedTeam === 'teamA' 
        ? (fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id)
        : (fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id);
      
      if (!teamId) {
        setFormError('Team ID not found. Please try again.');
        setIsAddingScore(false);
        return;
      }
      
      // Prepare payload according to API spec
      // Player is optional - only include if selected
      const payload = {
        type: eventType === 'selfGoal' ? 'selfGoal' : eventType,
        time: timeInSeconds,
        player: selectedPlayer ? (selectedPlayer.value || selectedPlayer._id) : null,
        team: teamId,
        _id: editingEvent._id
      };
      
      // Ensure event ID is in payload
      if (!editingEvent._id) {
        setFormError('Event ID not found. Cannot update event.');
        setIsAddingScore(false);
        return;
      }
      
      // Ensure matchId is available
      if (!matchId) {
        setFormError('Match ID not found. Cannot update event.');
        setIsAddingScore(false);
        return;
      }
      
      // Ensure _id is in payload (required by API)
      const finalPayload = {
        ...payload,
        _id: editingEvent._id
      };
      
      const sport = getSportValue();
      await updateLiveScore(matchId, finalPayload, sport);
      
      if (matchId) {
        await loadLiveScores(matchId, true);
      }
      
      setEditingEvent(null);
      setSelectedPlayer(null);
      setEventType('');
      setEventTime('');
      setFormError(null);
      setHasFormChanged(false);
    } catch (error: any) {
      console.error('Error updating score event:', error);
      setFormError(extractErrorMessage(error, 'Failed to update score event. Please try again.'));
    } finally {
      setIsAddingScore(false);
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!eventToDelete || !matchId) return;
    
    try {
      const sport = getSportValue();
      await deleteLiveScoreEvent(matchId, eventToDelete._id, sport);
      
      // Reload live scores
      await loadLiveScores(matchId);
      
      setShowDeleteModal(false);
      setEventToDelete(null);
    } catch (error: any) {
      setFormError(extractErrorMessage(error, 'Failed to delete event'));
    }
  };

  // Helper function to format time to 12-hour format with AM/PM
  const formatTimeTo12Hour = (time: string | undefined): string => {
    if (!time) return '-';
    
    try {
      // Handle ISO datetime string (e.g., "2025-01-15T14:30:00.000Z")
      if (time.includes('T') && time.includes('Z')) {
        const date = new Date(time);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      }
      
      // Handle 24-hour format (e.g., "14:30" or "14:30:00")
      if (time.includes(':') && !time.includes('AM') && !time.includes('PM')) {
        const parts = time.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1] || '0', 10);
        if (!isNaN(hours) && !isNaN(minutes)) {
          const ampm = hours >= 12 ? 'PM' : 'AM';
          const hours12 = hours % 12 || 12;
          return `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
        }
      }
      
      // Already in 12-hour format or return as-is
      return time;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  // Normalize status to match match management listing
  const normalizeStatus = (status?: string) => {
    if (!status) return 'pending';
    const s = status.toLowerCase().trim();
    if (!s) return 'pending';
    
    // Map to the required status values - prioritize exact backend values
    // Backend values: PENDING = 'pending', STARTED = 'started', PAUSED = 'paused', CANCELLED = 'cancelled', COMPLETED = 'completed'
    if (s === 'pending') return 'pending';
    if (s === 'started') return 'started';
    if (s === 'paused') return 'paused';
    if (s === 'cancelled' || s === 'canceled') return 'cancelled';
    if (s === 'completed') return 'completed';
    
    // Fallback mappings for legacy/alternative values
    if (s === 'upcoming' || (s.includes('yet') && s.includes('start'))) return 'pending';
    if (s === 'start' || s === 'live' || s === 'ongoing' || s === 'running') return 'started';
    if (s === 'on-hold' || s === 'on_hold' || s === 'hold') return 'paused';
    if (s === 'cancel') return 'cancelled';
    if (s === 'ended' || s === 'complete' || s === 'finished') return 'completed';
    
    // Default to pending for unknown statuses
    return 'pending';
  };

  const getStatusBadge = (status: string) => {
    const normalized = normalizeStatus(status);
    
    // Map fixture statuses to match manage-tournaments-fixtures styling
    if (normalized === 'started') {
      return (
        <span 
          className="badge payment-badge" 
          style={{
            border: '1px solid',
            fontWeight: '500',
            padding: '0.375rem 0.75rem',
            borderRadius: '6px',
            backgroundColor: '#E6F1FF',
            color: '#006EFF',
            borderColor: '#006EFF',
            textTransform: 'capitalize'
          }}
        >
          live
        </span>
      );
    }
    
    if (normalized === 'paused') {
      return (
        <span 
          className="badge payment-badge" 
          style={{
            border: '1px solid',
            fontWeight: '500',
            padding: '0.375rem 0.75rem',
            borderRadius: '6px',
            backgroundColor: '#FFF5EC',
            color: '#FD9644',
            borderColor: '#FD9644',
            textTransform: 'capitalize'
          }}
        >
          paused
        </span>
      );
    }
    
    if (normalized === 'cancelled') {
      return (
        <span 
          className="badge payment-badge" 
          style={{
            border: '1px solid',
            fontWeight: '500',
            padding: '0.375rem 0.75rem',
            borderRadius: '6px',
            backgroundColor: '#FEE',
            color: '#DC3545',
            borderColor: '#DC3545',
            textTransform: 'capitalize'
          }}
        >
          cancelled
        </span>
      );
    }
    
    if (normalized === 'completed') {
      return (
        <span 
          className="badge payment-badge" 
          style={{
            border: '1px solid',
            fontWeight: '500',
            padding: '0.375rem 0.75rem',
            borderRadius: '6px',
            backgroundColor: '#E9FCF2',
            color: '#1B9E5C',
            borderColor: '#1B9E5C',
            textTransform: 'capitalize'
          }}
        >
          completed
        </span>
      );
    }
    
    // Default: pending/upcoming
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#FFF5EC',
          color: '#FD9644',
          borderColor: '#FD9644',
          textTransform: 'capitalize'
        }}
      >
        upcoming
      </span>
    );
  };

  // Build breadcrumb items with actual tournament and season names
  const refreshMatchArtwork = async () => {
    if (!matchId) return;
    const sport = fixture?.sports || getSportValue();
    const response = await getFixtureById(matchId, sport);
    setFixture((response?.data || response) as Fixture);
  };

  const breadcrumbItems = [
    { 
      label: 'Tournament Registrations',
      onClick: () => navigate('/manage-tournaments-fixtures', {
        state: {
          allTournaments: (location.state as any)?.allTournaments
        }
      })
    },
    { 
      label: tournament?.name || 'Tournament',
      onClick: () => {
        if (tournamentSeasonId && !fromTournamentDetailPage) {
          // Navigate to seasons list
          navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons`, {
            state: {
              selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
              allTournaments: (location.state as any)?.allTournaments
            }
          });
        } else {
          // Navigate to tournament detail page
          navigate(`/manage-tournaments-fixtures/${tournamentId}`, {
            state: {
              selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
              allTournaments: (location.state as any)?.allTournaments
            }
          });
        }
      }
    },
    ...(tournamentSeasonId && !fromTournamentDetailPage ? [
      { 
        label: season?.seasonLabel || 'Season',
        onClick: () => navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, {
          state: {
            activeTab: 'match-management',
            selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
            allTournaments: (location.state as any)?.allTournaments
          }
        })
      }
    ] : []),
    { label: 'Match Details', active: true }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {/* Match Header */}
        <div style={{ padding: '0 15px', marginBottom: '12px' }}>
          <div className="page-header-section mb-4" style={{ position: 'relative', zIndex: 10 }}>
            <div className="d-flex align-items-center mb-4" style={{ gap: '16px', position: 'relative', zIndex: 10, marginBottom: '24px !important', justifyContent: 'flex-start', width: '100%' }}>
              <button 
                className="back-button"
                onClick={() => {
                  // If from tournament detail page (default season), navigate back to tournament detail page
                  // Otherwise, navigate to season detail page if tournamentSeasonId exists
                  if (fromTournamentDetailPage || !tournamentSeasonId) {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}`, { 
                      state: { 
                        activeTab: 'match-management',
                        selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
                        allTournaments: (location.state as any)?.allTournaments
                      } 
                    });
                  } else {
                    navigate(`/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}`, { 
                      state: { 
                        activeTab: 'match-management',
                        selectedTournament: tournament ? { sports: tournament.sports } : (location.state as any)?.selectedTournament,
                        allTournaments: (location.state as any)?.allTournaments
                      } 
                    });
                  }
                }}
                style={{ position: 'relative', zIndex: 10 }}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <h1 className="page-title mb-0" style={{ position: 'relative', zIndex: 10 }}>
                  Match {fixture?.matchNum || ''} - {getTeam1Name()} vs {getTeam2Name()}
                </h1>
                {fixture && getStatusBadge(matchStatus)}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <ShimmerLoader type="tournament-card" />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : fixture ? (
          <>
            {matchId && (fixture.sports || getSportValue()).replace(/[\s_-]/g, '').toLowerCase() !== 'football' && (
              <MatchArtworkSection
                matchId={matchId}
                sport={fixture.sports || getSportValue()}
                featuredImage={fixture.featuredImage}
                backgroundImage={fixture.template?.backgroundImage}
                onSaved={refreshMatchArtwork}
              />
            )}
            {/* Fixture Details Card - First Fold */}
            <Card className="mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fc', borderBottom: '1px solid #e0e0e0' }}>
                <h5 className="mb-0" style={{ fontWeight: 600 }}>Match</h5>
                <div className="d-flex gap-2">
                  {!showEditModal ? (
                    <>
                      {/* Walkover button - available at any time except when match is finalized */}
                      {!isMatchFinalized && (
                        <Button 
                          variant="warning" 
                          size="sm"
                          onClick={handleOpenWalkoverModal}
                          disabled={!canPerformMatchActions}
                          title={!canPerformMatchActions ? 'You do not have permission to declare walkover' : 'Declare Walkover'}
                        >
                          <i className="ri-flag-line me-1"></i>
                          Walkover
                        </Button>
                      )}
                      {/* Abandon Match button - available at any time except when match is finalized */}
                      {!isMatchFinalized && (
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={handleOpenAbandonModal}
                          disabled={!canPerformMatchActions}
                          title={!canPerformMatchActions ? 'You do not have permission to abandon match' : 'Abandon Match'}
                        >
                          <i className="ri-close-circle-line me-1"></i>
                          Abandon Match
                        </Button>
                      )}
                      {/* Edit button - hide when match is finalized */}
                      {!isMatchFinalized && (
                        <Button 
                          variant="dark" 
                          size="sm"
                          onClick={handleEdit}
                          disabled={isMatchCompletedOrEnded}
                          title={isMatchCompletedOrEnded ? 'Cannot edit fixture once match has completed' : 'Edit Fixture'}
                        >
                          <i className="ri-pencil-line me-1"></i>
                          Edit
                        </Button>
                      )}
                      {/* Delete button - hide when match is finalized */}
                      {!isMatchFinalized && (
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => setShowDeleteModal(true)}
                          disabled={!canEditFixture || isMatchLiveOrCompleted}
                          title={!canEditFixture || isMatchLiveOrCompleted ? 'Cannot delete fixture once match has started or completed' : 'Delete Fixture'}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Delete
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={handleCloseEdit}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleSave} 
                        disabled={isSaving}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                {formError && showEditModal && <div className="alert alert-danger mb-3">{formError}</div>}
                {(() => {
                  const validation = showEditModal ? validateForm() : { 
                    errors: {
                      matchNum: '',
                      time: '',
                      sports: '',
                      stage: '',
                      ageCategory: '',
                      group: '',
                      teamA: '',
                      teamB: '',
                      date: '',
                      venue: '',
                      venueLong: '',
                    }, 
                    valid: false 
                  };
                  const errors = validation.errors || {
                    matchNum: '',
                    time: '',
                    sports: '',
                    stage: '',
                    ageCategory: '',
                    group: '',
                    teamA: '',
                    teamB: '',
                    date: '',
                    venue: '',
                    venueLong: '',
                  };
                  
                  return (
                    <Row className="g-3">
                      {/* 1. Match Number */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Match No<span className="text-danger">*</span></Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            Match {fixture.matchNum}
                          </div>
                        ) : (
                          <>
                            <Form.Control
                              name="matchNum"
                              type="number"
                              value={formData.matchNum}
                              onChange={handleInputChange}
                              onWheel={handleWheel}
                              isInvalid={!!errors.matchNum && formTouched.matchNum}
                              onBlur={() => setFormTouched({ ...formTouched, matchNum: true })}
                            />
                            {(errors.matchNum && formTouched.matchNum) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.matchNum}</div>}
                          </>
                        )}
                      </Col>
                      {/* Optional match metadata */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Title</Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {fixture.title || '-'}
                          </div>
                        ) : (
                          <Form.Control
                            name="title"
                            type="text"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="Enter match title"
                          />
                        )}
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Subtitle</Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {fixture.subtitle || '-'}
                          </div>
                        ) : (
                          <Form.Control
                            name="subtitle"
                            type="text"
                            value={formData.subtitle}
                            onChange={handleInputChange}
                            placeholder="Enter match subtitle"
                          />
                        )}
                      </Col>
                      {/* 2. Age Category */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Age Category<span className="text-danger">*</span></Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {formData.ageCategory?.label || (typeof fixture.ageCategory === 'object' ? fixture.ageCategory?.ageCategory : fixture.ageCategory) || '-'}
                          </div>
                        ) : (
                          <>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.ageCategory}
                              onChange={(option) => handleSelectChange('ageCategory', option)}
                              onBlur={() => setFormTouched({ ...formTouched, ageCategory: true })}
                              options={ageCategoryOptions}
                              placeholder="Select Age Category"
                              isClearable={false}
                              styles={{ control: (base, state) => ({ 
                                ...base, 
                                minHeight: '38px',
                                borderColor: state.isFocused ? '#80bdff' : (errors.ageCategory && formTouched.ageCategory ? '#dc3545' : '#ced4da')
                              }) }}
                            />
                            {(errors.ageCategory && formTouched.ageCategory) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.ageCategory}</div>}
                          </>
                        )}
                      </Col>
                      {(fixture?.sports || formData.sports?.value || formData.sports?.label || '').replace(/[\s_-]/g, '').toLowerCase() === 'pickleball' && (
                        <Col md={3}>
                          <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Event Category<span className="text-danger">*</span></Form.Label>
                          {!showEditModal ? (
                            <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                              {formData.eventCategory?.label || (fixture as any)?.eventCategory || '-'}
                            </div>
                          ) : (
                            <>
                              <Select
                                classNamePrefix="react-select"
                                value={formData.eventCategory}
                                onChange={(option) => handleSelectChange('eventCategory', option)}
                                onBlur={() => setFormTouched({ ...formTouched, eventCategory: true })}
                                options={PICKLEBALL_EVENT_CATEGORIES}
                                placeholder="Select Event Category"
                                isClearable={false}
                                styles={{ control: (base, state) => ({
                                  ...base,
                                  minHeight: '38px',
                                  borderColor: state.isFocused ? '#80bdff' : (errors.eventCategory && formTouched.eventCategory ? '#dc3545' : '#ced4da')
                                }) }}
                              />
                              {(errors.eventCategory && formTouched.eventCategory) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.eventCategory}</div>}
                            </>
                          )}
                        </Col>
                      )}
                      {/* 3. Stage */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Stage</Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {fixture.stage ? fixture.stage.charAt(0).toUpperCase() + fixture.stage.slice(1) : '-'}
                          </div>
                        ) : (
                          <>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.stage}
                              onChange={(option) => handleSelectChange('stage', option)}
                              onBlur={() => setFormTouched({ ...formTouched, stage: true })}
                              options={[
                                { value: 'league', label: 'League' },
                                { value: 'knockout', label: 'Knockout' },
                              ]}
                              placeholder="Select Stage"
                              isClearable={true}
                              styles={{ control: (base, state) => ({ 
                                ...base, 
                                minHeight: '38px',
                                borderColor: state.isFocused ? '#80bdff' : (errors.stage && formTouched.stage ? '#dc3545' : '#ced4da')
                              }) }}
                            />
                            {(errors.stage && formTouched.stage) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.stage}</div>}
                          </>
                        )}
                      </Col>
                      {/* 4. Group/Round - Different display for Football vs Badminton/TT */}
                      {!isBadmintonOrTT() ? (
                        <>
                          {(fixture?.sports || formData.sports?.value || formData.sports?.label || '').replace(/[\s_-]/g, '').toLowerCase() === 'pickleball' && isPickleballDoubles(formData.eventCategory?.value || (fixture as any)?.eventCategory) && (
                            <Col md={3}>
                              <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Sets<span className="text-danger">*</span></Form.Label>
                              {!showEditModal ? (
                                <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                  {(fixture as any)?.setCount || '-'}
                                </div>
                              ) : (
                                <>
                                  <Form.Control
                                    name="sets"
                                    type="number"
                                    min="1"
                                    step="2"
                                    value={formData.sets}
                                    onChange={handleInputChange}
                                    onBlur={() => setFormTouched({ ...formTouched, sets: true })}
                                    isInvalid={!!errors.sets && formTouched.sets}
                                  />
                                  {(errors.sets && formTouched.sets) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.sets}</div>}
                                </>
                              )}
                            </Col>
                          )}
                          <Col md={3}>
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                              Group
                              {showEditModal && (() => {
                                const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === formData.ageCategory?.value);
                                const isGroupStandings = ageCategoryObj?.standingsType === 'group';
                                const isKnockout = formData.stage?.value === 'knockout';
                                const shouldShowGroupField = isGroupStandings && !isKnockout;
                                return shouldShowGroupField ? <span className="text-danger">*</span> : null;
                              })()}
                            </Form.Label>
                            {!showEditModal ? (
                              <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                {fixture.group ? `Group ${fixture.group}` : '-'}
                              </div>
                            ) : (
                              (() => {
                                const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === formData.ageCategory?.value);
                                const isGroupStandings = ageCategoryObj?.standingsType === 'group';
                                const isKnockout = formData.stage?.value === 'knockout';
                                const shouldShowGroupField = isGroupStandings && !isKnockout;
                                
                                if (shouldShowGroupField) {
                                  return (
                                    <>
                                      <Select
                                        classNamePrefix="react-select"
                                        value={formData.group}
                                        onChange={handleGroupChange}
                                        onBlur={() => setFormTouched({ ...formTouched, group: true })}
                                        options={availableGroups}
                                        placeholder={groupsLoading ? "Loading groups..." : "Select Group"}
                                        isClearable={false}
                                        isLoading={groupsLoading}
                                        isDisabled={!formData.ageCategory || groupsLoading}
                                        styles={{ control: (base, state) => ({ 
                                          ...base, 
                                          minHeight: '38px',
                                          borderColor: state.isFocused ? '#80bdff' : (errors.group && formTouched.group ? '#dc3545' : '#ced4da')
                                        }) }}
                                      />
                                      {(errors.group && formTouched.group) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.group}</div>}
                                    </>
                                  );
                                } else {
                                  return (
                                    <Form.Control
                                      value="Not Required"
                                      readOnly
                                      style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                      placeholder="Group not required for this age category"
                                    />
                                  );
                                }
                              })()
                            )}
                          </Col>
                          {/* Knockout fields for Football - Show when stage is knockout */}
                          {formData.stage?.value === 'knockout' && (
                            <>
                              <Col md={3}>
                                <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                                  Knockout Stage
                                </Form.Label>
                                {!showEditModal ? (
                                  <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                    {(fixture as any)?.knockout?.stage 
                                      ? (fixture as any).knockout.stage.charAt(0).toUpperCase() + (fixture as any).knockout.stage.slice(1)
                                      : '-'}
                                  </div>
                                ) : (
                                  <Select
                                    classNamePrefix="react-select"
                                    value={formData.knockoutStage}
                                    onChange={(option) => handleSelectChange('knockoutStage', option)}
                                    onBlur={() => setFormTouched({ ...formTouched, knockoutStage: true })}
                                    options={[
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
                                      { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
                                    ]}
                                    placeholder="Select Knockout Stage"
                                    isClearable={true}
                                    styles={{ control: (base, state) => ({ 
                                      ...base, 
                                      minHeight: '38px',
                                      borderColor: state.isFocused ? '#80bdff' : '#ced4da'
                                    }) }}
                                  />
                                )}
                              </Col>
                              <Col md={3}>
                                <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                                  Knockout Index
                                </Form.Label>
                                {!showEditModal ? (
                                  <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                    {(fixture as any)?.knockout?.index ?? '-'}
                                  </div>
                                ) : (
                                  <Form.Control
                                    name="knockoutIndex"
                                    type="number"
                                    value={formData.knockoutIndex}
                                    onChange={handleInputChange}
                                    onBlur={() => setFormTouched({ ...formTouched, knockoutIndex: true })}
                                    placeholder="Enter index"
                                  />
                                )}
                              </Col>
                              <Col md={3}>
                                <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                                  Knockout Match Number
                                </Form.Label>
                                {!showEditModal ? (
                                  <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                    {(fixture as any)?.knockout?.matchNum || '-'}
                                  </div>
                                ) : (
                                  <Form.Control
                                    name="knockoutMatchNum"
                                    type="text"
                                    value={formData.knockoutMatchNum}
                                    onChange={handleInputChange}
                                    onBlur={() => setFormTouched({ ...formTouched, knockoutMatchNum: true })}
                                    placeholder="Enter match number"
                                  />
                                )}
                              </Col>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {/* For Badminton/TT: Show Round field */}
                          <Col md={3}>
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                              Round
                              {showEditModal && formData.stage?.value === 'knockout' && <span className="text-danger">*</span>}
                            </Form.Label>
                            {!showEditModal ? (
                              <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                {(fixture as any)?.round ? `Round ${(fixture as any).round}` : '-'}
                              </div>
                            ) : (
                              <>
                                <Form.Control
                                  name="round"
                                  type="text"
                                  value={formData.round}
                                  onChange={handleInputChange}
                                  onBlur={() => setFormTouched({ ...formTouched, round: true })}
                                  placeholder="Enter round (e.g., Quarter Final, Semi Final)"
                                  isInvalid={!!errors.round && formTouched.round}
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                                  {formData.stage?.value === 'knockout' ? 'Required for Knockout stage' : 'Optional: Enter round information'}
                                </Form.Text>
                                {(errors.round && formTouched.round) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.round}</div>}
                              </>
                            )}
                          </Col>
                          {/* For Badminton/TT: Show Sets field */}
                          <Col md={3}>
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                              Sets<span className="text-danger">*</span>
                            </Form.Label>
                            {!showEditModal ? (
                              <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                {(fixture as any)?.setCount || '-'}
                              </div>
                            ) : (
                              <>
                                <Form.Control
                                  name="sets"
                                  type="number"
                                  min="1"
                                  step="2"
                                  value={formData.sets}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = e.target.value;
                                    // Only allow numeric input
                                    if (value === '' || /^\d+$/.test(value)) {
                                      const updatedFormData = { ...formData, sets: value };
                                      setFormData(updatedFormData);
                                      setFormTouched({ ...formTouched, sets: true });
                                      // Trigger validation and form modification check
                                      requestAnimationFrame(() => {
                                        const validation = validateForm(updatedFormData);
                                        setIsFormValid(validation.valid);
                                        // Update form modified state using the updated data
                                        checkFormModified(updatedFormData);
                                      });
                                    }
                                  }}
                                  onBlur={() => setFormTouched({ ...formTouched, sets: true })}
                                  placeholder="Enter sets (1, 3, 5, 7...)"
                                  isInvalid={!!errors.sets && formTouched.sets}
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                                  Enter odd number only (1, 3, 5, 7, 9, etc.)
                                </Form.Text>
                                {(errors.sets && formTouched.sets) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.sets}</div>}
                              </>
                            )}
                          </Col>
                          {/* For Badminton/TT: Show Group field only if group exists or if group standings type */}
                          {(() => {
                            const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; standingsType?: string }) => ac.value === formData.ageCategory?.value);
                            const isGroupStandings = ageCategoryObj?.standingsType === 'group';
                            const hasGroup = fixture.group || formData.group?.value;
                            
                            // Only show Group field if: group exists OR it's a group standings type
                            if (hasGroup || isGroupStandings) {
                              return (
                                <Col md={3}>
                                  <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                                    Group
                                    {/* No mandatory asterisk for Badminton/TT - Group is optional */}
                                  </Form.Label>
                                  {!showEditModal ? (
                                    <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                                      {fixture.group ? `Group: ${fixture.group}` : 'Group: NA'}
                                    </div>
                                  ) : (
                                    (() => {
                                      if (isGroupStandings) {
                                        // For Badminton/TT with group standings, allow selection but it's optional
                                        return (
                                          <>
                                            <Select
                                              classNamePrefix="react-select"
                                              value={formData.group}
                                              onChange={handleGroupChange}
                                              onBlur={() => setFormTouched({ ...formTouched, group: true })}
                                              options={availableGroups}
                                              placeholder={groupsLoading ? "Loading groups..." : "Select Group (Optional)"}
                                              isClearable={true}
                                              isLoading={groupsLoading}
                                              isDisabled={!formData.ageCategory || groupsLoading}
                                              styles={{ control: (base, state) => ({ 
                                                ...base, 
                                                minHeight: '38px',
                                                borderColor: state.isFocused ? '#80bdff' : '#ced4da'
                                              }) }}
                                            />
                                            <Form.Text className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                                              Optional: Select a group if applicable
                                            </Form.Text>
                                          </>
                                        );
                                      } else {
                                        return (
                                          <Form.Control
                                            value={fixture.group ? `Group: ${fixture.group}` : 'Group: NA'}
                                            readOnly
                                            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                          />
                                        );
                                      }
                                    })()
                                  )}
                                </Col>
                              );
                            }
                            return null; // Don't show Group field if no group and not group standings
                          })()}
                        </>
                      )}
                      {/* 5. Team A / Player A */}
                      <Col md={3}>
                        {(() => {
                          // Check age category format instead of sport type
                          const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory?.value);
                          const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
                          
                          // Fallback: If format is missing, check sport type for Badminton/Table Tennis
                          let isPlayerFormat = ageCategoryFormat === 'player';
                          if (!ageCategoryFormat && formData.sports) {
                            const sport = formData.sports?.value || formData.sports?.label || '';
                            const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
                            const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
                            if (isBadmintonOrTT || normalizedSport === 'padel' || normalizedSport === 'pickleball') {
                              isPlayerFormat = true;
                            }
                          }
                          
                          return (
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                              {isPlayerFormat ? 'Player A' : 'Team A'}{!isPlayerFormat && <span className="text-danger">*</span>}
                            </Form.Label>
                          );
                        })()}
                        {!showEditModal ? (
                          <div className="d-flex align-items-center gap-2 form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {(() => {
                              // Priority: 1. side1.team object, 2. team1Info, 3. teamA
                              const side1 = (fixture as any)?.side1;
                              const team1Info = (fixture as any)?.team1Info;
                              const teamA = fixture.teamA || (fixture as any).teamA;
                              
                              // Get logo - check side1.team object first, then fallback
                              let teamALogo = null;
                              if (side1?.team && typeof side1.team === 'object' && side1.team.logo) {
                                teamALogo = side1.team.logo;
                              } else {
                                teamALogo = team1Info?.logo || teamA?.logo || (fixture as any)?.teamA?.logo;
                              }
                              
                              const teamAName = getTeam1Name();
                              
                              return (
                                <>
                                  {teamALogo ? (
                                    <img
                                      src={getImageUrl(teamALogo)}
                                      alt="Team A"
                                      style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', border: '1px solid #e9ecef' }}
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : null}
                                  <span>{teamAName}</span>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <>
                            {(() => {
                              // Check age category format instead of sport type
                              const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory?.value);
                              const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
                              const isPlayerFormat = ageCategoryFormat === 'player'
                                || ['padel', 'pickleball'].includes((formData.sports?.value || formData.sports?.label || '').replace(/\s+/g, '').toLowerCase());
                              
                              if (isPlayerFormat) {
                                // Show Player A dropdown for player format age categories
                                return (
                                  <>
                                    <Select
                                      classNamePrefix="react-select"
                                      value={formData.playerA}
                                      onChange={(option) => handleSelectChange('playerA', option)}
                                      onBlur={() => setFormTouched({ ...formTouched, playerA: true })}
                                      options={getPlayerAOptions()}
                                      placeholder={
                                        !formData.ageCategory ? "Select Age Category first" :
                                        "Select Player A"
                                      }
                                      isClearable={true}
                                      isLoading={playersLoading}
                                      isDisabled={!formData.ageCategory}
                                      menuPortalTarget={document.body}
                                      styles={{ 
                                        control: (base, state) => ({ 
                                          ...base, 
                                          minHeight: '38px',
                                          borderColor: state.isFocused ? '#80bdff' : (errors.playerA && formTouched.playerA ? '#dc3545' : '#ced4da')
                                        }),
                                        menu: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        }),
                                        menuPortal: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        })
                                      }}
                                    />
                                    {(errors.playerA && formTouched.playerA) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.playerA}</div>}
                                  </>
                                );
                              } else {
                                // Show Team A dropdown for team format age categories (default)
                                return (
                          <>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.teamA}
                              onChange={(option) => handleSelectChange('teamA', option)}
                              onBlur={() => setFormTouched({ ...formTouched, teamA: true })}
                              options={getTeamAOptions()}
                              placeholder={
                                !formData.ageCategory ? "Select Age Category first" :
                                (() => {
                                  const isKnockout = formData.stage?.value === 'knockout';
                                  const shouldRequireGroup = availableGroups.length > 0 && !isKnockout && !formData.group;
                                  return shouldRequireGroup ? "Select Group first" : "Select Team A";
                                })()
                              }
                              isClearable={false}
                              isLoading={teamsLoading}
                              isDisabled={!formData.ageCategory || (() => {
                                const isKnockout = formData.stage?.value === 'knockout';
                                return availableGroups.length > 0 && !isKnockout && !formData.group;
                              })()}
                                      menuPortalTarget={document.body}
                                      styles={{ 
                                        control: (base, state) => ({ 
                                ...base, 
                                minHeight: '38px',
                                borderColor: state.isFocused ? '#80bdff' : (errors.teamA && formTouched.teamA ? '#dc3545' : '#ced4da')
                                        }),
                                        menu: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        }),
                                        menuPortal: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        })
                                      }}
                            />
                            {(errors.teamA && formTouched.teamA) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.teamA}</div>}
                                  </>
                                );
                              }
                            })()}
                          </>
                        )}
                      </Col>
                      {/* 6. Team B / Player B */}
                      <Col md={3}>
                        {(() => {
                          // Check age category format instead of sport type
                          const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory?.value);
                          const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
                          
                          // Fallback: If format is missing, check sport type for Badminton/Table Tennis
                          let isPlayerFormat = ageCategoryFormat === 'player';
                          if (!ageCategoryFormat && formData.sports) {
                            const sport = formData.sports?.value || formData.sports?.label || '';
                            const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
                            const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
                            if (isBadmintonOrTT || normalizedSport === 'padel' || normalizedSport === 'pickleball') {
                              isPlayerFormat = true;
                            }
                          }
                          
                          return (
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>
                              {isPlayerFormat ? 'Player B' : 'Team B'}{!isPlayerFormat && <span className="text-danger">*</span>}
                            </Form.Label>
                          );
                        })()}
                        {!showEditModal ? (
                          <div className="d-flex align-items-center gap-2 form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {(() => {
                              // Priority: 1. side2.team object, 2. team2Info, 3. teamB
                              const side2 = (fixture as any)?.side2;
                              const team2Info = (fixture as any)?.team2Info;
                              const teamB = fixture.teamB || (fixture as any).teamB;
                              
                              // Get logo - check side2.team object first, then fallback
                              let teamBLogo = null;
                              if (side2?.team && typeof side2.team === 'object' && side2.team.logo) {
                                teamBLogo = side2.team.logo;
                              } else {
                                teamBLogo = team2Info?.logo || teamB?.logo || (fixture as any)?.teamB?.logo;
                              }
                              
                              const teamBName = getTeam2Name();
                              
                              return (
                                <>
                                  {teamBLogo ? (
                                    <img
                                      src={getImageUrl(teamBLogo)}
                                      alt="Team B"
                                      style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', border: '1px solid #e9ecef' }}
                                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : null}
                                  <span>{teamBName}</span>
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <>
                            {(() => {
                              // Check age category format instead of sport type
                              const ageCategoryObj = ageCategoryOptions.find((ac: { value: string; label: string; format?: string }) => ac.value === formData.ageCategory?.value);
                              const ageCategoryFormat = ageCategoryObj?.format?.toLowerCase();
                              
                              // Fallback: If format is missing, check sport type for Badminton/Table Tennis
                              let isPlayerFormat = ageCategoryFormat === 'player';
                              if (!ageCategoryFormat && formData.sports) {
                                const sport = formData.sports?.value || formData.sports?.label || '';
                                const normalizedSport = sport.replace(/\s+/g, '').toLowerCase();
                                const isBadmintonOrTT = normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
                                if (isBadmintonOrTT || normalizedSport === 'padel' || normalizedSport === 'pickleball') {
                                  isPlayerFormat = true;
                                }
                              }
                              
                              if (isPlayerFormat) {
                                // Show Player B dropdown for player format age categories
                                return (
                                  <>
                                    <Select
                                      classNamePrefix="react-select"
                                      value={formData.playerB}
                                      onChange={(option) => handleSelectChange('playerB', option)}
                                      onBlur={() => setFormTouched({ ...formTouched, playerB: true })}
                                      options={getPlayerBOptions()}
                                      placeholder={
                                        !formData.ageCategory ? "Select Age Category first" :
                                        "Select Player B"
                                      }
                                      isClearable={true}
                                      isLoading={playersLoading}
                                      isDisabled={!formData.ageCategory}
                                      menuPortalTarget={document.body}
                                      styles={{ 
                                        control: (base, state) => ({ 
                                          ...base, 
                                          minHeight: '38px',
                                          borderColor: state.isFocused ? '#80bdff' : (errors.playerB && formTouched.playerB ? '#dc3545' : '#ced4da')
                                        }),
                                        menu: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        }),
                                        menuPortal: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        })
                                      }}
                                    />
                                    {(errors.playerB && formTouched.playerB) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.playerB}</div>}
                                  </>
                                );
                              } else {
                                // Show Team B dropdown for team format age categories (default)
                                return (
                          <>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.teamB}
                              onChange={(option) => handleSelectChange('teamB', option)}
                              onBlur={() => setFormTouched({ ...formTouched, teamB: true })}
                              options={getTeamBOptions()}
                              placeholder={
                                !formData.ageCategory ? "Select Age Category first" :
                                (() => {
                                  const isKnockout = formData.stage?.value === 'knockout';
                                  const shouldRequireGroup = availableGroups.length > 0 && !isKnockout && !formData.group;
                                  return shouldRequireGroup ? "Select Group first" : "Select Team B";
                                })()
                              }
                              isClearable={false}
                              isLoading={teamsLoading}
                              isDisabled={!formData.ageCategory || (() => {
                                const isKnockout = formData.stage?.value === 'knockout';
                                return availableGroups.length > 0 && !isKnockout && !formData.group;
                              })()}
                                      menuPortalTarget={document.body}
                                      styles={{ 
                                        control: (base, state) => ({ 
                                ...base, 
                                minHeight: '38px',
                                borderColor: state.isFocused ? '#80bdff' : (errors.teamB && formTouched.teamB ? '#dc3545' : '#ced4da')
                                        }),
                                        menu: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        }),
                                        menuPortal: (base) => ({ 
                                          ...base, 
                                          zIndex: 99999 
                                        })
                                      }}
                            />
                            {(errors.teamB && formTouched.teamB) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.teamB}</div>}
                                  </>
                                );
                              }
                            })()}
                          </>
                        )}
                      </Col>
                      {(() => {
                        const sport = (formData.sports?.value || formData.sports?.label || '').replace(/[\s_-]/g, '').toLowerCase();
                        return sport === 'padel' || (sport === 'pickleball' && isPickleballDoubles(formData.eventCategory?.value));
                      })() && (
                        <>
                          <Col md={3}>
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Second Player - Team 1<span className="text-danger">*</span></Form.Label>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.playerA2}
                              onChange={(option) => handleSelectChange('playerA2', option)}
                              onBlur={() => setFormTouched({ ...formTouched, playerA2: true })}
                              options={getPlayerOptions('playerA2')}
                              placeholder={!formData.ageCategory ? 'Select Age Category first' : 'Select second player'}
                              isClearable={false}
                              isLoading={playersLoading}
                              isDisabled={!showEditModal || !formData.ageCategory}
                              menuPortalTarget={document.body}
                              styles={{ control: (base) => ({ ...base, minHeight: '38px' }), menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                            />
                            {(errors.playerA2 && formTouched.playerA2) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.playerA2}</div>}
                          </Col>
                          <Col md={3}>
                            <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Second Player - Team 2<span className="text-danger">*</span></Form.Label>
                            <Select
                              classNamePrefix="react-select"
                              value={formData.playerB2}
                              onChange={(option) => handleSelectChange('playerB2', option)}
                              onBlur={() => setFormTouched({ ...formTouched, playerB2: true })}
                              options={getPlayerOptions('playerB2')}
                              placeholder={!formData.ageCategory ? 'Select Age Category first' : 'Select second player'}
                              isClearable={false}
                              isLoading={playersLoading}
                              isDisabled={!showEditModal || !formData.ageCategory}
                              menuPortalTarget={document.body}
                              styles={{ control: (base) => ({ ...base, minHeight: '38px' }), menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                            />
                            {(errors.playerB2 && formTouched.playerB2) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.playerB2}</div>}
                          </Col>
                        </>
                      )}
                      {/* 7. Date */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Date<span className="text-danger">*</span></Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {fixture.date ? new Date(fixture.date).toLocaleDateString() : '-'}
                          </div>
                        ) : (
                          <>
                            <Form.Control
                              name="date"
                              type="date"
                              value={formData.date}
                              onChange={handleInputChange}
                              isInvalid={!!errors.date && formTouched.date}
                              onBlur={() => setFormTouched({ ...formTouched, date: true })}
                            />
                            {(errors.date && formTouched.date) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.date}</div>}
                          </>
                        )}
                      </Col>
                      {/* 8. Match Time */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Match Time<span className="text-danger">*</span></Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {formatTimeTo12Hour(fixture.time || (fixture as any)?.dateTime || fixture.startTime)}
                          </div>
                        ) : (
                          <>
                            <div className="time-picker-container">
                              <Button
                                ref={timePickerButtonRef}
                                variant="outline-secondary"
                                className="w-100 d-flex justify-content-between align-items-center time-picker-button"
                                onClick={openTimePicker}
                                style={{
                                  height: '38px',
                                  border: `1px solid ${errors.time && formTouched.time ? '#dc3545' : '#ced4da'}`,
                                  backgroundColor: 'white',
                                  fontSize: '14px',
                                  borderRadius: '4px',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <span className={formData.time ? 'text-dark fw-medium' : 'text-muted'}>
                                  {formData.time || 'Select time'}
                                </span>
                                <i className="ri-time-line text-primary"></i>
                              </Button>
                              <Form.Text className="text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                                <i className="ri-information-line me-1"></i>
                                Click to select time
                              </Form.Text>
                            </div>
                            {(errors.time && formTouched.time) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.time}</div>}
                          </>
                        )}
                      </Col>
                      {/* 9. Venue */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Venue<span className="text-danger">*</span></Form.Label>
                        {!showEditModal ? (
                          <div className="form-control-plaintext" style={{ paddingLeft: 0, fontSize: 16, fontWeight: 600, color: '#212529' }}>
                            {fixture.venue || '-'}
                          </div>
                        ) : (
                          <>
                            <Form.Control
                              name="venue"
                              type="text"
                              value={formData.venue}
                              onChange={handleInputChange}
                              placeholder="Enter venue name"
                              isInvalid={!!errors.venue && formTouched.venue}
                              onBlur={() => setFormTouched({ ...formTouched, venue: true })}
                            />
                            {(errors.venue && formTouched.venue) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.venue}</div>}
                          </>
                        )}
                      </Col>
                      {/* 10. Venue Full Address - Only show when editing */}
                      {showEditModal && (
                        <Col md={6}>
                          <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Venue Full Address<span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            name="venueLong"
                            type="text"
                            value={formData.venueLong}
                            onChange={handleInputChange}
                            placeholder="Enter full venue address"
                            isInvalid={!!errors.venueLong && formTouched.venueLong}
                            onBlur={() => setFormTouched({ ...formTouched, venueLong: true })}
                          />
                          {(errors.venueLong && formTouched.venueLong) && <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>{errors.venueLong}</div>}
                        </Col>
                      )}
                      
                      {/* Stream-related fields (read-only) - shown after editable fields */}
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Match Id</Form.Label>
                        <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                          <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                            {fixture?._id || matchId || '-'}
                          </div>
                          {(fixture?._id || matchId) && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy(fixture?._id || matchId || '', 'Match Id')}
                              title="Copy Match Id"
                              style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                              <i className="ri-file-copy-line"></i>
                            </Button>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Channel Name</Form.Label>
                        <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                          <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                            {(fixture as any)?.liveStreamInfo?.channelName || '-'}
                          </div>
                          {(fixture as any)?.liveStreamInfo?.channelName && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy((fixture as any)?.liveStreamInfo?.channelName, 'Channel Name')}
                              title="Copy Channel Name"
                              style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                              <i className="ri-file-copy-line"></i>
                            </Button>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Stream Key</Form.Label>
                        <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                          <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                            {(fixture as any)?.liveStreamInfo?.streamKey || '-'}
                          </div>
                          {(fixture as any)?.liveStreamInfo?.streamKey && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy((fixture as any)?.liveStreamInfo?.streamKey, 'Stream Key')}
                              title="Copy Stream Key"
                              style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                              <i className="ri-file-copy-line"></i>
                            </Button>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Ingestion URL</Form.Label>
                        <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                          <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                            {(() => {
                              const ingestionUrl = (fixture as any)?.liveStreamInfo?.ingestionUrl || 
                                                 (fixture as any)?.liveStreamInfo?.ingestUrl || 
                                                 (fixture as any)?.liveStreamInfo?.ingestEndpoint || 
                                                 (fixture as any)?.liveStreamInfo?.streamUrl || 
                                                 (fixture as any)?.streamingVideoUrl || 
                                                 '-';
                              return ingestionUrl;
                            })()}
                          </div>
                          {(() => {
                            const ingestionUrl = (fixture as any)?.liveStreamInfo?.ingestionUrl || 
                                               (fixture as any)?.liveStreamInfo?.ingestUrl || 
                                               (fixture as any)?.liveStreamInfo?.ingestEndpoint || 
                                               (fixture as any)?.liveStreamInfo?.streamUrl || 
                                               (fixture as any)?.streamingVideoUrl;
                            return ingestionUrl && ingestionUrl !== '-' ? (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleCopy(ingestionUrl, 'Ingestion URL')}
                                title="Copy Ingestion URL"
                                style={{ minWidth: 'auto', padding: '4px 8px' }}
                              >
                                <i className="ri-file-copy-line"></i>
                              </Button>
                            ) : null;
                          })()}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Recorded URL</Form.Label>
                        <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                          <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                            {(fixture as any)?.streamRecordingInfo?.streamS3Url || (fixture as any)?.streamRecordingInfo?.recordedUrl || '-'}
                          </div>
                          {((fixture as any)?.streamRecordingInfo?.streamS3Url || (fixture as any)?.streamRecordingInfo?.recordedUrl) && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy((fixture as any)?.streamRecordingInfo?.streamS3Url || (fixture as any)?.streamRecordingInfo?.recordedUrl, 'Recorded URL')}
                              title="Copy Recorded URL"
                              style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                              <i className="ri-file-copy-line"></i>
                            </Button>
                          )}
                        </div>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label" style={{ fontSize: 12, marginBottom: 4 }}>Youtube Streaming URL</Form.Label>
                        {showEditModal ? (
                          <Form.Control
                            name="streamingVideoUrl"
                            type="text"
                            value={formData.streamingVideoUrl}
                            onChange={handleInputChange}
                            placeholder="Enter Youtube streaming URL"
                          />
                        ) : (
                          <div className="d-flex align-items-center gap-2" style={{ paddingLeft: 0 }}>
                            <div className="form-control-plaintext flex-grow-1" style={{ fontSize: 14, color: '#212529', wordBreak: 'break-all', paddingRight: '8px' }}>
                              {(fixture as any)?.streamingVideoUrl || '-'}
                            </div>
                            {(fixture as any)?.streamingVideoUrl && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleCopy((fixture as any)?.streamingVideoUrl, 'Youtube Streaming URL')}
                                title="Copy Youtube Streaming URL"
                                style={{ minWidth: 'auto', padding: '4px 8px' }}
                              >
                                <i className="ri-file-copy-line"></i>
                              </Button>
                            )}
                          </div>
                        )}
                      </Col>
                    </Row>
                  );
                })()}
              </Card.Body>
            </Card>

            {/* Existing live-scoring UI, with sport-aware lifecycle controls. */}
            {SHOW_MATCH_AND_SCORE_MANAGEMENT && fixture && (
              <>
                <Card className="mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <Card.Header style={{ backgroundColor: '#f8f9fc', borderBottom: '1px solid #e0e0e0' }}>
                    <h5 className="mb-0" style={{ fontWeight: 600 }}>Match Management</h5>
                  </Card.Header>
                  <Card.Body>
                    <>
                {/* Match Status Alert */}
                {matchStatus === 'paused' && (
                  <div className="alert alert-info mb-4" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: 8 }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Match Paused</strong>
                        <p className="mb-0" style={{ fontSize: 14, color: '#495057' }}>
                          The match has been temporarily paused. You can resume it anytime to continue tracking scores and player performance.
                        </p>
                      </div>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={handleResumeMatchClick}
                        disabled={isStartingMatch}
                      >
                        <i className="ri-play-line me-1"></i>
                        Resume Match
                      </Button>
                    </div>
                  </div>
                )}

                {matchStatus === 'completed' || matchStatus === 'ended' ? (
                  <>
                    {/* Check for walkover and cancelled information from live scores API */}
                    {(() => {
                      // Get walkover and cancelled status from live scores API response
                      // API endpoint: /api/v1/tournaments/manage/fetch-live-scores/{matchId}
                      // Response includes: isWalkover: true/false, isCancelled: true/false
                      const isWalkover = liveScoreData?.isWalkover === true;
                      const isCancelled = liveScoreData?.isCancelled === true;
                      
                      // Show cancelled message if match is cancelled
                      if (isCancelled) {
                        // Get cancellation reason from live score data
                        const cancelledReason = liveScoreData?.cancelledReason || 
                                               liveScoreData?.cancelReason || 
                                               liveScoreData?.abandonReason ||
                                               liveScoreData?.reason;
                        
                        return (
                          <div className="alert alert-danger mb-4" style={{ backgroundColor: '#f8d7da', border: '2px solid #dc3545', borderRadius: 8, boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)' }}>
                            <div className="d-flex align-items-start gap-3">
                              <i className="ri-close-circle-line" style={{ fontSize: '28px', color: '#721c24', marginTop: '2px' }}></i>
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: '#721c24', fontSize: '16px' }}>Match Cancelled</strong>
                                <p className="mb-2" style={{ fontSize: 14, color: '#721c24', marginTop: '4px' }}>
                                  This match has been cancelled. No result will be recorded.
                                </p>
                                {cancelledReason && (
                                  <p className="mb-0" style={{ fontSize: 13, color: '#721c24', fontStyle: 'italic' }}>
                                    <strong>Reason:</strong> {cancelledReason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Show walkover message if match is walkover
                      if (isWalkover) {
                        // Try to determine which team had the walkover from live score data
                        // Check if we have walkover team info in liveScoreData
                        const walkoverTeamId = liveScoreData?.walkoverTeam || liveScoreData?.walkoverTeamId;
                        
                        let isTeamAWalkover = false;
                        if (walkoverTeamId) {
                          const teamAId = liveScoreData?.teamAInfo?._id || fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id;
                          isTeamAWalkover = String(walkoverTeamId) === String(teamAId);
                        } else {
                          // If no team ID, try to infer from scores
                          // For walkover, typically one team has 0 or default score
                          if (teamAScore === 0 && teamBScore > 0) {
                            isTeamAWalkover = true;
                          } else if (teamBScore === 0 && teamAScore > 0) {
                            isTeamAWalkover = false;
                          }
                        }
                        
                        // Get team names using helper functions, with fallback to liveScoreData
                        const team1NameFromLive = liveScoreData?.team1Info?.name || liveScoreData?.teamAInfo?.name;
                        const team2NameFromLive = liveScoreData?.team2Info?.name || liveScoreData?.teamBInfo?.name;
                        const team1Name = team1NameFromLive || getTeam1Name();
                        const team2Name = team2NameFromLive || getTeam2Name();
                        
                        const walkoverTeamName = isTeamAWalkover ? team1Name : team2Name;
                        const winningTeamName = isTeamAWalkover ? team2Name : team1Name;
                        
                        const walkoverReason = liveScoreData?.walkoverReason || liveScoreData?.reason;
                        
                        return (
                          <div className="alert alert-warning mb-4" style={{ backgroundColor: '#fff3cd', border: '2px solid #ffc107', borderRadius: 8, boxShadow: '0 2px 4px rgba(255, 193, 7, 0.2)' }}>
                            <div className="d-flex align-items-start gap-3">
                              <i className="ri-flag-line" style={{ fontSize: '28px', color: '#856404', marginTop: '2px' }}></i>
                              <div style={{ flex: 1 }}>
                                <strong style={{ color: '#856404', fontSize: '16px' }}>Walkover Declared</strong>
                                <p className="mb-2" style={{ fontSize: 14, color: '#856404', marginTop: '4px' }}>
                                  <strong>{walkoverTeamName}</strong> did not participate. <strong>{winningTeamName}</strong> wins by walkover.
                                </p>
                                {walkoverReason && (
                                  <p className="mb-0" style={{ fontSize: 13, color: '#856404', fontStyle: 'italic' }}>
                                    <strong>Reason:</strong> {walkoverReason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Default: Match ended normally
                      return (
                        <div className="alert alert-danger mb-4" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Match Ended</strong>
                              <p className="mb-0" style={{ fontSize: 14, color: '#721c24' }}>
                                The match has been successfully ended. Final scores and player statistics have been recorded and saved.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Final Score Display for Completed Matches */}
                    {isLiveScoringSupported && (() => {
                      // Get walkover and cancelled status from live scores API response
                      const isWalkover = liveScoreData?.isWalkover === true;
                      const isCancelled = liveScoreData?.isCancelled === true;
                      const hasWalkover = isWalkover;
                      const hasCancelled = isCancelled;
                      
                      return (
                        <div className="mb-4">
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <h6 className="mb-0">Final Score</h6>
                            {hasWalkover && (
                              <Badge 
                                bg="warning" 
                                className="payment-badge"
                                style={{
                                  border: '1px solid',
                                  fontWeight: '500',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '6px',
                                  backgroundColor: '#FFF5EC',
                                  color: '#FD9644',
                                  borderColor: '#FD9644',
                                  textTransform: 'capitalize',
                                  fontSize: '12px'
                                }}
                              >
                                WALKOVER
                              </Badge>
                            )}
                            {hasCancelled && (
                              <Badge 
                                bg="danger" 
                                className="payment-badge"
                                style={{
                                  border: '1px solid',
                                  fontWeight: '500',
                                  padding: '0.375rem 0.75rem',
                                  borderRadius: '6px',
                                  backgroundColor: '#FEE',
                                  color: '#DC3545',
                                  borderColor: '#DC3545',
                                  textTransform: 'capitalize',
                                  fontSize: '12px'
                                }}
                              >
                                CANCELLED
                              </Badge>
                            )}
                          </div>
                          {isBadmintonOrTT() ? (
                            (() => {
                              // Final Score table should always be read-only - editing is done via set cards below
                              return renderSetWiseScoreTable(false);
                            })()
                          ) : (
                          <Row className="g-3 mb-4">
                            <Col md={6}>
                              <div className="text-center p-3" style={{ 
                                backgroundColor: '#f8f9fc', 
                                borderRadius: 8, 
                                border: hasWalkover ? '2px solid #ffc107' : hasCancelled ? '2px solid #dc3545' : '1px solid #e0e0e0' 
                              }}>
                                <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                                  {liveScoreData?.team1Info?.name || liveScoreData?.teamAInfo?.name || getTeam1Name()}
                                </div>
                                <div style={{ fontSize: 32, fontWeight: 700, color: '#212529' }}>{teamAScore}</div>
                              </div>
                            </Col>
                            <Col md={6}>
                              <div className="text-center p-3" style={{ 
                                backgroundColor: '#f8f9fc', 
                                borderRadius: 8, 
                                border: hasWalkover ? '2px solid #ffc107' : hasCancelled ? '2px solid #dc3545' : '1px solid #e0e0e0' 
                              }}>
                                <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                                  {liveScoreData?.team2Info?.name || liveScoreData?.teamBInfo?.name || getTeam2Name()}
                                </div>
                                <div style={{ fontSize: 32, fontWeight: 700, color: '#212529' }}>{teamBScore}</div>
                              </div>
                            </Col>
                          </Row>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Sets Management UI - Only for Badminton and Table Tennis - Show for ALL match statuses including completed */}
                    {isLiveScoringSupported && isBadmintonOrTT() && (() => {
                      const currentMatchStatus: string = matchStatus;
                      const isMatchCompleted = currentMatchStatus === 'completed' || currentMatchStatus === 'ended' || currentMatchStatus === 'finished';
                      const isMatchActive = currentMatchStatus === 'ongoing' || currentMatchStatus === 'paused';
                      
                      return (
                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">
                              {isMatchCompleted ? 'Edit Sets' : 'Manage Live Score'}
                            </h6>
                            {/* Show action buttons for active matches (Pause/Resume/End) */}
                            {isMatchActive && (
                            <div className="d-flex gap-2">
                              {currentMatchStatus === 'ongoing' && (
                                <Button 
                                  variant="warning" 
                                  size="sm"
                                  onClick={handlePauseMatchClick}
                                  disabled={isPausingMatch}
                                >
                                  <i className="ri-pause-line me-1"></i>
                                  {isPausingMatch ? 'Pausing...' : 'Pause'}
                                </Button>
                              )}
                              {currentMatchStatus === 'paused' && (
                                <Button 
                                  variant="warning" 
                                  size="sm"
                                  onClick={handleResumeMatchClick}
                                  disabled={isStartingMatch}
                                >
                                  <i className="ri-play-line me-1"></i>
                                  {isStartingMatch ? 'Resuming...' : 'Resume'}
                                </Button>
                              )}
                              {/* Add Scores button - show when no sets exist (for both active and completed matches) */}
                              {sets.length === 0 && !loadingSets && (
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={handleCreateSet}
                                  disabled={isSavingSet}
                                >
                                  <i className="ri-add-line me-1"></i>
                                  {isSavingSet ? 'Creating...' : 'Add Scores'}
                                </Button>
                              )}
                              <Button 
                                variant="danger" 
                                size="sm"
                                onClick={handleEndMatchClick}
                                disabled={isEndingMatch}
                              >
                                <i className="ri-stop-circle-line me-1"></i>
                                {isEndingMatch ? 'Ending...' : 'End'}
                              </Button>
                            </div>
                          )}
                          {/* Show "Add Scores" button for completed matches when no sets exist */}
                          {isMatchCompleted && sets.length === 0 && !loadingSets && (
                            <div className="d-flex gap-2">
                              <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleCreateSet}
                                disabled={isSavingSet}
                              >
                                <i className="ri-add-line me-1"></i>
                                {isSavingSet ? 'Creating...' : 'Add Scores'}
                              </Button>
                            </div>
                          )}
                        </div>
                        
                        {loadingSets ? (
                          <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading sets...</span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {/* Display existing sets - Always show set cards for editing, regardless of match status */}
                            {sets.length > 0 ? (
                              sets.map((set, index) => {
                              // Check if set is completed - handle both isComplete boolean and status === 'completed'
                              const isCompleted = set.isComplete || (set as any).status === 'completed';
                              const isActive = !isCompleted && index === sets.filter(s => !(s.isComplete || (s as any).status === 'completed')).length - 1;
                              
                              // Calculate winner from points if not set by API
                              // Handle Draw case: if both points are 0, it's a draw
                              const isDraw = isCompleted && set.side1Points === 0 && set.side2Points === 0;
                              const calculatedWinner = isCompleted && !isDraw
                                ? (set.winner || (set.side1Points > set.side2Points ? 'side1' : set.side2Points > set.side1Points ? 'side2' : null))
                                : null;
                              
                              return (
                                <Card key={set.setNumber || index} id={`set-card-${set.setNumber}`} className="mb-3" style={{ 
                                  border: isCompleted ? '2px solid #1B9E5C' : '1px solid #e0e0e0', 
                                  borderRadius: 12, 
                                  boxShadow: isCompleted ? '0 2px 8px rgba(27, 158, 92, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  overflow: 'hidden'
                                }}>
                                  <Card.Header style={{ 
                                    backgroundColor: isCompleted ? '#E9FCF2' : '#f8f9fc', 
                                    borderBottom: isCompleted ? '2px solid #1B9E5C' : '1px solid #e0e0e0',
                                    padding: '12px 16px'
                                  }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="d-flex align-items-center gap-2">
                                        <strong style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>Set {set.setNumber}</strong>
                                        {isCompleted && getCompletedBadge()}
                                      </div>
                                      {isCompleted && (
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => {
                                              setEditingSet(set);
                                              // Initialize lastSavedSetData if not already set for this set
                                              setLastSavedSetData(prev => {
                                                if (!prev.has(set.setNumber)) {
                                                  const newMap = new Map(prev);
                                                  newMap.set(set.setNumber, {
                                                    side1Points: set.side1Points || 0,
                                                    side2Points: set.side2Points || 0
                                                  });
                                                  return newMap;
                                                }
                                                return prev;
                                              });
                                            }}
                                            style={{ 
                                              minWidth: '36px', 
                                              padding: '6px 10px',
                                              borderColor: '#006EFF',
                                              color: '#006EFF'
                                            }}
                                            title="Edit set"
                                          >
                                            <i className="ri-edit-line"></i>
                                          </Button>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => setSetToDelete(set.setNumber)}
                                            style={{ 
                                              minWidth: '36px', 
                                              padding: '6px 10px',
                                              borderColor: '#FC5C65',
                                              color: '#FC5C65'
                                            }}
                                            title="Delete set"
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </Card.Header>
                                  <Card.Body style={{ padding: '20px' }}>
                                    {/* Show read-only view for completed sets unless explicitly editing */}
                                    {isCompleted && !(editingSet?.setNumber === set.setNumber) ? (
                                      <Row className="g-3">
                                        <Col md={6}>
                                          <div className="text-center p-4" style={{ 
                                            backgroundColor: '#ffffff', 
                                            borderRadius: 12,
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                          }}>
                                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                              <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                              <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam1Name()}</div>
                                            </div>
                                            <div className="mb-3">
                                              {getResultBadge(calculatedWinner === 'side1', isDraw)}
                                            </div>
                                            <div style={{ fontSize: 32, fontWeight: 700, color: '#212529', marginTop: 8, marginBottom: 4 }}>
                                              {set.side1Points}
                                            </div>
                                            <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Points</div>
                                          </div>
                                        </Col>
                                        <Col md={6}>
                                          <div className="text-center p-4" style={{ 
                                            backgroundColor: '#ffffff', 
                                            borderRadius: 12,
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                          }}>
                                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                              <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                              <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam2Name()}</div>
                                            </div>
                                            <div className="mb-3">
                                              {getResultBadge(calculatedWinner === 'side2', isDraw)}
                                            </div>
                                            <div style={{ fontSize: 32, fontWeight: 700, color: '#212529', marginTop: 8, marginBottom: 4 }}>
                                              {set.side2Points}
                                            </div>
                                            <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Points</div>
                                          </div>
                                        </Col>
                                      </Row>
                                    ) : (
                                      <Row className="g-3">
                                        <Col md={6}>
                                          <div className="text-center p-4" style={{ 
                                            backgroundColor: '#ffffff', 
                                            borderRadius: 12,
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                          }}>
                                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                              <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                              <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam1Name()}</div>
                                            </div>
                                            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12, fontWeight: 500 }}>Points</div>
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                  const newSet = { ...currentEditing, side1Points: Math.max(0, currentEditing.side1Points - 1) };
                                                  setEditingSet(newSet);
                                                }}
                                              >
                                                <i className="ri-subtract-line"></i>
                                              </Button>
                                              <div style={{ 
                                                width: '90px', 
                                                textAlign: 'center', 
                                                fontSize: 24, 
                                                fontWeight: 700,
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '8px',
                                                backgroundColor: '#ffffff',
                                                color: '#212529',
                                                minHeight: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                {editingSet && editingSet.setNumber === set.setNumber ? editingSet.side1Points : set.side1Points}
                                              </div>
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                  const newSet = { ...currentEditing, side1Points: currentEditing.side1Points + 1 };
                                                  setEditingSet(newSet);
                                                }}
                                              >
                                                <i className="ri-add-line"></i>
                                              </Button>
                                            </div>
                                          </div>
                                        </Col>
                                        <Col md={6}>
                                          <div className="text-center p-4" style={{ 
                                            backgroundColor: '#ffffff', 
                                            borderRadius: 12,
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                          }}>
                                            <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                              <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                              <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam2Name()}</div>
                                            </div>
                                            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12, fontWeight: 500 }}>Points</div>
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                  const newSet = { ...currentEditing, side2Points: Math.max(0, currentEditing.side2Points - 1) };
                                                  setEditingSet(newSet);
                                                }}
                                              >
                                                <i className="ri-subtract-line"></i>
                                              </Button>
                                              <div style={{ 
                                                width: '90px', 
                                                textAlign: 'center', 
                                                fontSize: 24, 
                                                fontWeight: 700,
                                                border: '2px solid #e9ecef',
                                                borderRadius: '8px',
                                                padding: '8px',
                                                backgroundColor: '#ffffff',
                                                color: '#212529',
                                                minHeight: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}>
                                                {editingSet && editingSet.setNumber === set.setNumber ? editingSet.side2Points : set.side2Points}
                                              </div>
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                  const newSet = { ...currentEditing, side2Points: currentEditing.side2Points + 1 };
                                                  setEditingSet(newSet);
                                                }}
                                              >
                                                <i className="ri-add-line"></i>
                                              </Button>
                                            </div>
                                          </div>
                                        </Col>
                                      </Row>
                                    )}
                                    
                                    {(!isCompleted || (isCompleted && editingSet && editingSet.setNumber === set.setNumber)) && (
                                      <div className="d-flex gap-2 mt-3">
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={() => {
                                            handleResetSet(set.setNumber);
                                          }}
                                          disabled={isSavingSet}
                                        >
                                          <i className="ri-restart-line me-1"></i>
                                          Reset
                                        </Button>
                                        {editingSet && editingSet.setNumber === set.setNumber && (
                                          <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleSaveSet(editingSet)}
                                            disabled={isSavingSet || !hasSetChanges(set.setNumber)}
                                          >
                                            <i className="ri-save-line me-1"></i>
                                            {isSavingSet ? 'Saving...' : 'Save & Update'}
                                          </Button>
                                        )}
                                        {!isCompleted && (
                                          <Button
                                            variant="success"
                                            size="sm"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              const scrollPosition = window.scrollY;
                                              setSetToMarkComplete(set.setNumber);
                                              setShowMarkCompleteModal(true);
                                              requestAnimationFrame(() => {
                                                window.scrollTo(0, scrollPosition);
                                              });
                                            }}
                                            disabled={isSavingSet}
                                            style={{ color: '#0d6efd' }}
                                          >
                                            Mark the set as Complete
                                          </Button>
                                        )}
                                        {isCompleted && editingSet && editingSet.setNumber === set.setNumber && (
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => {
                                              setEditingSet(null);
                                            }}
                                            disabled={isSavingSet}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              );
                              })
                            ) : (
                              <div className="text-center py-4" style={{ color: '#6c757d', fontSize: '14px' }}>
                                {isMatchCompleted 
                                  ? 'No sets available. Click "Add Scores" above to create sets and update scores.' 
                                  : 'No sets created yet. Click "Add Scores" to create the first set.'}
                              </div>
                            )}
                            
                            {/* Add new set button - show if previous sets are complete and sets exist */}
                            {/* Allow adding sets for both active and completed matches */}
                            {sets.length > 0 && sets.length < ((fixture as any)?.setCount || 3) && !loadingSets && (
                              (() => {
                                // Check if all sets are complete - handle both isComplete boolean and status === 'completed'
                                const allPreviousComplete = sets.every(s => s.isComplete || (s as any).status === 'completed');
                                return allPreviousComplete ? (
                                  <div className="d-flex justify-content-center mt-3">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={handleCreateSet}
                                      disabled={isSavingSet}
                                      style={{ 
                                        fontWeight: 500, 
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        borderRadius: '6px'
                                      }}
                                    >
                                      Add Set {sets.length + 1} <i className="ri-add-line ms-2"></i>
                                    </Button>
                                  </div>
                                ) : null;
                              })()
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })()}
                    
                    {/* Player of the Match Display - Supported non-set sports */}
                    {isLiveScoringSupported && !isBadmintonOrTT() && (
                    <Card className="mb-4" style={{ border: '2px solid #28a745', borderRadius: 12, boxShadow: '0 2px 8px rgba(40, 167, 69, 0.15)' }}>
                        <Card.Header style={{ backgroundColor: '#d4edda', borderBottom: '2px solid #28a745', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h5 className="mb-0" style={{ fontWeight: 600, color: '#155724' }}>
                            <i className="ri-trophy-line me-2"></i>
                            Player of the Match
                          </h5>
                          {(matchStatus === 'completed' || matchStatus === 'ended' || matchStatus === 'finished') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleOpenPlayerOfMatchModal}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              <i className="ri-edit-line me-1"></i>
                              {playerOfTheMatch.player || secondPlayerOfTheMatch.player ? 'Edit' : 'Add'}
                            </Button>
                          )}
                        </Card.Header>
                        {(playerOfTheMatch.player || secondPlayerOfTheMatch.player) ? (
                        <Card.Body>
                          <Row className="g-3">
                            {playerOfTheMatch.player && (
                              <Col md={secondPlayerOfTheMatch.player ? 6 : 12}>
                                <div style={{ 
                                  padding: '16px', 
                                  backgroundColor: '#f8f9fa', 
                                  borderRadius: 8,
                                  border: '1px solid #dee2e6'
                                }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="ri-trophy-fill me-2" style={{ fontSize: '24px', color: '#ffc107' }}></i>
                                    <strong style={{ fontSize: '14px', color: '#6c757d', textTransform: 'uppercase' }}>Player of the Match</strong>
                                  </div>
                                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#212529', marginTop: '8px' }}>
                                    {(() => {
                                      // Try to find player from loaded players first
                                      const allPlayers = [...teamAPlayers, ...teamBPlayers];
                                      let player = allPlayers.find((p: any) => 
                                        (p._id || p.id) === playerOfTheMatch.player
                                      );
                                      
                                      // If not found, try to find from liveScoreData.playerList if available
                                      // (This is a fallback in case players weren't loaded yet)
                                      if (!player && liveScoreData?.playerList) {
                                        player = liveScoreData.playerList.find((p: any) => 
                                          (p._id || p.id) === playerOfTheMatch.player
                                        );
                                      }
                                      
                                      return player?.name || player?.playerName || 'Loading...';
                                    })()}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '4px' }}>
                                    {(() => {
                                      const teamId = playerOfTheMatch.team;
                                      const team1Id = (fixture as any)?.team1Info?._id || fixture?.teamA?._id;
                                      const team2Id = (fixture as any)?.team2Info?._id || fixture?.teamB?._id;
                                      const isTeam1 = team1Id === teamId;
                                      return isTeam1 ? getTeam1Name() : getTeam2Name();
                                    })()}
                                  </div>
                                </div>
                              </Col>
                            )}
                            {secondPlayerOfTheMatch.player && (
                              <Col md={playerOfTheMatch.player ? 6 : 12}>
                                <div style={{ 
                                  padding: '16px', 
                                  backgroundColor: '#f8f9fa', 
                                  borderRadius: 8,
                                  border: '1px solid #dee2e6'
                                }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="ri-medal-fill me-2" style={{ fontSize: '24px', color: '#6c757d' }}></i>
                                    <strong style={{ fontSize: '14px', color: '#6c757d', textTransform: 'uppercase' }}>Second Player of the Match</strong>
                                  </div>
                                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#212529', marginTop: '8px' }}>
                                    {(() => {
                                      // Try to find player from loaded players first
                                      const allPlayers = [...teamAPlayers, ...teamBPlayers];
                                      let player = allPlayers.find((p: any) => 
                                        (p._id || p.id) === secondPlayerOfTheMatch.player
                                      );
                                      
                                      // If not found, try to find from liveScoreData.playerList if available
                                      // (This is a fallback in case players weren't loaded yet)
                                      if (!player && liveScoreData?.playerList) {
                                        player = liveScoreData.playerList.find((p: any) => 
                                          (p._id || p.id) === secondPlayerOfTheMatch.player
                                        );
                                      }
                                      
                                      return player?.name || player?.playerName || 'Loading...';
                                    })()}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '4px' }}>
                                    {(() => {
                                      const teamId = secondPlayerOfTheMatch.team;
                                      const team1Id = (fixture as any)?.team1Info?._id || fixture?.teamA?._id;
                                      const team2Id = (fixture as any)?.team2Info?._id || fixture?.teamB?._id;
                                      const isTeam1 = team1Id === teamId;
                                      return isTeam1 ? getTeam1Name() : getTeam2Name();
                                    })()}
                                  </div>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Card.Body>
                        ) : (
                          <Card.Body>
                            <div className="text-center py-4">
                              <i className="ri-trophy-line" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '12px' }}></i>
                              <p className="text-muted mb-3">No Player of the Match selected yet</p>
                              {(matchStatus === 'completed' || matchStatus === 'ended' || matchStatus === 'finished') && (
                                <Button
                                  variant="primary"
                                  onClick={handleOpenPlayerOfMatchModal}
                                >
                                  <i className="ri-add-line me-1"></i>
                                  Select Player of the Match
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        )}
                      </Card>
                    )}
                  </>
                ) : (
                  <>
                    {/* Ready to Start Section - Show when match hasn't started */}
                    {(matchStatus === 'upcoming' || !matchStatus || matchStatus === 'pending' || matchStatus === 'yet-to-start') && (
                      <div className="alert alert-success mb-4" style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 8 }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div style={{ flex: 1, minWidth: '250px' }}>
                            <strong>Ready to Start the Match?</strong>
                            <p className="mb-0" style={{ fontSize: 14, color: '#155724' }}>
                              Ensure players, venue, and streaming are set before you start. Once started, match details will be visible in the live section for viewers.
                            </p>
                          </div>
                          <Button 
                            variant="success" 
                            size="sm"
                            onClick={handleStartMatchClick}
                            disabled={isStartingMatch}
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            <i className="ri-play-line me-1"></i>
                            {isStartingMatch ? 'Starting...' : 'Start Match'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Score Update Section */}
                    {(matchStatus === 'ongoing' || matchStatus === 'paused') && (
                      <div className="mb-4">
                        {/* Match Timer Display with Control Buttons - Only for Football */}
                        {!isBadmintonOrTT() && (
                        <div className="mb-3" style={{ width: '100%' }}>
                          <div className="d-flex align-items-center justify-content-center gap-3 mb-2" style={{ width: '100%' }}>
                            {/* Pause/Resume Button - Left side */}
                            {matchStatus === 'ongoing' && (
                              <Button 
                                variant="warning" 
                                onClick={handlePauseMatchClick}
                                disabled={isPausingMatch}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="ri-pause-line me-1"></i>
                                {isPausingMatch ? 'Pausing...' : 'Pause'}
                              </Button>
                            )}
                            {matchStatus === 'paused' && (
                              <Button 
                                variant="warning" 
                                onClick={handleResumeMatchClick}
                                disabled={isStartingMatch}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="ri-play-line me-1"></i>
                                {isStartingMatch ? 'Resuming...' : 'Resume'}
                              </Button>
                            )}
                            
                            {/* Timer Display - Center */}
                            <div style={{ 
                              backgroundColor: matchStatus === 'ongoing' ? '#d4edda' : '#e7f3ff',
                              border: `2px solid ${matchStatus === 'ongoing' ? '#28a745' : '#0d6efd'}`,
                              borderRadius: 12,
                              padding: '16px 32px',
                              minWidth: '200px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>
                                Match Time
                              </div>
                              <div style={{ 
                                fontSize: 32, 
                                fontWeight: 700, 
                                color: matchStatus === 'ongoing' ? '#155724' : '#0d6efd',
                                fontFamily: 'monospace',
                              }}>
                                {formatTimer(matchTimer)}
                              </div>
                              <div style={{ fontSize: 11, color: '#6c757d', marginTop: 4 }}>
                                {matchStatus === 'ongoing' ? 'Running' : 'Paused'}
                              </div>
                            </div>
                            
                            {/* End Match Button - Right side */}
                            {(matchStatus === 'ongoing' || matchStatus === 'paused') && (
                              <Button 
                                variant="danger" 
                                onClick={handleEndMatchClick}
                                disabled={isEndingMatch}
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                <i className="ri-stop-circle-line me-1"></i>
                                {isEndingMatch ? 'Ending...' : 'End'}
                              </Button>
                            )}
                          </div>
                          
                          {/* Timer Control Buttons - Bottom - Aligned with timer display center */}
                          <div className="d-flex align-items-center justify-content-center gap-3 mt-3" style={{ width: '100%' }}>
                            {/* Left spacer - matches Pause/Resume button width */}
                            <div style={{ 
                              flex: '0 0 auto',
                              width: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'auto' : '0px',
                              minWidth: '0px',
                              visibility: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'hidden' : 'hidden',
                              pointerEvents: 'none',
                              display: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'block' : 'none'
                            }}>
                              <Button 
                                variant="warning" 
                                size="sm"
                                style={{ whiteSpace: 'nowrap' }}
                                disabled
                              >
                                <i className="ri-pause-line me-1"></i>
                                {matchStatus === 'ongoing' ? 'Pause' : 'Resume'}
                              </Button>
                            </div>
                            
                            {/* Center section - Timer control buttons */}
                            <div className="d-flex flex-column align-items-center justify-content-center gap-2" style={{ 
                              minWidth: '200px',
                              flex: '0 0 auto'
                            }}>
                              {/* Clear indication message when match is not paused */}
                              {matchStatus === 'ongoing' && (
                                <div 
                                  className="alert alert-warning mb-0 py-2 px-3" 
                                  style={{ 
                                    fontSize: '12px', 
                                    borderRadius: '6px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    marginBottom: '0',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    color: '#856404',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <i className="ri-information-line" style={{ fontSize: '16px' }}></i>
                                  <span><strong>Pause the match first</strong> to update/reset the timer</span>
                                </div>
                              )}
                              <div className="d-flex gap-2 align-items-center">
                                {/* Update Timer Button - Only show when paused */}
                                {matchStatus === 'paused' && (
                                  <Button 
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleOpenUpdateTimerModal}
                                    disabled={isUpdatingTimer}
                                    title="Update timer to a specific time"
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ 
                                      whiteSpace: 'nowrap',
                                      minWidth: '140px'
                                    }}
                                  >
                                    <i className="ri-time-line me-1"></i>
                                    Update Timer
                                  </Button>
                                )}
                                {/* Reset Timer Button */}
                                <Button 
                                  variant={matchStatus === 'paused' ? "outline-secondary" : "outline-secondary"}
                                  size="sm"
                                  onClick={handleResetTimer}
                                  disabled={matchStatus !== 'paused' || isResettingTimer}
                                  title={matchStatus !== 'paused' ? 'Please pause the match before resetting the timer' : 'Reset Timer to 00:00'}
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ 
                                    whiteSpace: 'nowrap',
                                    opacity: matchStatus !== 'paused' ? 0.5 : 1,
                                    cursor: matchStatus !== 'paused' ? 'not-allowed' : 'pointer',
                                    minWidth: '140px'
                                  }}
                                >
                                  <i className="ri-restart-line me-1"></i>
                                  {isResettingTimer ? 'Resetting...' : matchStatus !== 'paused' ? 'Reset Timer (Pause Required)' : 'Reset Timer'}
                                </Button>
                              </div>
                            </div>
                            
                            {/* Right spacer - matches End button width */}
                            <div style={{ 
                              flex: '0 0 auto',
                              width: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'auto' : '0px',
                              minWidth: '0px',
                              visibility: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'hidden' : 'hidden',
                              pointerEvents: 'none',
                              display: (matchStatus === 'ongoing' || matchStatus === 'paused') ? 'block' : 'none'
                            }}>
                              <Button 
                                variant="danger" 
                                size="sm"
                                style={{ whiteSpace: 'nowrap' }}
                                disabled
                              >
                                <i className="ri-stop-circle-line me-1"></i>
                                End
                              </Button>
                            </div>
                          </div>
                        </div>
                        )}
                        
                        
                        {/* Sets Management UI - Only for Badminton and Table Tennis - Show for ALL match statuses including completed */}
                        {isLiveScoringSupported && isBadmintonOrTT() && (() => {
                          const currentMatchStatus: string = matchStatus;
                          const isMatchCompleted = currentMatchStatus === 'completed' || currentMatchStatus === 'ended' || currentMatchStatus === 'finished';
                          const isMatchActive = currentMatchStatus === 'ongoing' || currentMatchStatus === 'paused';
                          
                          return (
                            <div className="mb-4">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">
                                  {isMatchCompleted ? 'Edit Sets' : 'Manage Live Score'}
                                </h6>
                                {/* Show action buttons for active matches (Pause/Resume/End) */}
                                {isMatchActive && (
                                <div className="d-flex gap-2">
                                  {currentMatchStatus === 'ongoing' && (
                                    <Button 
                                      variant="warning" 
                                      size="sm"
                                      onClick={handlePauseMatchClick}
                                      disabled={isPausingMatch}
                                    >
                                      <i className="ri-pause-line me-1"></i>
                                      {isPausingMatch ? 'Pausing...' : 'Pause'}
                                    </Button>
                                  )}
                                  {currentMatchStatus === 'paused' && (
                                    <Button 
                                      variant="warning" 
                                      size="sm"
                                      onClick={handleResumeMatchClick}
                                      disabled={isStartingMatch}
                                    >
                                      <i className="ri-play-line me-1"></i>
                                      {isStartingMatch ? 'Resuming...' : 'Resume'}
                                    </Button>
                                  )}
                                  {/* Add Scores button - show when no sets exist (for both active and completed matches) */}
                                  {sets.length === 0 && !loadingSets && (
                                    <Button 
                                      variant="primary" 
                                      size="sm"
                                      onClick={handleCreateSet}
                                      disabled={isSavingSet}
                                    >
                                      <i className="ri-add-line me-1"></i>
                                      {isSavingSet ? 'Creating...' : 'Add Scores'}
                                    </Button>
                                  )}
                                  <Button 
                                    variant="danger" 
                                    size="sm"
                                    onClick={handleEndMatchClick}
                                    disabled={isEndingMatch}
                                  >
                                    <i className="ri-stop-circle-line me-1"></i>
                                    {isEndingMatch ? 'Ending...' : 'End'}
                                  </Button>
                                </div>
                              )}
                              {/* Show "Add Scores" button for completed matches when no sets exist */}
                              {isMatchCompleted && sets.length === 0 && !loadingSets && (
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={handleCreateSet}
                                    disabled={isSavingSet}
                                  >
                                    <i className="ri-add-line me-1"></i>
                                    {isSavingSet ? 'Creating...' : 'Add Scores'}
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {loadingSets ? (
                              <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading sets...</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {/* Display existing sets - Always show set cards for editing, regardless of match status */}
                                {sets.length > 0 ? (
                                  sets.map((set, index) => {
                                  // Check if set is completed - handle both isComplete boolean and status === 'completed'
                                  const isCompleted = set.isComplete || (set as any).status === 'completed';
                                  const isActive = !isCompleted && index === sets.filter(s => !(s.isComplete || (s as any).status === 'completed')).length - 1;
                                  
                                  // Calculate winner from points if not set by API
                                  // Handle Draw case: if both points are 0, it's a draw
                                  const isDraw = isCompleted && set.side1Points === 0 && set.side2Points === 0;
                                  const calculatedWinner = isCompleted && !isDraw
                                    ? (set.winner || (set.side1Points > set.side2Points ? 'side1' : set.side2Points > set.side1Points ? 'side2' : null))
                                    : null;
                                  
                                  return (
                                    <Card key={set.setNumber || index} id={`set-card-${set.setNumber}`} className="mb-3" style={{ 
                                      border: isCompleted ? '2px solid #1B9E5C' : '1px solid #e0e0e0', 
                                      borderRadius: 12, 
                                      boxShadow: isCompleted ? '0 2px 8px rgba(27, 158, 92, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                      overflow: 'hidden'
                                    }}>
                                      <Card.Header style={{ 
                                        backgroundColor: isCompleted ? '#E9FCF2' : '#f8f9fc', 
                                        borderBottom: isCompleted ? '2px solid #1B9E5C' : '1px solid #e0e0e0',
                                        padding: '12px 16px'
                                      }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div className="d-flex align-items-center gap-2">
                                            <strong style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>Set {set.setNumber}</strong>
                                            {isCompleted && getCompletedBadge()}
                                          </div>
                                          {isCompleted && (
                                            <div className="d-flex gap-2">
                                              <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => {
                                                  setEditingSet(set);
                                                  // Initialize lastSavedSetData if not already set for this set
                                                  setLastSavedSetData(prev => {
                                                    if (!prev.has(set.setNumber)) {
                                                      const newMap = new Map(prev);
                                                      newMap.set(set.setNumber, {
                                                        side1Points: set.side1Points || 0,
                                                        side2Points: set.side2Points || 0
                                                      });
                                                      return newMap;
                                                    }
                                                    return prev;
                                                  });
                                                }}
                                                style={{ 
                                                  minWidth: '36px', 
                                                  padding: '6px 10px',
                                                  borderColor: '#006EFF',
                                                  color: '#006EFF'
                                                }}
                                                title="Edit set"
                                              >
                                                <i className="ri-edit-line"></i>
                                              </Button>
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => setSetToDelete(set.setNumber)}
                                                style={{ 
                                                  minWidth: '36px', 
                                                  padding: '6px 10px',
                                                  borderColor: '#FC5C65',
                                                  color: '#FC5C65'
                                                }}
                                                title="Delete set"
                                              >
                                                <i className="ri-delete-bin-line"></i>
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      </Card.Header>
                                      <Card.Body style={{ padding: '20px' }}>
                                        {/* Show read-only view for completed sets unless explicitly editing */}
                                        {isCompleted && !(editingSet?.setNumber === set.setNumber) ? (
                                          <Row className="g-3">
                                            <Col md={6}>
                                              <div className="text-center p-4" style={{ 
                                                backgroundColor: '#ffffff', 
                                                borderRadius: 12,
                                                border: '1px solid #e9ecef',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                              }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                                  <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                                  <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam1Name()}</div>
                                                </div>
                                                <div className="mb-3">
                                                  {getResultBadge(calculatedWinner === 'side1', isDraw)}
                                                </div>
                                                <div style={{ fontSize: 32, fontWeight: 700, color: '#212529', marginTop: 8, marginBottom: 4 }}>
                                                  {set.side1Points}
                                                </div>
                                                <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Points</div>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="text-center p-4" style={{ 
                                                backgroundColor: '#ffffff', 
                                                borderRadius: 12,
                                                border: '1px solid #e9ecef',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                              }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                                  <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                                  <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam2Name()}</div>
                                                </div>
                                                <div className="mb-3">
                                                  {getResultBadge(calculatedWinner === 'side2', isDraw)}
                                                </div>
                                                <div style={{ fontSize: 32, fontWeight: 700, color: '#212529', marginTop: 8, marginBottom: 4 }}>
                                                  {set.side2Points}
                                                </div>
                                                <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>Points</div>
                                              </div>
                                            </Col>
                                          </Row>
                                        ) : (
                                          <Row className="g-3">
                                            <Col md={6}>
                                              <div className="text-center p-4" style={{ 
                                                backgroundColor: '#ffffff', 
                                                borderRadius: 12,
                                                border: '1px solid #e9ecef',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                              }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                                  <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                                  <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam1Name()}</div>
                                                </div>
                                                <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12, fontWeight: 500 }}>Points</div>
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                      const newSet = { ...set, side1Points: Math.max(0, set.side1Points - 1) };
                                                      setEditingSet(newSet);
                                                    }}
                                                  >
                                                    <i className="ri-subtract-line"></i>
                                                  </Button>
                                                  <div style={{ 
                                                    width: '90px', 
                                                    textAlign: 'center', 
                                                    fontSize: 24, 
                                                    fontWeight: 700,
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '8px',
                                                    padding: '8px',
                                                    backgroundColor: '#ffffff',
                                                    color: '#212529',
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}>
                                                    {editingSet && editingSet.setNumber === set.setNumber ? editingSet.side1Points : set.side1Points}
                                                  </div>
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                      const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                      const newSet = { ...currentEditing, side1Points: currentEditing.side1Points + 1 };
                                                      setEditingSet(newSet);
                                                    }}
                                                  >
                                                    <i className="ri-add-line"></i>
                                                  </Button>
                                                </div>
                                              </div>
                                            </Col>
                                            <Col md={6}>
                                              <div className="text-center p-4" style={{ 
                                                backgroundColor: '#ffffff', 
                                                borderRadius: 12,
                                                border: '1px solid #e9ecef',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                                              }}>
                                                <div className="d-flex align-items-center justify-content-center gap-2 mb-3">
                                                  <i className="ri-user-line" style={{ fontSize: '20px', color: '#495057' }}></i>
                                                  <div style={{ fontSize: 15, color: '#212529', fontWeight: 500 }}>{getTeam2Name()}</div>
                                                </div>
                                                <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 12, fontWeight: 500 }}>Points</div>
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                      const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                      const newSet = { ...currentEditing, side2Points: Math.max(0, currentEditing.side2Points - 1) };
                                                      setEditingSet(newSet);
                                                    }}
                                                  >
                                                    <i className="ri-subtract-line"></i>
                                                  </Button>
                                                  <div style={{ 
                                                    width: '90px', 
                                                    textAlign: 'center', 
                                                    fontSize: 24, 
                                                    fontWeight: 700,
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '8px',
                                                    padding: '8px',
                                                    backgroundColor: '#ffffff',
                                                    color: '#212529',
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                  }}>
                                                    {editingSet && editingSet.setNumber === set.setNumber ? editingSet.side2Points : set.side2Points}
                                                  </div>
                                                  <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                      const currentEditing = editingSet && editingSet.setNumber === set.setNumber ? editingSet : set;
                                                      const newSet = { ...currentEditing, side2Points: currentEditing.side2Points + 1 };
                                                      setEditingSet(newSet);
                                                    }}
                                                  >
                                                    <i className="ri-add-line"></i>
                                                  </Button>
                                                </div>
                                              </div>
                                            </Col>
                                          </Row>
                                        )}
                                        
                                        {(!isCompleted || (isCompleted && editingSet && editingSet.setNumber === set.setNumber)) && (
                                          <div className="d-flex gap-2 mt-3">
                                            <Button
                                              variant="outline-secondary"
                                              size="sm"
                                              onClick={() => {
                                                handleResetSet(set.setNumber);
                                              }}
                                              disabled={isSavingSet}
                                            >
                                              {isSavingSet ? 'Resetting...' : 'Reset'}
                                            </Button>
                                            <Button
                                              variant="primary"
                                              size="sm"
                                              onClick={() => {
                                                const setToSave = editingSet || set;
                                                handleSaveSet(setToSave);
                                              }}
                                              disabled={isSavingSet || !(editingSet && editingSet.setNumber === set.setNumber && hasSetChanges(set.setNumber))}
                                            >
                                              {isSavingSet ? 'Saving...' : 'Save & Update'}
                                            </Button>
                                            {!isCompleted && (
                                              <Button
                                                variant="link"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  // Store scroll position to prevent jumping to top
                                                  const scrollPosition = window.scrollY;
                                                  setSetToMarkComplete(set.setNumber);
                                                  setShowMarkCompleteModal(true);
                                                  // Restore scroll position immediately
                                                  requestAnimationFrame(() => {
                                                    window.scrollTo(0, scrollPosition);
                                                  });
                                                }}
                                                disabled={isSavingSet}
                                                style={{ color: '#0d6efd' }}
                                              >
                                                Mark the set as Complete
                                              </Button>
                                            )}
                                            {isCompleted && editingSet && editingSet.setNumber === set.setNumber && (
                                              <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                  setEditingSet(null);
                                                }}
                                                disabled={isSavingSet}
                                              >
                                                Cancel
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </Card.Body>
                                    </Card>
                                  );
                                  })
                                ) : (
                                  <div className="text-center py-4" style={{ color: '#6c757d', fontSize: '14px' }}>
                                    {isMatchCompleted 
                                      ? 'No sets available. Click "Add Scores" above to create sets and update scores.' 
                                      : 'No sets created yet. Click "Add Scores" to create the first set.'}
                                  </div>
                                )}
                                
                                {/* Add new set button - show if previous sets are complete and sets exist */}
                                {/* This button appears after first set is marked complete, replacing "Add Scores" flow */}
                                {/* Allow adding sets for both active and completed matches */}
                                {sets.length > 0 && sets.length < ((fixture as any)?.setCount || 3) && !loadingSets && (
                                  (() => {
                                    // Check if all sets are complete - handle both isComplete boolean and status === 'completed'
                                    const allPreviousComplete = sets.every(s => s.isComplete || (s as any).status === 'completed');
                                    return allPreviousComplete ? (
                                      <div className="d-flex justify-content-center mt-3">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={handleCreateSet}
                                          disabled={isSavingSet}
                                          style={{ 
                                            fontWeight: 500, 
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            borderRadius: '6px'
                                          }}
                                        >
                                          Add Set {sets.length + 1} <i className="ri-add-line ms-2"></i>
                                        </Button>
                                      </div>
                                    ) : null;
                                  })()
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })()}
                        
                        {/* Current Score section - Show set-wise table for Badminton/TT, overall score for Football */}
                        {isLiveScoringSupported && (isBadmintonOrTT() ? (
                          <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">Match Score</h6>
                              {(() => {
                                const status: string = matchStatus;
                                const isCompleted = status === 'completed' || status === 'ended' || status === 'finished';
                                return isCompleted && !editingSet && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      // Enable edit mode for all sets
                                      const firstSet = sets.find(s => s.isComplete || (s as any).status === 'completed');
                                      if (firstSet) {
                                        setEditingSet(firstSet);
                                      }
                                    }}
                                    style={{ 
                                      padding: '6px 12px',
                                      borderColor: '#006EFF',
                                      color: '#006EFF'
                                    }}
                                  >
                                    <i className="ri-edit-line me-1"></i>
                                    Edit Scores
                                  </Button>
                                );
                              })()}
                            </div>
                            {(() => {
                              const status: string = matchStatus;
                              const isCompleted = status === 'completed' || status === 'ended' || status === 'finished';
                              // When match is completed, show read-only table at top, set cards below for editing
                              return renderSetWiseScoreTable(false);
                            })()}
                          </div>
                        ) : (
                          <div>
                        <h6 className="mb-3">Current Score</h6>
                        <Row className="g-3 mb-4">
                          <Col md={6}>
                            <div className="text-center p-3" style={{ backgroundColor: '#f8f9fc', borderRadius: 8 }}>
                              <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>{getTeam1Name()}</div>
                              <div style={{ fontSize: 32, fontWeight: 700, color: '#212529' }}>{teamAScore}</div>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="text-center p-3" style={{ backgroundColor: '#f8f9fc', borderRadius: 8 }}>
                              <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>{getTeam2Name()}</div>
                              <div style={{ fontSize: 32, fontWeight: 700, color: '#212529' }}>{teamBScore}</div>
                            </div>
                          </Col>
                        </Row>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Live Scores Events Table - Only visible when match has started or ended - Hide for Badminton/TT */}
                {isLiveScoringSupported && (matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended') && !isBadmintonOrTT() && (
                  <div className="mb-4 mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Score Updates</h6>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={async () => {
                          setShowAddScoreForm(!showAddScoreForm);
                          if (!showAddScoreForm) {
                            // Reset form when opening
                            setSelectedPlayer(null);
                            setEventType('');
                            setEventTime('');
                            setFormError(null);
                            // Always load teams/players from live scores API when opening the form
                            // This ensures we have the latest player data from the API
                            if (matchId) {
                              // Reset team selection when opening form
                              setSelectedTeam('');
                              setSelectedPlayer(null);
                              // Load players from API with force reload to ensure fresh data
                              await loadLiveScores(matchId, true);
                            }
                          }
                        }}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        <i className={showAddScoreForm ? 'ri-close-line me-1' : 'ri-add-line me-1'}></i>
                        {showAddScoreForm ? 'Cancel' : 'Add New'}
                      </Button>
                    </div>
                  
                  {/* Add Score Form - Shown when Add New is clicked */}
                  {showAddScoreForm && (matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended') && (
                    <Card className="mb-4" style={{ border: '2px solid #0d6efd', borderRadius: 8 }}>
                      <Card.Header style={{ backgroundColor: '#e7f3ff' }}>
                        <h6 className="mb-0">Add New Score Event</h6>
                      </Card.Header>
                      <Card.Body>
                        {formError && <div className="alert alert-danger mb-3">{formError}</div>}
                        <Row className="g-3">
                          {/* Team Selection */}
                          <Col md={12}>
                            <Form.Label style={{ fontWeight: 600, marginBottom: 8 }}>Choose the team</Form.Label>
                            <div className="d-flex gap-3">
                              <Form.Check
                                type="radio"
                                id="teamA-radio"
                                name="teamSelection"
                                label={getTeam1Name()}
                                checked={selectedTeam === 'teamA'}
                                onChange={async () => {
                                  setSelectedTeam('teamA');
                                  setSelectedPlayer(null);
                                  // If players haven't loaded yet, load them
                                  if (teamAPlayers.length === 0 && matchId && !loadingLiveScores) {
                                    await loadLiveScores(matchId, true);
                                  }
                                }}
                                style={{ flex: 1 }}
                              />
                              <Form.Check
                                type="radio"
                                id="teamB-radio"
                                name="teamSelection"
                                label={getTeam2Name()}
                                checked={selectedTeam === 'teamB'}
                                onChange={async () => {
                                  setSelectedTeam('teamB');
                                  setSelectedPlayer(null);
                                  // If players haven't loaded yet, load them
                                  if (teamBPlayers.length === 0 && matchId && !loadingLiveScores) {
                                    await loadLiveScores(matchId, true);
                                  }
                                }}
                                style={{ flex: 1 }}
                              />
                            </div>
                          </Col>

                          {/* Player Selection - Optional */}
                          <Col md={12}>
                            <Form.Label style={{ fontWeight: 600 }}>
                              Player <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.9em' }}>(Optional)</span>
                            </Form.Label>
                            <Select
                              key={`player-select-${selectedTeam}-${teamAPlayers.length}-${teamBPlayers.length}`}
                              classNamePrefix="react-select"
                              value={selectedPlayer}
                              onChange={(option: any) => setSelectedPlayer(option)}
                              options={getAvailablePlayers}
                              placeholder={
                                loadingLiveScores 
                                  ? "Loading players..." 
                                  : !selectedTeam 
                                    ? "Select a team first" 
                                    : getAvailablePlayers.length === 0 || (getAvailablePlayers.length === 1 && getAvailablePlayers[0].isDisabled)
                                      ? "No players available (optional)" 
                                      : "Choose the player (optional)"
                              }
                              isDisabled={loadingLiveScores || !selectedTeam}
                              isLoading={loadingLiveScores}
                              isClearable
                              noOptionsMessage={() => {
                                if (loadingLiveScores) return "Loading players...";
                                if (!selectedTeam) return "Please select a team first";
                                const players = selectedTeam === 'teamA' ? teamAPlayers : teamBPlayers;
                                return players && players.length === 0 
                                  ? `No players found for ${selectedTeam === 'teamA' ? (fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A') : (fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B')}. You can still add score without selecting a player.`
                                  : "No players available for this team (optional)";
                              }}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  minHeight: '38px',
                                }),
                              }}
                            />
                            {selectedTeam && !loadingLiveScores && (
                              <Form.Text className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
                                {(() => {
                                  const players = selectedTeam === 'teamA' ? teamAPlayers : teamBPlayers;
                                  if (players && players.length > 0) {
                                    return <>Player selection is optional. {players.length} player{players.length !== 1 ? 's' : ''} available.</>;
                                  } else {
                                    return (
                                      <span style={{ color: '#6c757d' }}>
                                        No players found for {selectedTeam === 'teamA' ? (fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A') : (fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B')}. You can still add score without selecting a player.
                                      </span>
                                    );
                                  }
                                })()}
                              </Form.Text>
                            )}
                          </Col>

                          {/* Event Type Selection */}
                          <Col md={12}>
                            <Form.Label style={{ fontWeight: 600 }}>Choose the update</Form.Label>
                            <div className="d-flex gap-3 flex-wrap">
                              <Form.Check
                                type="radio"
                                id="goal-radio"
                                name="eventType"
                                label="Goal"
                                checked={eventType === 'goal'}
                                onChange={() => setEventType('goal')}
                              />
                              <Form.Check
                                type="radio"
                                id="penalty-radio"
                                name="eventType"
                                label="Penalty"
                                checked={eventType === 'penalty'}
                                onChange={() => setEventType('penalty')}
                              />
                              <Form.Check
                                type="radio"
                                id="selfGoal-radio"
                                name="eventType"
                                label="Self Goal"
                                checked={eventType === 'selfGoal'}
                                onChange={() => setEventType('selfGoal')}
                              />
                            </div>
                          </Col>

                          {/* Time Input */}
                          <Col md={12}>
                            <Form.Label style={{ fontWeight: 600 }}>Time</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g., 45:30"
                              value={eventTime}
                              onChange={(e) => handleTimeInputChange(e.target.value)}
                              maxLength={5}
                              pattern="[0-9]{1,2}:[0-9]{2}"
                            />
                            <Form.Text className="text-muted">Enter time in format MM:SS (max 99:59)</Form.Text>
                            {eventTime && (() => {
                              const parts = eventTime.split(':');
                              if (parts.length === 2) {
                                const minutes = parseInt(parts[0], 10);
                                const seconds = parseInt(parts[1], 10);
                                if (isNaN(minutes) || isNaN(seconds) || minutes > 99 || seconds > 59) {
                                  return <Form.Text className="text-danger">Invalid time format. Maximum is 99:59</Form.Text>;
                                }
                              }
                              return null;
                            })()}
                          </Col>

                          {/* Add Score Button */}
                          <Col md={12}>
                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                onClick={handleAddScoreUpdate}
                                disabled={!selectedTeam || !eventType || !eventTime || isAddingScore}
                                style={{ flex: 1 }}
                              >
                                <i className="ri-add-line me-1"></i>
                                {isAddingScore ? 'Adding...' : 'Add Score Event'}
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() => {
                                  setShowAddScoreForm(false);
                                  setSelectedPlayer(null);
                                  setEventType('');
                                  setEventTime('');
                                  setFormError(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                  
                  <div className="manage-tournaments-table-container">
                    <Table className="mb-0 manage-tournaments-table fixtures-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Team</th>
                          <th>Player</th>
                          <th>Event Type</th>
                          {(matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended') && (
                            <th>Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {loadingLiveScores ? (
                          <tr>
                            <td colSpan={matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended' ? 5 : 4}>
                              <ShimmerLoader type="table" rows={3} />
                            </td>
                          </tr>
                        ) : liveScoreEvents.length > 0 ? (
                          liveScoreEvents.map((event: any) => {
                            // Find player name from playerList or fixture data
                            // If event.player is null/undefined, show "-" instead of "Unknown Player"
                            let playerName = '-';
                            if (event.player) {
                              const player = (fixture as any)?.playerList?.find((p: any) => p._id === event.player) || 
                                             teamAPlayers.find((p: any) => (p._id || p.id) === event.player) ||
                                             teamBPlayers.find((p: any) => (p._id || p.id) === event.player);
                              playerName = player?.name || player?.playerName || '-';
                            }
                            
                            // Find team name - check both teamA/teamB and teamAInfo/teamBInfo
                            const teamId = event.team;
                            const isTeamA = fixture?.teamA?._id === teamId || (fixture as any)?.teamAInfo?._id === teamId;
                            const teamName = isTeamA 
                              ? (fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A') 
                              : (fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B');
                            
                            // Format time (seconds to MM:SS)
                            const timeInSeconds = event.time || 0;
                            const minutes = Math.floor(timeInSeconds / 60);
                            const seconds = timeInSeconds % 60;
                            const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            
                            // Format event type
                            const eventTypeLabel = event.type === 'penalty' ? 'Penalty' : 
                                                  event.type === 'self-goal' || event.type === 'selfGoal' ? 'Self Goal' : 'Goal';
                            const eventTypeBadge = event.type === 'penalty' ? 'warning' : 
                                                  event.type === 'self-goal' || event.type === 'selfGoal' ? 'danger' : 'success';
                            
                            return (
                              <tr key={event._id}>
                                <td>{formattedTime}</td>
                                <td>{teamName}</td>
                                <td>{playerName}</td>
                                <td>
                                  <Badge bg={eventTypeBadge}>{eventTypeLabel}</Badge>
                                </td>
                                {(matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended') && (
                                  <td>
                                    <div className="tournament-actions">
                                      <button
                                        className="action-btn btn-edit"
                                        onClick={() => handleEditEvent(event)}
                                        title="Edit"
                                        type="button"
                                      >
                                        <i className="ri-edit-line"></i>
                                      </button>
                                      <button
                                        className="action-btn btn-delete"
                                        onClick={() => {
                                          setEventToDelete(event);
                                          setShowDeleteModal(true);
                                        }}
                                        title="Delete"
                                        type="button"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={matchStatus === 'ongoing' || matchStatus === 'paused' || matchStatus === 'completed' || matchStatus === 'ended' ? 5 : 4}>
                              <div className="text-center py-5">
                                <div className="empty-state">
                                  <div className="empty-state-icon">
                                    <i className="ri-football-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                                  </div>
                                  <div className="empty-state-title">No Score Updates Yet</div>
                                  <div className="empty-state-description">
                                    No score updates have been recorded for this match.
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Edit Event Form (shown when editingEvent is set) */}
                  {editingEvent && (
                  <Card className="mb-4" style={{ border: '2px solid #0d6efd', borderRadius: 8 }}>
                    <Card.Header style={{ backgroundColor: '#e7f3ff' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Edit Score Event</h6>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => {
                            setEditingEvent(null);
                            setSelectedPlayer(null);
                            setEventType('');
                            setEventTime('');
                            setFormError(null);
                            setHasFormChanged(false);
                          }}
                        >
                          <i className="ri-close-line"></i>
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {formError && <div className="alert alert-danger mb-3">{formError}</div>}
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Label style={{ fontWeight: 600 }}>Team</Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="edit-teamA-radio"
                              name="edit-teamSelection"
                              label={fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A'}
                              checked={selectedTeam === 'teamA'}
                              onChange={async () => {
                                const previousTeam = selectedTeam;
                                setSelectedTeam('teamA');
                                setSelectedPlayer(null);
                                setHasFormChanged(true);
                                
                                // If team changed, ensure players are loaded for the new team
                                if (previousTeam !== 'teamA' && matchId && teamAPlayers.length === 0 && !loadingLiveScores) {
                                  await loadLiveScores(matchId, true);
                                }
                              }}
                            />
                            <Form.Check
                              type="radio"
                              id="edit-teamB-radio"
                              name="edit-teamSelection"
                              label={fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B'}
                              checked={selectedTeam === 'teamB'}
                              onChange={async () => {
                                const previousTeam = selectedTeam;
                                setSelectedTeam('teamB');
                                setSelectedPlayer(null);
                                setHasFormChanged(true);
                                
                                // If team changed, ensure players are loaded for the new team
                                if (previousTeam !== 'teamB' && matchId && teamBPlayers.length === 0 && !loadingLiveScores) {
                                  await loadLiveScores(matchId, true);
                                }
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={12}>
                          <Form.Label style={{ fontWeight: 600 }}>
                            Player <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.9em' }}>(Optional)</span>
                          </Form.Label>
                          <Select
                            key={`edit-player-select-${selectedTeam}-${teamAPlayers.length}-${teamBPlayers.length}`}
                            classNamePrefix="react-select"
                            value={selectedPlayer}
                            onChange={(option: any) => {
                              setSelectedPlayer(option);
                              setHasFormChanged(true);
                            }}
                            options={getAvailablePlayers}
                            placeholder={
                              loadingLiveScores 
                                ? "Loading players..." 
                                : !selectedTeam 
                                  ? "Select a team first" 
                                  : getAvailablePlayers.length === 0 || (getAvailablePlayers.length === 1 && getAvailablePlayers[0].isDisabled)
                                    ? "No players available (optional)" 
                                    : "Choose the player (optional)"
                            }
                            isDisabled={loadingLiveScores || !selectedTeam}
                            isLoading={loadingLiveScores}
                            isClearable
                            noOptionsMessage={() => {
                              if (loadingLiveScores) return "Loading players...";
                              if (!selectedTeam) return "Please select a team first";
                              const players = selectedTeam === 'teamA' ? teamAPlayers : teamBPlayers;
                              return players && players.length === 0 
                                ? `No players found for ${selectedTeam === 'teamA' ? (fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A') : (fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B')}. You can still update score without selecting a player.`
                                : "No players available for this team (optional)";
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: '38px',
                              }),
                            }}
                          />
                          {selectedTeam && !loadingLiveScores && (
                            <Form.Text className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
                              {(() => {
                                const players = selectedTeam === 'teamA' ? teamAPlayers : teamBPlayers;
                                if (players && players.length > 0) {
                                  return <>Player selection is optional. {players.length} player{players.length !== 1 ? 's' : ''} available.</>;
                                } else {
                                  return (
                                    <span style={{ color: '#6c757d' }}>
                                      No players found for {selectedTeam === 'teamA' ? (fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A') : (fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B')}. You can still update score without selecting a player.
                                    </span>
                                  );
                                }
                              })()}
                            </Form.Text>
                          )}
                        </Col>
                        <Col md={12}>
                          <Form.Label style={{ fontWeight: 600 }}>Event Type</Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="edit-goal-radio"
                              name="edit-eventType"
                              label="Goal"
                              checked={eventType === 'goal'}
                              onChange={() => {
                                setEventType('goal');
                                setHasFormChanged(true);
                              }}
                            />
                            <Form.Check
                              type="radio"
                              id="edit-penalty-radio"
                              name="edit-eventType"
                              label="Penalty"
                              checked={eventType === 'penalty'}
                              onChange={() => {
                                setEventType('penalty');
                                setHasFormChanged(true);
                              }}
                            />
                            <Form.Check
                              type="radio"
                              id="edit-selfGoal-radio"
                              name="edit-eventType"
                              label="Self Goal"
                              checked={eventType === 'selfGoal'}
                              onChange={() => {
                                setEventType('selfGoal');
                                setHasFormChanged(true);
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={12}>
                          <Form.Label style={{ fontWeight: 600 }}>Time</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., 45:30"
                            value={eventTime}
                            onChange={(e) => {
                              handleTimeInputChange(e.target.value);
                              setHasFormChanged(true);
                            }}
                            maxLength={5}
                            pattern="[0-9]{1,2}:[0-9]{2}"
                          />
                          <Form.Text className="text-muted">Enter time in format MM:SS (max 99:59)</Form.Text>
                          {eventTime && (() => {
                            const parts = eventTime.split(':');
                            if (parts.length === 2) {
                              const minutes = parseInt(parts[0], 10);
                              const seconds = parseInt(parts[1], 10);
                              if (isNaN(minutes) || isNaN(seconds) || minutes > 99 || seconds > 59) {
                                return <Form.Text className="text-danger">Invalid time format. Maximum is 99:59</Form.Text>;
                              }
                            }
                            return null;
                          })()}
                        </Col>
                        <Col md={12}>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              onClick={handleSaveEditedEvent}
                              disabled={!eventType || !eventTime || !selectedTeam || !hasFormChanged || isAddingScore}
                            >
                              <i className={isAddingScore ? 'ri-loader-4-line me-1 spin' : 'ri-save-line me-1'}></i>
                              {isAddingScore ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                setEditingEvent(null);
                                setSelectedPlayer(null);
                                setEventType('');
                                setEventTime('');
                                setFormError(null);
                                setHasFormChanged(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  )}
                </div>
                )}
                    </>
                  </Card.Body>
                </Card>

                <MatchHighlightsSection
                  matchId={matchId || ''}
                  sport={getSportValue()}
                  tournamentId={tournamentId}
                  tournamentSeasonId={tournamentSeasonId}
                  fixture={fixture}
                  liveScoreData={liveScoreData}
                />
              </>
            )}
          </>
        ) : null}

        {matchId && (
          <MatchGallerySection matchId={matchId} sport={getSportValue() || 'Football'} />
        )}

        {/* Delete Fixture Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal && !eventToDelete}
          handleClose={() => { setShowDeleteModal(false); setDeleteError(null); }}
          deleteModalFunction={handleDelete}
          title="Delete Fixture"
          message={`Are you sure you want to delete Match ${fixture?.matchNum}? This action cannot be undone.`}
          isLoading={isDeleting}
          error={deleteError}
        />
        
        {/* Delete Event Confirmation Modal */}
        <DeleteModal
          show={isLiveScoringSupported && showDeleteModal && !!eventToDelete}
          handleClose={() => { setShowDeleteModal(false); setEventToDelete(null); }}
          deleteModalFunction={handleDeleteEvent}
          title="Delete Score Event"
          message={`Are you sure you want to delete this score event? This action cannot be undone.`}
          isLoading={false}
          error={null}
        />
        
        {/* Mark Set as Complete Confirmation Modal */}
        <DeleteModal
          show={isLiveScoringSupported && showMarkCompleteModal}
          handleClose={() => { 
            setShowMarkCompleteModal(false); 
            setSetToMarkComplete(null); 
            setFormError(null);
          }}
          deleteModalFunction={handleConfirmMarkComplete}
          title="Mark Set as Completed"
          message={`Mark This set as completed. This will lock the scores. You won't be able to edit unless reopened.`}
          confirmLabel="Yes, Mark as Complete"
          cancelLabel="Cancel"
          isLoading={isSavingSet}
          error={formError}
        />

        {/* Start Match Confirmation Modal */}
        <Modal show={showStartMatchModal} onHide={() => setShowStartMatchModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Start Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="text-success mb-3">
                <i className="ri-play-circle-line" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5>Are you sure you want to start the match?</h5>
              <p className="text-muted">
                Once started, the match timer will begin and match details will be visible in the live section for viewers. 
                Make sure all preparations are complete before starting.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStartMatchModal(false)}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleStartMatch} disabled={isStartingMatch}>
              {isStartingMatch ? 'Starting...' : 'Start Match'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Pause Match Confirmation Modal */}
        <Modal show={showPauseMatchModal} onHide={() => setShowPauseMatchModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Pause Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="text-warning mb-3">
                <i className="ri-pause-circle-line" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5>Are you sure you want to pause the match?</h5>
              <p className="text-muted">
                The match timer will be paused and the match status will be updated. You can resume the match anytime to continue.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPauseMatchModal(false)}>
              Cancel
            </Button>
            <Button variant="warning" onClick={handlePauseMatch} disabled={isPausingMatch}>
              {isPausingMatch ? 'Pausing...' : 'Pause Match'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Resume Match Confirmation Modal */}
        <Modal show={showResumeMatchModal} onHide={() => setShowResumeMatchModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Resume Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="text-primary mb-3">
                <i className="ri-play-circle-line" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5>Are you sure you want to resume the match?</h5>
              <p className="text-muted">
                The match timer will resume from where it was paused and the match status will be updated to ongoing.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResumeMatchModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleResumeMatch} disabled={isStartingMatch}>
              {isStartingMatch ? 'Resuming...' : 'Resume Match'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* End Match Confirmation Modal */}
        <Modal show={showEndMatchModal} onHide={() => setShowEndMatchModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>End Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="text-danger mb-3">
                <i className="ri-stop-circle-line" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5>Are you sure you want to end the match?</h5>
              <p className="text-muted">
                This action will permanently end the match. The match timer will stop and final scores and player statistics will be recorded and saved. 
                This action cannot be undone.
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEndMatchModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleEndMatch} disabled={isEndingMatch}>
              {isEndingMatch ? 'Ending...' : 'End Match'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Player of the Match Selection Modal */}
        <Modal show={isLiveScoringSupported && showPlayerOfMatchModal} onHide={handleClosePlayerOfMatchModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Select Player of the Match</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && (
              <div className="alert alert-danger mb-3">{formError}</div>
            )}
            <Row className="g-3">
              {/* Player of the Match */}
              <Col md={6}>
                <Form.Label style={{ fontWeight: 600 }}>
                  Player of the Match <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.9em' }}>(Optional)</span>
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={playerOfTheMatch.player ? (() => {
                    const foundPlayer = [...teamAPlayers, ...teamBPlayers].find((p: any) => 
                      (p._id || p.id || p.value) === playerOfTheMatch.player
                    );
                    return {
                      value: playerOfTheMatch.player,
                      label: foundPlayer?.name || foundPlayer?.playerName || foundPlayer?.label || 'Unknown Player'
                    };
                  })() : null}
                  onChange={(option: any) => {
                    const selectedPlayer = [...teamAPlayers, ...teamBPlayers].find((p: any) => 
                      (p._id || p.id || p.value) === option?.value
                    );
                    const teamId = selectedPlayer?.teamId || 
                                  selectedPlayer?.team?._id || 
                                  selectedPlayer?.team ||
                                  (teamAPlayers.find((p: any) => (p._id || p.id || p.value) === option?.value) 
                                    ? (fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id) 
                                    : (fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id));
                    setPlayerOfTheMatch({
                      player: option?.value || null,
                      team: teamId || null
                    });
                  }}
                  options={[...teamAPlayers, ...teamBPlayers]
                    .filter((p: any) => p && (p._id || p.id || p.value))
                    .map((p: any) => ({
                      value: p._id || p.id || p.value || '',
                      label: p.name || p.playerName || p.label || 'Unknown Player'
                    }))}
                  placeholder="Select Player of the Match"
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                    }),
                  }}
                />
              </Col>
              
              {/* Second Player of the Match */}
              <Col md={6}>
                <Form.Label style={{ fontWeight: 600 }}>Second Player of the Match (Optional)</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={secondPlayerOfTheMatch.player ? (() => {
                    const foundPlayer = [...teamAPlayers, ...teamBPlayers].find((p: any) => 
                      (p._id || p.id || p.value) === secondPlayerOfTheMatch.player
                    );
                    return {
                      value: secondPlayerOfTheMatch.player,
                      label: foundPlayer?.name || foundPlayer?.playerName || foundPlayer?.label || 'Unknown Player'
                    };
                  })() : null}
                  onChange={(option: any) => {
                    const selectedPlayer = [...teamAPlayers, ...teamBPlayers].find((p: any) => 
                      (p._id || p.id || p.value) === option?.value
                    );
                    const teamId = selectedPlayer?.teamId || 
                                  selectedPlayer?.team?._id || 
                                  selectedPlayer?.team ||
                                  (teamAPlayers.find((p: any) => (p._id || p.id || p.value) === option?.value) 
                                    ? (fixture?.teamA?._id || (fixture as any)?.teamAInfo?._id) 
                                    : (fixture?.teamB?._id || (fixture as any)?.teamBInfo?._id));
                    setSecondPlayerOfTheMatch({
                      player: option?.value || null,
                      team: teamId || null
                    });
                  }}
                  options={[...teamAPlayers, ...teamBPlayers]
                    .filter((p: any) => p && (p._id || p.id || p.value))
                    .map((p: any) => ({
                      value: p._id || p.id || p.value || '',
                      label: p.name || p.playerName || p.label || 'Unknown Player'
                    }))}
                  placeholder="Select Second Player (Optional)"
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '38px',
                    }),
                  }}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={handleClosePlayerOfMatchModal}
              disabled={isSavingPlayerOfMatch}
            >
              Cancel
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={handleSkipPlayerOfMatch}
              disabled={isSavingPlayerOfMatch}
            >
              Skip
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSavePlayerOfMatch} 
              disabled={isSavingPlayerOfMatch || (!playerOfTheMatch.player && !secondPlayerOfTheMatch.player)}
            >
              {isSavingPlayerOfMatch ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Update Timer Modal */}
        <Modal show={showUpdateTimerModal} onHide={handleCloseUpdateTimerModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Update Timer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && (
              <div className="alert alert-danger mb-3" role="alert">
                {formError}
              </div>
            )}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600 }}>
                  Set Timer To <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 45:30"
                  value={updateTimerValue}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove any non-numeric characters except colon
                    let cleaned = value.replace(/[^0-9:]/g, '');
                    // Remove spaces
                    cleaned = cleaned.replace(/\s/g, '');
                    // Ensure only one colon
                    const colonCount = (cleaned.match(/:/g) || []).length;
                    if (colonCount > 1) {
                      const firstColonIndex = cleaned.indexOf(':');
                      cleaned = cleaned.substring(0, firstColonIndex + 1) + cleaned.substring(firstColonIndex + 1).replace(/:/g, '');
                    }
                    
                    const parts = cleaned.split(':');
                    if (parts.length === 1) {
                      if (cleaned.length <= 2) {
                        setUpdateTimerValue(cleaned);
                      } else if (cleaned.length === 3) {
                        setUpdateTimerValue(cleaned.substring(0, 2) + ':' + cleaned.substring(2));
                      }
                    } else if (parts.length === 2) {
                      let minutes = parts[0];
                      let seconds = parts[1];
                      
                      if (minutes.length > 2) {
                        minutes = minutes.substring(0, 2);
                      }
                      const minutesNum = parseInt(minutes, 10);
                      if (!isNaN(minutesNum) && minutesNum > 99) {
                        minutes = '99';
                      }
                      
                      if (seconds.length > 2) {
                        seconds = seconds.substring(0, 2);
                      }
                      const secondsNum = parseInt(seconds, 10);
                      if (!isNaN(secondsNum) && secondsNum > 59) {
                        seconds = '59';
                      }
                      
                      setUpdateTimerValue(minutes + ':' + seconds);
                    } else {
                      return;
                    }
                  }}
                  maxLength={5}
                  pattern="[0-9]{1,2}:[0-9]{2}"
                />
                <Form.Text className="text-muted">Enter time in format MM:SS (max 99:59)</Form.Text>
                {updateTimerValue && (() => {
                  const parts = updateTimerValue.split(':');
                  if (parts.length === 2) {
                    const minutes = parseInt(parts[0], 10);
                    const seconds = parseInt(parts[1], 10);
                    if (isNaN(minutes) || isNaN(seconds) || minutes > 99 || seconds > 59) {
                      return <Form.Text className="text-danger">Invalid time format. Maximum is 99:59</Form.Text>;
                    }
                  }
                  return null;
                })()}
              </Form.Group>
              <div className="alert alert-info mb-0" style={{ fontSize: '13px' }}>
                <i className="ri-information-line me-2"></i>
                Current timer: <strong>{formatTimer(matchTimer)}</strong>. The timer will be updated to the specified time when you click "Update Timer".
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseUpdateTimerModal} disabled={isUpdatingTimer}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpdateTimer}
              disabled={isUpdatingTimer || !updateTimerValue || !updateTimerValue.includes(':')}
            >
              {isUpdatingTimer ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="ri-time-line me-2"></i>
                  Update Timer
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Set Modal */}
        <DeleteModal
          show={setToDelete !== null}
          handleClose={() => setSetToDelete(null)}
          deleteModalFunction={handleDeleteSet}
          title="Delete Set"
          message="Are you sure you want to delete this set? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isLoading={isSavingSet}
        />

        {/* Walkover/Abandon Match Modal - Reusable */}
        <Modal show={showActionModal} onHide={handleCloseActionModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className={actionModalType === 'walkover' ? 'ri-flag-line me-2' : 'ri-close-circle-line me-2'}></i>
              {actionModalType === 'walkover' ? 'Declare Walkover' : 'Abandon Match'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && (
              <div className="alert alert-danger mb-3">{formError}</div>
            )}
            <Form>
              <Row className="g-3">
                {/* Team Selection - Only for Walkover */}
                {actionModalType === 'walkover' && (
                  <Col md={12}>
                    <Form.Label style={{ fontWeight: 600 }}>
                      Select Team <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex gap-3">
                      <div
                        onClick={() => setWalkoverTeam('teamA')}
                        style={{
                          flex: 1,
                          padding: '16px',
                          border: `2px solid ${walkoverTeam === 'teamA' ? '#ffc107' : '#dee2e6'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: walkoverTeam === 'teamA' ? '#fff3cd' : '#ffffff',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => {
                          if (walkoverTeam !== 'teamA') {
                            e.currentTarget.style.borderColor = '#ffc107';
                            e.currentTarget.style.backgroundColor = '#fffbf0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (walkoverTeam !== 'teamA') {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        <Form.Check
                          type="radio"
                          id="walkover-teamA"
                          name="walkoverTeam"
                          checked={walkoverTeam === 'teamA'}
                          onChange={() => setWalkoverTeam('teamA')}
                          style={{ margin: 0, pointerEvents: 'none' }}
                        />
                        <label
                          htmlFor="walkover-teamA"
                          style={{
                            margin: 0,
                            cursor: 'pointer',
                            flex: 1,
                            fontWeight: walkoverTeam === 'teamA' ? 600 : 400,
                            color: walkoverTeam === 'teamA' ? '#856404' : '#212529'
                          }}
                        >
                          {fixture?.teamA?.name || (fixture as any)?.teamAInfo?.name || 'Team A'}
                        </label>
                      </div>
                      <div
                        onClick={() => setWalkoverTeam('teamB')}
                        style={{
                          flex: 1,
                          padding: '16px',
                          border: `2px solid ${walkoverTeam === 'teamB' ? '#ffc107' : '#dee2e6'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: walkoverTeam === 'teamB' ? '#fff3cd' : '#ffffff',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => {
                          if (walkoverTeam !== 'teamB') {
                            e.currentTarget.style.borderColor = '#ffc107';
                            e.currentTarget.style.backgroundColor = '#fffbf0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (walkoverTeam !== 'teamB') {
                            e.currentTarget.style.borderColor = '#dee2e6';
                            e.currentTarget.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        <Form.Check
                          type="radio"
                          id="walkover-teamB"
                          name="walkoverTeam"
                          checked={walkoverTeam === 'teamB'}
                          onChange={() => setWalkoverTeam('teamB')}
                          style={{ margin: 0, pointerEvents: 'none' }}
                        />
                        <label
                          htmlFor="walkover-teamB"
                          style={{
                            margin: 0,
                            cursor: 'pointer',
                            flex: 1,
                            fontWeight: walkoverTeam === 'teamB' ? 600 : 400,
                            color: walkoverTeam === 'teamB' ? '#856404' : '#212529'
                          }}
                        >
                          {fixture?.teamB?.name || (fixture as any)?.teamBInfo?.name || 'Team B'}
                        </label>
                      </div>
                    </div>
                  </Col>
                )}

                {/* Reason */}
                <Col md={12}>
                  <Form.Label style={{ fontWeight: 600 }}>
                    Reason {actionModalType === 'walkover' ? 'for Walkover' : 'for Abandoning Match'} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder={actionModalType === 'walkover' 
                      ? "Enter the reason for declaring walkover (e.g., Team unable to participate, insufficient players, etc.)"
                      : "Enter the reason for abandoning the match (e.g., Weather conditions, safety concerns, etc.)"}
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                  <Form.Text className="text-muted">
                    Please provide a clear reason for {actionModalType === 'walkover' ? 'declaring the walkover' : 'abandoning the match'}.
                  </Form.Text>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseActionModal} disabled={isProcessingAction}>
              Cancel
            </Button>
            <Button 
              variant={actionModalType === 'walkover' ? 'warning' : 'danger'} 
              onClick={handleActionSubmit} 
              disabled={isProcessingAction || (actionModalType === 'walkover' && !walkoverTeam) || !actionReason.trim()}
            >
              {isProcessingAction ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {actionModalType === 'walkover' ? 'Declaring...' : 'Abandoning...'}
                </>
              ) : (
                <>
                  <i className={actionModalType === 'walkover' ? 'ri-flag-line me-1' : 'ri-close-circle-line me-1'}></i>
                  {actionModalType === 'walkover' ? 'Declare Walkover' : 'Abandon Match'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      {/* Custom Time Picker Dropdown - Portal Rendered */}
      {showTimePicker && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="time-picker-backdrop"
            onClick={closeTimePicker}
          ></div>
          
          <div 
            className="custom-time-picker-dropdown"
            style={{
              top: `${pickerPosition.top}px`,
              left: `${pickerPosition.left}px`
            }}
          >
            <div className="time-picker-header">
              <small className="text-muted fw-medium">Select Time</small>
              <button
                type="button"
                className="btn-close btn-sm"
                onClick={closeTimePicker}
              ></button>
            </div>
            
            <div className="time-picker-content">
              {/* Hour Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Hour</small>
                <div className="time-options">
                  {hours.map((hourOption) => (
                    <button
                      key={hourOption.value}
                      type="button"
                      className={`time-option ${selectedHour === hourOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('hour', hourOption.value)}
                    >
                      {hourOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Minute Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Minute</small>
                <div className="time-options">
                  {minutes.map((minuteOption) => (
                    <button
                      key={minuteOption.value}
                      type="button"
                      className={`time-option ${selectedMinute === minuteOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('minute', minuteOption.value)}
                    >
                      {minuteOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* AM/PM Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Period</small>
                <div className="time-options">
                  {amPmOptions.map((amPmOption) => (
                    <button
                      key={amPmOption.value}
                      type="button"
                      className={`time-option ampm-option ${selectedAmPm === amPmOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('amPm', amPmOption.value)}
                      disabled={!selectedHour || !selectedMinute}
                    >
                      {amPmOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default MatchDetailPage;
